import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Mail,
  Send,
  Users,
  TrendingUp,
  MousePointer,
  UserMinus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pause,
  Play,
  Copy,
  Edit3,
  Trash2,
  Filter,
  Search,
  MoreHorizontal,
  Target,
  BarChart3,
  Zap,
  TestTube,
  ArrowRight,
  ChevronDown,
  X,
  Loader2,
  FileText,
  Image,
  Images,
  Link,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Phone,
  MessageCircle,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon } from '../../components/PlatformIcon';
import MarketingMediaCalendar from '../../components/marketing/MarketingMediaCalendar';
import CampaignBillingRetryModal, {
  type CampaignBillingRetryTarget,
} from '../../components/marketing/CampaignBillingRetryModal';
import MultiMediaCampaignWizard, {
  type MultiMediaChannel,
  type MultiMediaSchedule,
} from '../../components/marketing/MultiMediaCampaignWizard';
import {
  buildMarketingChannelItems,
  type ChannelStatus,
} from '../../components/marketing/ConnectedChannelsBar';
import MarketingBottomNav from '../../components/marketing/MarketingBottomNav';
import MarketingPlatformsModal from '../../components/marketing/MarketingPlatformsModal';
import MarketingPlatformConnectModal, {
  type MarketingConnectTarget,
} from '../../components/marketing/MarketingPlatformConnectModal';
import CreateGbpPostModal from '../../components/CreateGbpPostModal';
import CreateSocialPostModal from '../../components/CreateSocialPostModal';
import AddMediaOptions from '../../components/AddMediaOptions';
import CampaignDetailsModal from '../../components/marketing/CampaignDetailsSheet';
import {
  type MarketingNavPage,
  type MarketingPageId,
} from './MarketingHomeDashboard';
import DiscountCodes from './DiscountCodes';
import BrandGuidelines from './BrandGuidelines';
import MediaManager from './MediaManager';
import EmailTemplates from './EmailTemplates';
import ReferralProgram from './ReferralProgram';
import RewardsProgram from './RewardsProgram';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../hooks/useAuth';
import { useSupabase } from '../../contexts/SupabaseContext';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import { buildEmailCampaignCheckoutUrls, redirectToCampaignCheckout } from '../../lib/emailCampaignCheckout';
import { useLocation, useNavigate } from 'react-router-dom';
import { GBP_ENABLED, SOCIAL_ENABLED } from '../../lib/featureFlags';
import { useBusinessId } from '../../hooks/useBusinessId';
import {
  buildCampaignRecipientList,
  createCampaignRecipients,
  getCampaignRecipientBreakdown,
  queueCampaignSend,
  saveCampaignRecord,
  uploadCampaignImage,
} from '../../services/emailCampaignService';
import AICampaignWriterModal from '../../components/marketing/AICampaignWriterModal';
import EmailTemplateBuilder from '../../components/EmailTemplateBuilder';
import TokenPurchaseModal from '../../components/TokenPurchaseModal';
import { emailTemplateService } from '../../services/emailTemplateService';
import {
  applyBrandColorsToEmailTemplateSections,
  formatBrandGuidelinesForPrompt,
  getBrandGuidelines,
  resolveDefaultOnlineBookingUrl,
} from '../../lib/brandGuidelines';
import { gatherEmailTemplateAiImages } from '../../lib/emailTemplateAiImages';
import toast from 'react-hot-toast';
import ExtendedCampaignEditor, {
  STEP_CONDITION_LABELS,
  stepDelayLabel,
  type ExtendedStepDraft,
} from '../../components/marketing/ExtendedCampaignEditor';
import {
  useUpcomingGbpPostsSnippet,
} from '../../hooks/useUpcomingGbpPostsSnippet';
import {
  useUpcomingSocialPostsSnippet,
} from '../../hooks/useUpcomingSocialPostsSnippet';
import type { CampaignAiOption, EmailOption, SmsOption } from '../../lib/marketingAI';
import {
  AUDIENCE_CONDITION_OPTIONS,
  audienceConditionKindLabel,
  bookingExcludeConditionSummary,
  getBookingExcludePeriodsFromConditions,
  getTagFiltersFromConditions,
  hasTagConditions,
  type AddConditionStep,
  type AudienceCondition,
} from '../../lib/campaignAudienceConditions';
import {
  BOOKING_EXCLUDE_PRESET_OPTIONS,
  fetchCustomerIdsWithBookingsInPeriods,
  formatBookingExcludePeriodLabel,
  resolveBookingExcludePeriod,
  type BookingExcludePreset,
} from '../../lib/campaignBookingExclude';
import { getCustomerTags } from '../../lib/customers';
import {
  listCustomerSegments,
  unionSegmentCustomerIds,
  type CustomerSegment,
} from '../../lib/customers/segments';
import SnippetsModal from '../../components/SnippetsModal';
import { renderEmailTemplateToHtml } from '../../lib/emailTemplateRenderer';
import {
  deleteSecondaryEmail,
  deleteSecondaryPhone,
  normalizeEmail,
  normalizePhoneNumber,
  type SecondaryEmailEntry,
  type SecondaryPhoneEntry
} from '../../services/secondaryContactLists';

// Types
export interface EmailCampaign {
  id: string;
  name: string;
  subject?: string; // Optional for SMS campaigns
  from_email?: string; // Optional for SMS campaigns
  from_name?: string; // Optional for SMS campaigns
  html_content?: string; // Optional for SMS campaigns
  channel_type?: 'email' | 'sms'; // Campaign channel type
  sms_content?: string; // SMS message content
  from_phone_number?: string; // Phone number for SMS campaigns
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'failed';
  campaign_type: 'standard' | 'ab_test' | 'automated';
  is_ab_test: boolean;
  recipient_count: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count?: number;
  unsubscribed_count?: number;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  billing_status?: 'none' | 'authorized' | 'charge_at_send' | 'captured' | 'failed' | string | null;
  billing_payment_intent_id?: string | null;
  billing_amount_cents?: number | null;
  billing_recipient_count?: number | null;
}

interface EmailMarketingSettings {
  id: string;
  business_id: string;
  verified_emails?: string[]; // Legacy field
  verified_senders?: Array<{
    email: string;
    sendgrid_sender_id: string;
    verification_status: 'pending' | 'verified' | 'failed';
    verification_method?: string;
    verified_at?: string;
    created_at?: string;
  }>;
  monthly_email_limit: number;
  emails_sent_this_month: number;
  is_enabled: boolean;
}







/** "48" (hours) → "2 days", "6" → "6 hours" for sequence step delays. */
function formatStepDelayHours(hours: number | null | undefined): string {
  const h = hours || 0;
  if (h >= 24 && h % 24 === 0) {
    const d = h / 24;
    return `${d} ${d === 1 ? 'day' : 'days'}`;
  }
  return `${h} ${h === 1 ? 'hour' : 'hours'}`;
}

// Rich Text Editor for campaign content
function CampaignRichTextEditor({ value, onChange, placeholder, rows = 6, businessId }: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  rows?: number;
  businessId?: string;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const execCmd = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) execCmd('createLink', url);
  };

  // Upload pasted/dropped images to Storage and insert as a URL instead of a
  // huge base64 data URI (which bloats the email and breaks rendering/sending).
  const insertUploadedImage = async (file: File) => {
    if (!businessId) return false;
    const url = await uploadCampaignImage(file, businessId);
    if (!url) return false;
    editorRef.current?.focus();
    document.execCommand(
      'insertHTML',
      false,
      `<img src="${url}" alt="" style="max-width:100%;height:auto;" />`
    );
    handleInput();
    return true;
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((it) => it.kind === 'file' && it.type.startsWith('image/'));
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file) {
        e.preventDefault();
        void insertUploadedImage(file);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/'));
    if (file) {
      e.preventDefault();
      void insertUploadedImage(file);
    }
  };

  const toolbarBtn = (active?: boolean) =>
    `p-1.5 rounded-md transition-colors touch-manipulation ${active ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50/50 flex-wrap">
        <button type="button" onClick={() => execCmd('bold')} className={toolbarBtn()}>
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('italic')} className={toolbarBtn()}>
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('underline')} className={toolbarBtn()}>
          <Underline className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <button type="button" onClick={() => execCmd('justifyLeft')} className={toolbarBtn()}>
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('justifyCenter')} className={toolbarBtn()}>
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('justifyRight')} className={toolbarBtn()}>
          <AlignRight className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <button type="button" onClick={() => execCmd('insertUnorderedList')} className={toolbarBtn()}>
          <List className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => execCmd('insertOrderedList')} className={toolbarBtn()}>
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={handleLink} className={toolbarBtn()}>
          <Link className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        onPaste={handlePaste}
        onDrop={handleDrop}
        data-placeholder={placeholder}
        className="px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none min-h-[120px] prose prose-sm max-w-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        style={{ minHeight: `${rows * 24}px` }}
      />
    </div>
  );
}

// Compact visual preview of a saved email template for the picker grid.
// Renders the structured sections the same way the Email Templates page does,
// just trimmed down to tile size.
function TemplateMiniPreview({ template, businessName, logoUrl }: {
  template: any;
  businessName?: string;
  logoUrl?: string;
}) {
  const sections = template?.content && typeof template.content === 'object' && Array.isArray(template.content.sections)
    ? template.content.sections
    : null;

  if (!sections) {
    return (
      <div className="h-full flex flex-col">
        <div className="bg-blue-600 px-3 py-2 text-center">
          <div className="text-[11px] text-white font-bold truncate">{businessName || 'Business'}</div>
        </div>
        <div className="flex-1 px-3 py-4 bg-gray-50 text-center overflow-hidden">
          <div className="text-[12px] font-bold text-gray-900 line-clamp-2">{template?.subject || template?.name || 'Email Template'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {sections.map((section: any, idx: number) => (
        <div key={idx} className="flex-shrink-0">
          {section.type === 'header' && (
            <div
              className="px-3 py-2 text-center flex items-center justify-center"
              style={{
                backgroundColor: section.content?.backgroundColor || '#6366f1',
                color: section.content?.textColor || '#ffffff',
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=""
                  className="h-4 object-contain"
                  style={{
                    filter: section.content?.logoInvert !== false ? 'brightness(0) invert(1)' : 'none',
                    maxWidth: '80px',
                  }}
                />
              ) : (
                <div className="text-[10px] font-bold truncate">{businessName || section.content?.businessName || 'Business'}</div>
              )}
            </div>
          )}
          {section.type === 'hero' && (
            <div
              className="px-3 py-3 text-center"
              style={{ backgroundColor: section.content?.backgroundColor || '#f3f4f6' }}
            >
              {section.content?.heading && (
                <div className="text-[12px] font-bold text-gray-900 leading-tight line-clamp-2" dangerouslySetInnerHTML={{ __html: section.content.heading }} />
              )}
              {section.content?.subheading && (
                <div className="text-[10px] text-gray-600 leading-tight line-clamp-2 mt-1" dangerouslySetInnerHTML={{ __html: section.content.subheading }} />
              )}
              {section.content?.buttonText && (
                <div
                  className="inline-block px-2.5 py-1 rounded text-[10px] font-bold mt-1.5"
                  style={{
                    backgroundColor: section.content?.buttonBackgroundColor || '#6366f1',
                    color: section.content?.buttonColor || '#ffffff',
                  }}
                  dangerouslySetInnerHTML={{ __html: section.content.buttonText }}
                />
              )}
            </div>
          )}
          {section.type === 'text' && section.content?.text && (
            <div className="px-3 py-2">
              <div className="text-[10px] text-gray-700 leading-tight line-clamp-3" dangerouslySetInnerHTML={{ __html: section.content.text }} />
            </div>
          )}
          {section.type === 'button' && section.content?.text && (
            <div className="px-3 py-2 text-center">
              <div
                className="inline-block px-2.5 py-1 rounded text-[10px] font-bold"
                style={{
                  backgroundColor: section.content?.backgroundColor || '#6366f1',
                  color: section.content?.color || '#ffffff',
                }}
                dangerouslySetInnerHTML={{ __html: section.content.text }}
              />
            </div>
          )}
          {section.type === 'image' && section.content?.url && (
            <div className="px-3 py-2">
              <img src={section.content.url} alt="" className="w-full h-10 object-cover rounded" />
            </div>
          )}
          {section.type === 'divider' && (
            <div className="px-3 py-1.5">
              <div className="w-full h-px bg-gray-300" />
            </div>
          )}
          {section.type === 'spacer' && <div style={{ height: `${Math.min(section.content?.height || 12, 16)}px` }} />}
        </div>
      ))}
    </div>
  );
}

// Campaign Builder Form Component
export function CampaignBuilderForm({ 
  onClose, 
  onSave, 
  editingCampaign, 
  selectedBusiness,
  businessId,
  supabase,
  initialDate,
  defaultChannelType,
  multiMediaStep = null,
  onStartMultiMedia,
}: { 
  onClose: () => void;
  onSave: (campaign: any) => void;
  editingCampaign: EmailCampaign | null;
  selectedBusiness: any;
  businessId: string;
  supabase: any;
  initialDate?: string | null;
  defaultChannelType?: 'email' | 'sms';
  /**
   * Multi Media wizard step mode: channel is locked and the send time comes from
   * the wizard. Hides When to Send + Campaign Type; submit hands campaignData to
   * onSave without the final confirm modal (the wizard reviews everything).
   */
  multiMediaStep?: {
    channel: 'email' | 'sms';
    sendOption: 'now' | 'schedule';
    scheduledDate: string;
    scheduledTime: string;
  } | null;
  /** When provided, the Multi Media card shows a Continue that starts the wizard. */
  onStartMultiMedia?: (
    channels: MultiMediaChannel[],
    schedule: MultiMediaSchedule,
  ) => void;
}) {
  const [currentStep, setCurrentStep] = useState(editingCampaign?.id || multiMediaStep ? 2 : 1);
  const [channelType, setChannelType] = useState<'email' | 'sms'>(
    multiMediaStep?.channel ||
      (editingCampaign?.channel_type as 'email' | 'sms') ||
      defaultChannelType ||
      'email'
  );
  const [campaignKind, setCampaignKind] = useState<'single' | 'multimedia'>('single');
  const [multiChannelToggles, setMultiChannelToggles] = useState({
    email: false,
    sms: false,
    gbp: false,
    facebook: false,
    instagram: false,
  });
  const [mediaConnections, setMediaConnections] = useState({
    gbp: false,
    facebook: false,
    instagram: false,
  });
  const [mediaConnectionsLoaded, setMediaConnectionsLoaded] = useState(false);
  const [emailType, setEmailType] = useState<'template' | 'custom'>('custom');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // Right-side template picker sheet + in-flow template builder (create a new
  // template without leaving the campaign — campaign state stays mounted).
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  // null = start from the builder's own template chooser; an object = AI result.
  const [builderTemplate, setBuilderTemplate] = useState<any>(null);
  // AI template creation (same ai-create-email-template flow as Email Templates page).
  const [showAiTemplatePrompt, setShowAiTemplatePrompt] = useState(false);
  const [aiTemplatePrompt, setAiTemplatePrompt] = useState('');
  const [aiTemplateGenerating, setAiTemplateGenerating] = useState(false);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  
  const [formData, setFormData] = useState({
    name: editingCampaign?.name || 'New Campaign',
    subject: editingCampaign?.subject || '',
    from_email: editingCampaign?.from_email || selectedBusiness?.email || 'test@business.com',
    from_name: editingCampaign?.from_name || selectedBusiness?.name || 'Test Business',
    html_content: editingCampaign?.html_content || '<p>Enter your email content here...</p>',
    sms_content: editingCampaign?.sms_content || '',
    from_phone_number: editingCampaign?.from_phone_number || ''
  });
  
  // Step 2: Recipient selection state - initialize from editingCampaign if exists
  const [recipientType, setRecipientType] = useState<'all_customers' | 'segment'>(
    editingCampaign?.recipient_type || 'all_customers'
  );
  const [recipientFilters, setRecipientFilters] = useState<any>(
    editingCampaign?.recipient_filters || {
      tags: [],
      status: 'active',
      source: null,
      hasEmail: true,
      minSpent: null,
      createdAfter: null,
      createdBefore: null,
      lastInteractionAfter: null
    }
  );
  
  // Initialize scheduling from editingCampaign
  const getScheduledDateTime = () => {
    if (editingCampaign?.scheduled_at) {
      const date = new Date(editingCampaign.scheduled_at);
      return {
        date: date.toISOString().split('T')[0],
        time: date.toTimeString().slice(0, 5)
      };
    }
    return { date: '', time: '' };
  };
  
  const scheduledDateTime = getScheduledDateTime();
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [estimatedRecipientCount, setEstimatedRecipientCount] = useState<number | null>(null);
  const [calculatingCount, setCalculatingCount] = useState(false);
  const [verifiedEmails, setVerifiedEmails] = useState<string[]>([]);
  const [verifiedEmailsLoaded, setVerifiedEmailsLoaded] = useState(false);
  // Extended (multi-step) campaign: staged follow-up emails (email channel only).
  // Restores from _sequenceSteps when re-editing a multi-media wizard draft.
  const [extendedSteps, setExtendedSteps] = useState<ExtendedStepDraft[]>(() => {
    const steps = (editingCampaign as any)?._sequenceSteps;
    if (!Array.isArray(steps)) return [];
    return steps.map((s: any, i: number) => ({
      id: `restored-${i}-${Date.now()}`,
      delayValue: s.delay_hours % 24 === 0 ? s.delay_hours / 24 : s.delay_hours,
      delayUnit: s.delay_hours % 24 === 0 ? 'days' : 'hours',
      condition: s.condition,
      subject: s.subject || '',
      html_content: s.html_content || '',
      variants: s.variants || [],
    }));
  });
  const [showExtendedEditor, setShowExtendedEditor] = useState(false);
  const [appliedRecipientCount, setAppliedRecipientCount] = useState<number | null>(null);
  const [twilioSettings, setTwilioSettings] = useState<{
    twilio_enabled: boolean;
    VITE_TWILIO_PHONE_NUMBER: string | null;
    twilio_status: string | null;
  } | null>(null);
  const [loadingTwilioSettings, setLoadingTwilioSettings] = useState(false);
  
  // Scheduling state - initialize from multiMediaStep, editingCampaign, or initialDate
  const [sendOption, setSendOption] = useState<'now' | 'schedule'>(
    multiMediaStep
      ? multiMediaStep.sendOption
      : editingCampaign?.scheduled_at ? 'schedule' : (initialDate ? 'schedule' : 'now')
  );
  const [scheduledDate, setScheduledDate] = useState(
    multiMediaStep ? multiMediaStep.scheduledDate : (scheduledDateTime.date || initialDate || '')
  );
  const [scheduledTime, setScheduledTime] = useState(
    multiMediaStep ? multiMediaStep.scheduledTime : (scheduledDateTime.time || (initialDate ? '10:00' : ''))
  );
  const [timezone, setTimezone] = useState(editingCampaign?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [isAddCustomersModalOpen, setIsAddCustomersModalOpen] = useState(false);
  const [isAddCustomersAnimating, setIsAddCustomersAnimating] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Add Customers Modal State
  const [customerTags, setCustomerTags] = useState<any[]>([]);
  const [loadingCustomerTags, setLoadingCustomerTags] = useState(false);
  const [conditions, setConditions] = useState<AudienceCondition[]>([]);
  const [addConditionStep, setAddConditionStep] = useState<AddConditionStep>(null);
  const [newConditionType, setNewConditionType] = useState<'includes' | 'excludes'>('includes');
  const [selectedTagsForCondition, setSelectedTagsForCondition] = useState<string[]>([]);
  const [bookingExcludePreset, setBookingExcludePreset] = useState<BookingExcludePreset>('this_year');
  const [bookingExcludeCustomStart, setBookingExcludeCustomStart] = useState('');
  const [bookingExcludeCustomEnd, setBookingExcludeCustomEnd] = useState('');
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [loadingRecipientCount, setLoadingRecipientCount] = useState(false);
  const [, setRecipientBreakdown] = useState<{ customerCount: number; secondaryListCount: number; total: number } | null>(null);
  const [audienceSegments, setAudienceSegments] = useState<CustomerSegment[]>([]);
  const [loadingAudienceSegments, setLoadingAudienceSegments] = useState(false);
  const [audienceSegmentError, setAudienceSegmentError] = useState<string | null>(null);
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);
  
  // Manual customer selection state
  const [manualSelectionMode, setManualSelectionMode] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customersList, setCustomersList] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  
  // Recipient list preview in Target Audience modal
  const [recipientListView, setRecipientListView] = useState<'selected' | 'removed'>('selected');
  const [selectedEmailsList, setSelectedEmailsList] = useState<any[]>([]);
  const [filteredOutEmailsList, setFilteredOutEmailsList] = useState<any[]>([]);
  const [loadingEmailLists, setLoadingEmailLists] = useState(false);
  const [isSnippetsModalOpen, setIsSnippetsModalOpen] = useState(false);

  // Split test / A/B variant state
  interface CampaignVariant {
    name: string;
    subject: string;
    html_content: string;
    sms_content: string;
    from_name: string;
    emailType: 'template' | 'custom';
    selectedTemplateId: string;
  }

  const defaultVariant = (name: string): CampaignVariant => ({
    name,
    subject: '',
    html_content: '<p>Enter your email content here...</p>',
    sms_content: '',
    from_name: '',
    emailType: 'custom',
    selectedTemplateId: ''
  });

  const [isSplitTest, setIsSplitTest] = useState(editingCampaign?.is_ab_test || false);
  const [variants, setVariants] = useState<CampaignVariant[]>(() => {
    // Multi-media wizard drafts round-trip _variants (primary "a" + extras).
    const storedVariants = (editingCampaign as any)?._variants;
    if (Array.isArray(storedVariants) && storedVariants.length > 1) {
      return storedVariants.slice(1) as CampaignVariant[];
    }
    return editingCampaign?.is_ab_test ? [defaultVariant('b')] : [];
  });

  const addVariant = () => {
    const nextLetter = String.fromCharCode(98 + variants.length); // b, c, d...
    setVariants(prev => [...prev, defaultVariant(nextLetter)]);
  };

  const removeVariant = (name: string) => {
    setVariants(prev =>
      prev.filter(v => v.name !== name).map((v, i) => ({ ...v, name: String.fromCharCode(98 + i) }))
    );
  };

  const updateVariant = (name: string, field: keyof CampaignVariant, value: string) => {
    setVariants(prev => prev.map(v => v.name === name ? { ...v, [field]: value } : v));
  };

  // AI campaign writer
  const [aiWriterOpen, setAiWriterOpen] = useState(false);

  const applyAiOption = (option: CampaignAiOption) => {
    if (channelType === 'sms') {
      setFormData(prev => ({ ...prev, sms_content: (option as SmsOption).message }));
    } else {
      setEmailType('custom');
      setFormData(prev => ({ ...prev, subject: (option as EmailOption).subject, html_content: (option as EmailOption).body }));
    }
    setAiWriterOpen(false);
  };

  const applyAiSplitTest = (options: CampaignAiOption[]) => {
    if (options.length === 0) return;
    const [first, ...rest] = options;
    applyAiOptionToPrimary(first);
    setIsSplitTest(true);
    const newVariants = rest.map((o, idx) => {
      const v = defaultVariant(String.fromCharCode(98 + idx));
      if (channelType === 'sms') return { ...v, sms_content: (o as SmsOption).message };
      return { ...v, emailType: 'custom' as const, subject: (o as EmailOption).subject, html_content: (o as EmailOption).body };
    });
    setVariants(newVariants);
    setAiWriterOpen(false);
  };

  const applyAiOptionToPrimary = (option: CampaignAiOption) => {
    if (channelType === 'sms') {
      setFormData(prev => ({ ...prev, sms_content: (option as SmsOption).message }));
    } else {
      setEmailType('custom');
      setFormData(prev => ({ ...prev, subject: (option as EmailOption).subject, html_content: (option as EmailOption).body }));
    }
  };

  // Handle Add Customers Modal
  const handleAddCustomersModalClose = () => {
    setIsAddCustomersModalOpen(false);
    setIsAddCustomersAnimating(true);
  };

  const handleAddCustomersModalCancel = () => {
    // Reset to defaults when canceling
    setConditions([]);
    setAddConditionStep(null);
    setSelectedTagsForCondition([]);
    setManualSelectionMode(false);
    setSelectedCustomerIds([]);
    setCustomerSearchQuery('');
    handleAddCustomersModalClose();
  };

  const handleAddCustomersTransitionEnd = () => {
    if (!isAddCustomersModalOpen) {
      setIsAddCustomersAnimating(false);
    }
  };

  // Load customer tags when modal opens
  useEffect(() => {
    if (isAddCustomersModalOpen && businessId) {
      loadCustomerTags();
    }
  }, [isAddCustomersModalOpen, businessId]);

  // Load customers when manual selection mode is enabled
  useEffect(() => {
    if (isAddCustomersModalOpen && businessId && manualSelectionMode && supabase) {
      loadCustomers();
    }
  }, [isAddCustomersModalOpen, businessId, manualSelectionMode, supabase, channelType]);

  // Load variants when editing an existing A/B test campaign
  useEffect(() => {
    if (editingCampaign?.is_ab_test && editingCampaign?.id && supabase) {
      (async () => {
        const { data: dbVariants } = await supabase
          .from('email_campaign_variants')
          .select('*')
          .eq('campaign_id', editingCampaign.id)
          .order('variant_name');

        if (dbVariants && dbVariants.length > 0) {
          setIsSplitTest(true);
          setVariants(dbVariants.map((v: any) => ({
            name: v.variant_name,
            subject: v.subject || '',
            html_content: v.html_content || '<p>Enter your email content here...</p>',
            sms_content: v.sms_content || '',
            from_name: v.from_name || '',
            emailType: (v.html_content && !v.subject) ? 'template' as const : 'custom' as const,
            selectedTemplateId: ''
          })));
          // variants loaded from DB
        }
      })();
    }
  }, [editingCampaign?.id]);

  // Filter customers based on search query
  const filteredCustomers = customersList.filter(customer => {
    if (!customerSearchQuery.trim()) return true;
    const query = customerSearchQuery.toLowerCase();
    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    const email = (customer.email || '').toLowerCase();
    const phone = (customer.phone || '').toLowerCase();
    return fullName.includes(query) || email.includes(query) || phone.includes(query);
  });

  const loadCustomers = async () => {
    if (!businessId || !supabase) return;
    try {
      setLoadingCustomers(true);
      
      // For email campaigns, get unsubscribed emails to exclude
      let unsubscribedEmails = new Set<string>();
      if (channelType === 'email') {
        const UNSUBSCRIBE_BATCH_SIZE = 1000;
        let allUnsubscribed: any[] = [];
        let unsubscribeOffset = 0;
        let hasMoreUnsubscribed = true;

        while (hasMoreUnsubscribed) {
          const { data: unsubscribed, error: unsubscribedError } = await supabase
            .from('email_unsubscribes')
            .select('email')
        .eq('business_id', businessId)
            .eq('is_active', true)
            .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

          if (unsubscribedError) {
            console.error('Error fetching unsubscribed emails:', unsubscribedError);
            break;
          }

          if (unsubscribed && unsubscribed.length > 0) {
            allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
            unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
            hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
          } else {
            hasMoreUnsubscribed = false;
          }
        }

        unsubscribedEmails = new Set(
          allUnsubscribed.map((u: any) => u.email?.toLowerCase()).filter(Boolean)
        );
      }
      
      // Supabase has a default limit of 1000 rows, so we need to fetch in batches
      const BATCH_SIZE = 1000;
      let allCustomers: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const selectFields = channelType === 'sms' 
          ? 'id, first_name, last_name, email, phone, status'
          : 'id, first_name, last_name, email, status';
        
        let query = supabase
          .from('customers')
          .select(selectFields)
        .eq('business_id', businessId)
          .eq('status', 'active');

        if (channelType === 'email') {
          query = query.not('email', 'is', null).neq('email', '');
        } else {
          // SMS: filter by phone number
          query = query.not('phone', 'is', null).neq('phone', '');
        }

        const { data, error } = await query
          .order('first_name', { ascending: true })
          .order('last_name', { ascending: true })
          .range(offset, offset + BATCH_SIZE - 1);

      if (error) throw error;

        if (data && data.length > 0) {
          let filtered = data;
          
          if (channelType === 'email') {
            // Filter out unsubscribed emails
            filtered = data.filter((c: any) => {
              const email = c.email?.toLowerCase();
              return email && !unsubscribedEmails.has(email);
            });
          } else {
            // SMS: filter is already done by query, but we'll deduplicate by phone
            filtered = data;
          }
          
          allCustomers = [...allCustomers, ...filtered];
          offset += BATCH_SIZE;
          hasMore = data.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      // Deduplicate customers by email (for email campaigns) or phone (for SMS campaigns)
      const seenContacts = new Set<string>();
      const uniqueCustomers = allCustomers.filter((c: any) => {
        if (channelType === 'email') {
          const email = c.email?.toLowerCase();
          if (!email || seenContacts.has(email)) return false;
          seenContacts.add(email);
          return true;
        } else {
          // SMS: deduplicate by phone number
          const phone = c.phone?.trim();
          if (!phone || seenContacts.has(phone)) return false;
          seenContacts.add(phone);
          return true;
        }
      });

      setCustomersList(uniqueCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomersList([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleToggleCustomerSelection = (customerId: string) => {
    setSelectedCustomerIds(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = filteredCustomers.map(c => c.id);
    const allSelected = allFilteredIds.every(id => selectedCustomerIds.includes(id));
    
    if (allSelected) {
      // Deselect all filtered
      setSelectedCustomerIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered
      setSelectedCustomerIds(prev => {
        const newIds = allFilteredIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  // Calculate recipient count when modal opens or conditions/manual selection change
  useEffect(() => {
    if (isAddCustomersModalOpen && businessId && supabase) {
      calculateRecipientCount();
    }
  }, [isAddCustomersModalOpen, conditions, selectedCustomerIds, manualSelectionMode, businessId, supabase, channelType, selectedSegmentIds]);

  const loadCustomerTags = async () => {
    if (!businessId) return;
    try {
      setLoadingCustomerTags(true);
      const tags = await getCustomerTags(businessId);
      setCustomerTags(tags);
    } catch (error) {
      console.error('Error loading customer tags:', error);
    } finally {
      setLoadingCustomerTags(false);
    }
  };

  const resetAddConditionDraft = () => {
    setAddConditionStep(null);
    setSelectedTagsForCondition([]);
    setNewConditionType('includes');
    setBookingExcludePreset('this_year');
    setBookingExcludeCustomStart('');
    setBookingExcludeCustomEnd('');
  };

  const buildFiltersFromConditions = () => {
    const { includeTags, excludeTags } = getTagFiltersFromConditions(conditions);
    const excludeBookingPeriods = getBookingExcludePeriodsFromConditions(conditions);
    return {
      hasEmail: channelType === 'email' ? true : undefined,
      includeTags: includeTags.length > 0 ? includeTags : undefined,
      excludeTags: excludeTags.length > 0 ? excludeTags : undefined,
      excludeBookingPeriods:
        excludeBookingPeriods.length > 0 ? excludeBookingPeriods : undefined,
    };
  };

  const handleAddBookingExcludeCondition = () => {
    const period = resolveBookingExcludePeriod(
      bookingExcludePreset,
      bookingExcludeCustomStart,
      bookingExcludeCustomEnd
    );
    if (!period.start || !period.end) {
      alert('Please select a valid date range.');
      return;
    }
    if (period.start > period.end) {
      alert('Start date must be on or before end date.');
      return;
    }
    setConditions([
      ...conditions,
      {
        kind: 'booking_exclude',
        preset: bookingExcludePreset,
        startDate: period.start,
        endDate: period.end,
        label: formatBookingExcludePeriodLabel(period),
      },
    ]);
    resetAddConditionDraft();
  };

  const handleAddCondition = () => {
    if (selectedTagsForCondition.length === 0) return;
    const selectedTagObjects = customerTags.filter((t) =>
      selectedTagsForCondition.includes(t.id)
    );
    if (selectedTagObjects.length > 0) {
      setConditions([
        ...conditions,
        {
          kind: 'tags',
          type: newConditionType,
          tagIds: selectedTagObjects.map((t) => t.id),
          tagNames: selectedTagObjects.map((t) => t.name),
        },
      ]);
      resetAddConditionDraft();
    }
  };

  const handleToggleTagSelection = (tagId: string) => {
    setSelectedTagsForCondition(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const handleRemoveCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const calculateRecipientCount = async () => {
    if (!businessId || !supabase) return;

    setLoadingRecipientCount(true);
    try {
      // If manual selection mode is active, count manually selected customers
      if (manualSelectionMode) {
        if (selectedCustomerIds.length === 0) {
          setRecipientCount(0);
          setLoadingRecipientCount(false);
          return;
        }

        // Get unsubscribed emails to exclude (only for email campaigns)
        let unsubscribedEmails = new Set<string>();
        if (channelType === 'email') {
          const UNSUBSCRIBE_BATCH_SIZE = 1000;
          let allUnsubscribed: any[] = [];
          let unsubscribeOffset = 0;
          let hasMoreUnsubscribed = true;

          while (hasMoreUnsubscribed) {
            const { data: unsubscribed, error: unsubscribedError } = await supabase
              .from('email_unsubscribes')
              .select('email')
        .eq('business_id', businessId)
              .eq('is_active', true)
              .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

            if (unsubscribedError) {
              console.error('Error fetching unsubscribed emails:', unsubscribedError);
              break;
            }

            if (unsubscribed && unsubscribed.length > 0) {
              allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
              unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
              hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
            } else {
              hasMoreUnsubscribed = false;
            }
          }

          unsubscribedEmails = new Set(
            allUnsubscribed.map((u: any) => u.email?.toLowerCase()).filter(Boolean)
          );
        }

        // Filter selected customers based on channel type
        let selectedCustomers = customersList.filter(c => {
          if (!selectedCustomerIds.includes(c.id) || c.status !== 'active') return false;
          
          if (channelType === 'email') {
            return c.email && !unsubscribedEmails.has(c.email?.toLowerCase());
          } else {
            // SMS: must have phone number
            return c.phone && c.phone.trim() !== '';
          }
        });

        // If some selected customers aren't in customersList, fetch them
        const missingIds = selectedCustomerIds.filter(id => !customersList.some(c => c.id === id));
        if (missingIds.length > 0) {
          const selectFields = channelType === 'sms' 
            ? 'id, email, phone, first_name, last_name, status'
            : 'id, email, first_name, last_name, status';
          
          const { data: missingCustomers } = await supabase
            .from('customers')
            .select(selectFields)
            .eq('business_id', businessId)
            .in('id', missingIds);

          if (missingCustomers) {
            const filteredMissing = missingCustomers.filter(c => {
              if (c.status !== 'active') return false;
              if (channelType === 'email') {
                return c.email && !unsubscribedEmails.has(c.email?.toLowerCase());
              } else {
                return c.phone && c.phone.trim() !== '';
              }
            });
            selectedCustomers = [...selectedCustomers, ...filteredMissing];
          }
        }

        // Deduplicate by email (for email) or phone (for SMS) - normalize phone numbers
        const seenContacts = new Set<string>();
        const uniqueCustomers = selectedCustomers.filter(c => {
          if (channelType === 'email') {
            const email = normalizeEmail(c.email || '');
            if (!email || seenContacts.has(email)) return false;
            seenContacts.add(email);
            return true;
          } else {
            // SMS: deduplicate by phone (normalize)
            const phone = c.phone?.trim();
            if (!phone) return false;
            const normalizedPhone = normalizePhoneNumber(phone);
            if (seenContacts.has(normalizedPhone)) return false;
            seenContacts.add(normalizedPhone);
            return true;
          }
        });

        let finalManualCustomers = uniqueCustomers;
        const manualBookingPeriods = getBookingExcludePeriodsFromConditions(conditions);
        if (manualBookingPeriods.length > 0) {
          const bookedIds = await fetchCustomerIdsWithBookingsInPeriods(
            businessId,
            manualBookingPeriods
          );
          finalManualCustomers = uniqueCustomers.filter((c) => !bookedIds.has(c.id));
        }

        setRecipientCount(finalManualCustomers.length);
        setRecipientBreakdown({
          customerCount: finalManualCustomers.length,
          secondaryListCount: 0,
          total: finalManualCustomers.length,
        });
        setLoadingRecipientCount(false);
        return;
      }

      // Get unsubscribed emails to exclude (only for email campaigns)
      let unsubscribedEmails = new Set<string>();
      if (channelType === 'email') {
        const UNSUBSCRIBE_BATCH_SIZE = 1000;
        let allUnsubscribed: any[] = [];
        let unsubscribeOffset = 0;
        let hasMoreUnsubscribed = true;

        while (hasMoreUnsubscribed) {
          const { data: unsubscribed, error: unsubscribedError } = await supabase
            .from('email_unsubscribes')
            .select('email')
            .eq('business_id', businessId)
            .eq('is_active', true)
            .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

          if (unsubscribedError) {
            console.error('Error fetching unsubscribed emails:', unsubscribedError);
            break;
          }

          if (unsubscribed && unsubscribed.length > 0) {
            allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
            unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
            hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
          } else {
            hasMoreUnsubscribed = false;
          }
        }

        unsubscribedEmails = new Set(
          allUnsubscribed.map((u: any) => u.email?.toLowerCase()).filter(Boolean)
        );
      }

      const audienceFilters = buildFiltersFromConditions();
      const pickedSegments = audienceSegments.filter((segment) => selectedSegmentIds.includes(segment.id));
      const segmentIds = pickedSegments.some((segment) => segment.system_key === 'all_contacts') || pickedSegments.length === 0
        ? undefined
        : selectedSegmentIds;
      const useSegment = hasTagConditions(conditions) || Boolean(segmentIds);

      if (conditions.length === 0 && !segmentIds) {
        const recipientType = 'all_customers';
        const recipientFilters = channelType === 'email' ? { hasEmail: true } : {};

        const breakdown = await getCampaignRecipientBreakdown(
          businessId,
          recipientType,
          recipientFilters,
          channelType
        );

        setRecipientCount(breakdown.total);
        setRecipientBreakdown(breakdown);
      } else {
        const recipientType = useSegment ? 'segment' : 'all_customers';

        const breakdown = await getCampaignRecipientBreakdown(
          businessId,
          recipientType,
          { ...audienceFilters, segmentIds },
          channelType
        );

        setRecipientCount(breakdown.total);
        setRecipientBreakdown(breakdown);
      }
    } catch (error) {
      console.error('Error calculating recipient count:', error);
      setRecipientCount(null);
    } finally {
      setLoadingRecipientCount(false);
    }
  };

  const handleApplyCustomerSelection = () => {
    const audienceFilters = buildFiltersFromConditions();

    const pickedSegments = audienceSegments.filter((segment) => selectedSegmentIds.includes(segment.id));
    const segmentIds = manualSelectionMode || pickedSegments.some((segment) => segment.system_key === 'all_contacts') || pickedSegments.length === 0
      ? undefined
      : selectedSegmentIds;
    const onlyAllContacts = audienceSegments
      .filter((segment) => selectedSegmentIds.includes(segment.id))
      .every((segment) => segment.system_key === 'all_contacts');

    if (manualSelectionMode && selectedCustomerIds.length > 0) {
      setRecipientType('segment');
      setRecipientFilters({
        hasEmail: true,
        manualCustomerIds: selectedCustomerIds,
        ...audienceFilters,
      });
    } else if (conditions.length === 0 && onlyAllContacts) {
      setRecipientType('all_customers');
      setRecipientFilters({
        hasEmail: true,
        segmentIds,
      });
    } else {
      setRecipientType(hasTagConditions(conditions) || !onlyAllContacts ? 'segment' : 'all_customers');
      setRecipientFilters({
        ...audienceFilters,
        segmentIds,
      });
    }

    setAppliedRecipientCount(recipientCount || 0);

    handleAddCustomersModalClose();
  };

  // Fetch selected emails (recipients that will receive the campaign)
  const fetchSelectedEmails = async (): Promise<any[]> => {
    if (!businessId || !supabase) return [];

    try {
      // Use the same logic as buildCampaignRecipientList to get actual recipients
      const { includeTags, excludeTags } = getTagFiltersFromConditions(conditions);
      const bookingPeriods = getBookingExcludePeriodsFromConditions(conditions);
      let bookedCustomerIds = new Set<string>();
      if (bookingPeriods.length > 0) {
        bookedCustomerIds = await fetchCustomerIdsWithBookingsInPeriods(
          businessId,
          bookingPeriods
        );
      }

      // Get unsubscribed emails (only for email campaigns)
      let unsubscribedEmails = new Set<string>();
      if (channelType === 'email') {
        const UNSUBSCRIBE_BATCH_SIZE = 1000;
        let allUnsubscribed: any[] = [];
        let unsubscribeOffset = 0;
        let hasMoreUnsubscribed = true;

        while (hasMoreUnsubscribed) {
          const { data: unsubscribed, error: unsubscribedError } = await supabase
            .from('email_unsubscribes')
            .select('email')
        .eq('business_id', businessId)
            .eq('is_active', true)
            .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

          if (unsubscribedError) {
            console.error('Error fetching unsubscribed emails:', unsubscribedError);
            break;
          }

          if (unsubscribed && unsubscribed.length > 0) {
            allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
            unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
            hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
          } else {
            hasMoreUnsubscribed = false;
          }
        }

        unsubscribedEmails = new Set(
          allUnsubscribed.map((u: any) => u.email?.toLowerCase()).filter(Boolean)
        );
      }

      let customers: any[] = [];
      
      if (manualSelectionMode && selectedCustomerIds.length > 0) {
        // Manual selection - fetch in batches if needed
        const BATCH_SIZE = 1000;
        let allManualCustomers: any[] = [];
        let offset = 0;
        let hasMore = true;

        while (hasMore && offset < selectedCustomerIds.length) {
          const batchIds = selectedCustomerIds.slice(offset, offset + BATCH_SIZE);
          
          const selectFields = channelType === 'sms'
            ? 'id, email, phone, first_name, last_name, promotional_offers, Tags, status'
            : 'id, email, first_name, last_name, promotional_offers, Tags, status';
          
          let query = supabase
            .from('customers')
            .select(selectFields)
            .eq('business_id', businessId)
            .eq('status', 'active');

          if (channelType === 'email') {
            query = query.not('email', 'is', null).neq('email', '');
          } else {
            query = query.not('phone', 'is', null).neq('phone', '');
          }

          query = query.in('id', batchIds);

          const { data: manualCustomers, error } = await query;
      if (error) throw error;

          if (manualCustomers && manualCustomers.length > 0) {
            allManualCustomers = [...allManualCustomers, ...manualCustomers];
          }
          offset += BATCH_SIZE;
          hasMore = offset < selectedCustomerIds.length;
        }
        customers = allManualCustomers;
      } else {
        // All customers or tag-filtered - fetch in batches
        const BATCH_SIZE = 1000;
        let allCustomers: any[] = [];
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const selectFields = channelType === 'sms'
            ? 'id, email, phone, first_name, last_name, Tags, status'
            : 'id, email, first_name, last_name, Tags, status';
          
          let query = supabase
            .from('customers')
            .select(selectFields)
            .eq('business_id', businessId)
            .eq('status', 'active');

          if (channelType === 'email') {
            query = query.not('email', 'is', null).neq('email', '');
          } else {
            query = query.not('phone', 'is', null).neq('phone', '');
          }

          const { data: batchCustomers, error } = await query
            .range(offset, offset + BATCH_SIZE - 1);
          if (error) throw error;
          
          if (batchCustomers && batchCustomers.length > 0) {
            allCustomers = [...allCustomers, ...batchCustomers];
            offset += BATCH_SIZE;
            hasMore = batchCustomers.length === BATCH_SIZE;
          } else {
            hasMore = false;
          }
        }
        customers = allCustomers;
      }

      const pickedSegments = audienceSegments.filter((segment) => selectedSegmentIds.includes(segment.id));
      const limitToSegments = !manualSelectionMode
        && pickedSegments.length > 0
        && !pickedSegments.some((segment) => segment.system_key === 'all_contacts');
      if (limitToSegments) {
        const allowed = await unionSegmentCustomerIds(businessId, selectedSegmentIds);
        customers = customers.filter((c: any) => allowed.has(c.id));
      }

      // Apply tag filters
      if (includeTags.length > 0) {
        customers = customers.filter((customer: any) => {
          const customerTags = customer.Tags || [];
          const customerTagNames = customerTags.map((tag: any) => 
            typeof tag === 'string' ? tag : (tag?.name || tag)
          );
          return includeTags.some(includeTag => customerTagNames.includes(includeTag));
        });
      }

      if (excludeTags.length > 0) {
        customers = customers.filter((customer: any) => {
          const customerTags = customer.Tags || [];
          const customerTagNames = customerTags.map((tag: any) => 
            typeof tag === 'string' ? tag : (tag?.name || tag)
          );
          return !excludeTags.some(excludeTag => customerTagNames.includes(excludeTag));
        });
      }

      if (bookedCustomerIds.size > 0) {
        customers = customers.filter((c: any) => !bookedCustomerIds.has(c.id));
      }

      // Filter out unsubscribed (for email) and deduplicate by email/phone
      const seenContacts = new Set<string>();
      return customers.filter((c: any) => {
        if (channelType === 'email') {
          const email = c.email?.toLowerCase();
          if (!email || unsubscribedEmails.has(email)) return false;
          if (seenContacts.has(email)) return false;
          seenContacts.add(email);
          return true;
        } else {
          // SMS: deduplicate by phone
          const phone = c.phone?.trim();
          if (!phone || seenContacts.has(phone)) return false;
          seenContacts.add(phone);
          return true;
        }
      });
    } catch (error) {
      console.error('Error fetching selected emails:', error);
      throw error;
    }
  };

  // Fetch filtered out emails (customers excluded from the campaign)
  const fetchFilteredOutEmails = async (): Promise<any[]> => {
    if (!businessId || !supabase) return [];

    try {
      // Get all customers with email/phone - fetch in batches
      const BATCH_SIZE = 1000;
      let allCustomers: any[] = [];
      let offset = 0;
      let hasMore = true;

      const selectFields = channelType === 'sms'
        ? 'id, email, phone, first_name, last_name, Tags, status'
        : 'id, email, first_name, last_name, Tags, status';

      while (hasMore) {
      let query = supabase
        .from('customers')
          .select(selectFields)
        .eq('business_id', businessId);

        if (channelType === 'email') {
          query = query.not('email', 'is', null).neq('email', '');
        } else {
          query = query.not('phone', 'is', null).neq('phone', '');
        }

        const { data, error } = await query
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allCustomers = [...allCustomers, ...data];
          offset += BATCH_SIZE;
          hasMore = data.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      if (!allCustomers || allCustomers.length === 0) return [];

      // Get selected customers
      const selectedCustomers = await fetchSelectedEmails();
      const selectedContactSet = channelType === 'sms'
        ? new Set(selectedCustomers.map((c: any) => c.phone?.trim()).filter(Boolean))
        : new Set(selectedCustomers.map((c: any) => c.email?.toLowerCase()).filter(Boolean));

      // Get unsubscribed emails (only for email campaigns)
      let unsubscribedEmails = new Set<string>();
      if (channelType === 'email') {
        const UNSUBSCRIBE_BATCH_SIZE = 1000;
        let allUnsubscribed: any[] = [];
        let unsubscribeOffset = 0;
        let hasMoreUnsubscribed = true;

        while (hasMoreUnsubscribed) {
          const { data: unsubscribed, error: unsubscribedError } = await supabase
            .from('email_unsubscribes')
            .select('email')
            .eq('business_id', businessId)
            .eq('is_active', true)
            .range(unsubscribeOffset, unsubscribeOffset + UNSUBSCRIBE_BATCH_SIZE - 1);

        if (unsubscribedError) {
          console.error('Error fetching unsubscribed emails:', unsubscribedError);
          break;
        }

          if (unsubscribed && unsubscribed.length > 0) {
            allUnsubscribed = [...allUnsubscribed, ...unsubscribed];
            unsubscribeOffset += UNSUBSCRIBE_BATCH_SIZE;
            hasMoreUnsubscribed = unsubscribed.length === UNSUBSCRIBE_BATCH_SIZE;
          } else {
            hasMoreUnsubscribed = false;
          }
        }

        unsubscribedEmails = new Set(
          allUnsubscribed.map((u: any) => u.email?.toLowerCase()).filter(Boolean)
        );
      }

      const { includeTags, excludeTags } = getTagFiltersFromConditions(conditions);
      const bookingPeriods = getBookingExcludePeriodsFromConditions(conditions);
      let bookedCustomerIds = new Set<string>();
      if (bookingPeriods.length > 0) {
        bookedCustomerIds = await fetchCustomerIdsWithBookingsInPeriods(
          businessId,
          bookingPeriods
        );
      }

      // Find filtered out customers
      const filteredOut = allCustomers
        .filter((customer: any) => {
          if (channelType === 'sms') {
            const phone = customer.phone?.trim();
            // Not in selected list
            if (phone && selectedContactSet.has(phone)) return false;
          } else {
            const email = customer.email?.toLowerCase();
            // Not in selected list
            if (email && selectedContactSet.has(email)) return false;
          }

          // Reasons for exclusion
          const reasons: string[] = [];

          // Inactive
          if (customer.status !== 'active') {
            reasons.push('Inactive');
          }

          // Unsubscribed (only for email)
          if (channelType === 'email') {
            const email = customer.email?.toLowerCase();
            if (email && unsubscribedEmails.has(email)) {
              reasons.push('Unsubscribed');
            }
          }

          // Missing contact info
          if (channelType === 'sms' && !customer.phone) {
            reasons.push('No phone number');
          } else if (channelType === 'email' && !customer.email) {
            reasons.push('No email address');
          }

          // Excluded by tag
          if (excludeTags.length > 0) {
            const customerTags = customer.Tags || [];
            const customerTagNames = customerTags.map((tag: any) => 
              typeof tag === 'string' ? tag : (tag?.name || tag)
            );
            if (excludeTags.some(excludeTag => customerTagNames.includes(excludeTag))) {
              reasons.push('Excluded by tag');
            }
          }

          if (includeTags.length > 0) {
            const customerTags = customer.Tags || [];
            const customerTagNames = customerTags.map((tag: any) =>
              typeof tag === 'string' ? tag : tag?.name || tag
            );
            if (!includeTags.some((includeTag) => customerTagNames.includes(includeTag))) {
              reasons.push('Missing required tag');
            }
          }

          if (
            bookedCustomerIds.has(customer.id) &&
            customer.status === 'active' &&
            (channelType === 'sms' ? customer.phone : customer.email)
          ) {
            reasons.push('Has booking in period');
          }

          // Only include if there's a reason
          if (reasons.length > 0) {
            (customer as any).exclusionReasons = reasons;
            return true;
          }

          return false;
        })
        .map((customer: any) => ({
          ...customer,
          exclusionReasons: (customer as any).exclusionReasons || []
        }));

      return filteredOut;
    } catch (error) {
      console.error('Error fetching filtered out emails:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isAddCustomersModalOpen) {
      setRecipientListView('selected');
    }
  }, [isAddCustomersModalOpen]);

  const loadRecipientListForView = useCallback(
    async (view: 'selected' | 'removed') => {
      if (!businessId) return;
      setLoadingEmailLists(true);
      try {
        if (view === 'selected') {
          const recipients = await fetchSelectedEmails();
          setSelectedEmailsList(recipients);
        } else {
          const removed = await fetchFilteredOutEmails();
          setFilteredOutEmailsList(removed);
        }
      } catch (error) {
        console.error('Error loading recipient list:', error);
      } finally {
        setLoadingEmailLists(false);
      }
    },
    [businessId, channelType, conditions, manualSelectionMode, selectedCustomerIds, selectedSegmentIds, audienceSegments]
  );

  useEffect(() => {
    if (!isAddCustomersModalOpen || !businessId) return;
    void loadRecipientListForView(recipientListView);
  }, [
    isAddCustomersModalOpen,
    recipientListView,
    loadRecipientListForView,
    conditions,
    selectedCustomerIds,
    manualSelectionMode,
    selectedSegmentIds,
    channelType,
  ]);

  const displayTotalRecipients = recipientCount ?? 0;

  const pickerSegments = [...audienceSegments].sort((a, b) => {
    const order = (segment: CustomerSegment) => {
      if (segment.system_key === 'all_contacts') return 0;
      if (segment.system_key === 'customers') return 1;
      if (segment.system_key === 'active_customers') return 2;
      return 3;
    };
    return order(a) - order(b) || a.name.localeCompare(b.name);
  });

  const audienceSummaryText = manualSelectionMode
    ? `${selectedCustomerIds.length} selected customer${selectedCustomerIds.length === 1 ? '' : 's'}`
    : pickerSegments
        .filter((segment) => selectedSegmentIds.includes(segment.id))
        .map((segment) => segment.name)
        .join(', ') || 'All contacts';

  const toggleAudienceSegment = (segment: CustomerSegment) => {
    setSelectedSegmentIds((current) => {
      const allContacts = audienceSegments.find((item) => item.system_key === 'all_contacts');
      if (segment.system_key === 'all_contacts') {
        return allContacts ? [allContacts.id] : [segment.id];
      }
      const withoutAll = current.filter((id) => id !== allContacts?.id);
      const next = withoutAll.includes(segment.id)
        ? withoutAll.filter((id) => id !== segment.id)
        : [...withoutAll, segment.id];
      if (next.length === 0 && allContacts) return [allContacts.id];
      return next;
    });
  };

  const activeRecipientList =
    recipientListView === 'selected' ? selectedEmailsList : filteredOutEmailsList;

  // Reset applied recipient count when editing campaign changes
  useEffect(() => {
    if (editingCampaign) {
      // If editing, use the existing recipient count
      setAppliedRecipientCount(editingCampaign.recipient_count || null);
      
      // Restore manual selection if manualCustomerIds exist
      if (editingCampaign.recipient_filters?.manualCustomerIds && 
          Array.isArray(editingCampaign.recipient_filters.manualCustomerIds) &&
          editingCampaign.recipient_filters.manualCustomerIds.length > 0) {
        setManualSelectionMode(true);
        setSelectedCustomerIds(editingCampaign.recipient_filters.manualCustomerIds);
      } else {
        setManualSelectionMode(false);
        setSelectedCustomerIds([]);
      }
      const savedSegments = editingCampaign.recipient_filters?.segmentIds;
      if (Array.isArray(savedSegments) && savedSegments.length > 0) {
        setSelectedSegmentIds(savedSegments);
      }
    } else {
      // Reset when creating new campaign
      setAppliedRecipientCount(null);
      setManualSelectionMode(false);
      setSelectedCustomerIds([]);
      setSelectedSegmentIds([]);
    }
  }, [editingCampaign]);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    setLoadingAudienceSegments(true);
    setAudienceSegmentError(null);
    listCustomerSegments(businessId)
      .then((segments) => {
        if (cancelled) return;
        setAudienceSegments(segments.filter((segment) => segment.visible));
      })
      .catch((error) => {
        console.error('Error loading customer segments:', error);
        if (!cancelled) setAudienceSegmentError(error?.message || 'Could not load segments');
      })
      .finally(() => {
        if (!cancelled) setLoadingAudienceSegments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (selectedSegmentIds.length > 0) return;
    if (editingCampaign?.recipient_filters?.segmentIds?.length) return;
    const allContacts = audienceSegments.find((segment) => segment.system_key === 'all_contacts');
    if (allContacts) setSelectedSegmentIds([allContacts.id]);
  }, [audienceSegments, editingCampaign, selectedSegmentIds.length]);

  // Load email templates when component mounts or when template type is selected
  useEffect(() => {
    console.log('[TEMPLATES] useEffect triggered:', { emailType, emailTemplatesLength: emailTemplates.length, businessId });
    if (emailType === 'template' && emailTemplates.length === 0) {
      console.log('[TEMPLATES] Calling loadEmailTemplates...');
      loadEmailTemplates();
    }
  }, [emailType]);

  // Refresh the list whenever the picker opens so newly created templates show up.
  useEffect(() => {
    if (showTemplatePicker) loadEmailTemplates();
  }, [showTemplatePicker]);

  // Load verified emails on mount
  useEffect(() => {
    if (businessId && supabase) {
      loadVerifiedEmails();
      loadTwilioSettings();
      void loadMediaConnections();
    }
  }, [businessId, supabase]);

  const loadMediaConnections = async () => {
    if (!businessId || !supabase) {
      setMediaConnections({ gbp: false, facebook: false, instagram: false });
      setMediaConnectionsLoaded(true);
      return;
    }
    try {
      const [gbpRes, socialRes] = await Promise.all([
        GBP_ENABLED
          ? supabase
              .from('google_business_connections')
              .select('id')
              .eq('business_id', businessId)
              .in('provider', ['ayrshare', 'google'])
              .eq('is_active', true)
              .limit(1)
          : Promise.resolve({ data: [] as { id: string }[] }),
        SOCIAL_ENABLED
          ? supabase
              .from('social_media_connections')
              .select('platform')
              .eq('business_id', businessId)
              .eq('provider', 'ayrshare')
              .eq('is_active', true)
          : Promise.resolve({ data: [] as { platform: string }[] }),
      ]);
      const platforms = new Set(
        ((socialRes.data as { platform?: string }[] | null) || [])
          .map((r) => r.platform)
          .filter(Boolean),
      );
      setMediaConnections({
        gbp: Boolean((gbpRes.data as { id: string }[] | null)?.length),
        facebook: platforms.has('facebook'),
        instagram: platforms.has('instagram'),
      });
    } catch (err) {
      console.error('Failed to load media connections for campaign builder:', err);
      setMediaConnections({ gbp: false, facebook: false, instagram: false });
    } finally {
      setMediaConnectionsLoaded(true);
    }
  };

  // Reload Twilio settings when SMS campaign is selected
  useEffect(() => {
    if (channelType === 'sms' && businessId && supabase) {
      loadTwilioSettings();
    }
  }, [channelType, businessId, supabase]);

  // Load tags when Step 2 is active
  useEffect(() => {
    if (currentStep === 2 && businessId) {
      loadAvailableTags();
    }
  }, [currentStep, businessId]);

  // Load verified emails from settings
  const loadVerifiedEmails = async () => {
    if (!supabase || !businessId) return;
    
    try {
      const { data, error } = await supabase
        .from('email_marketing_settings')
        .select('verified_senders, verified_emails')
        .eq('business_id', businessId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      
      // Extract verified emails from verified_senders (new structure)
      let verifiedEmailList: string[] = [];
      
      if (data?.verified_senders && Array.isArray(data.verified_senders)) {
        // Get only verified emails
        verifiedEmailList = data.verified_senders
          .filter((sender: any) => sender.verification_status === 'verified')
          .map((sender: any) => sender.email);
      } else if (data?.verified_emails && Array.isArray(data.verified_emails)) {
        // Fallback to legacy verified_emails field
        verifiedEmailList = data.verified_emails;
      }
      
      setVerifiedEmails(verifiedEmailList);
      
        // Set first verified email as default if not editing
      if (!editingCampaign && verifiedEmailList.length > 0) {
        setFormData(prev => ({ ...prev, from_email: verifiedEmailList[0] }));
      }
    } catch (error) {
      console.error('Error loading verified emails:', error);
    } finally {
      setVerifiedEmailsLoaded(true);
    }
  };

  // Load Twilio settings
  const loadTwilioSettings = async () => {
    if (!supabase || !businessId) return;
    
    try {
      setLoadingTwilioSettings(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('twilio_enabled, VITE_TWILIO_PHONE_NUMBER, twilio_status')
        .eq('id', businessId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      
      if (data) {
        setTwilioSettings({
          twilio_enabled: data.twilio_enabled || false,
          VITE_TWILIO_PHONE_NUMBER: data.VITE_TWILIO_PHONE_NUMBER || null,
          twilio_status: data.twilio_status || null
        });

        // Set Twilio phone number as default if not editing and Twilio is enabled
        if (!editingCampaign && data.twilio_enabled && data.VITE_TWILIO_PHONE_NUMBER) {
          setFormData(prev => ({ ...prev, from_phone_number: data.VITE_TWILIO_PHONE_NUMBER }));
        }
      } else {
        setTwilioSettings(null);
      }
    } catch (error) {
      console.error('Error loading Twilio settings:', error);
      setTwilioSettings(null);
    } finally {
      setLoadingTwilioSettings(false);
    }
  };

  // Handle snippet selection for SMS campaigns
  const handleSnippetSelect = (snippet: any) => {
    try {
      // For campaigns, insert snippet content as-is (variables will be replaced when sending)
      const snippetContent = snippet?.content || '';
      if (!snippetContent) {
        console.warn('Snippet has no content:', snippet);
        return;
      }
      
      const currentContent = formData.sms_content || '';
      const newContent = currentContent 
        ? `${currentContent}\n\n${snippetContent}` 
        : snippetContent;
      
      setFormData(prev => ({ ...prev, sms_content: newContent }));
      setIsSnippetsModalOpen(false);
    } catch (error) {
      console.error('Error inserting snippet:', error);
    }
  };

  // Load available tags for filtering
  const loadAvailableTags = async () => {
    if (!supabase || !businessId) return;
    
    setLoadingTags(true);
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('id, name')
        .eq('business_id', businessId)
        .order('name');

      if (error) throw error;
      setAvailableTags(data || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };


  const loadEmailTemplates = async () => {
    console.log('[TEMPLATES] loadEmailTemplates called:', { supabase: !!supabase, businessId });
    
    if (!supabase || !businessId) {
      console.log('[TEMPLATES] Early return - missing supabase or businessId');
      return;
    }
    
    setLoadingTemplates(true);
    console.log('[TEMPLATES] Starting template query...');
    
    try {
      console.log('[TEMPLATES] About to run query with businessId:', businessId);
      
      // First try a simple query to see what columns exist
      const { data: allTemplates, error: testError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('business_id', businessId)
        .limit(1);
      
      console.log('[TEMPLATES] Test query result:', { allTemplates, testError });
      
      // Now try the specific query with correct column names
      const { data: templates, error } = await supabase
        .from('email_templates')
        .select('id, name, subject, content, template_type, one_off')
        .eq('business_id', businessId)
        .order('name');

      console.log('[TEMPLATES] Raw query result:', { 
        templates, 
        error, 
        templatesCount: templates?.length,
        errorMessage: error?.message,
        errorDetails: error?.details,
        errorHint: error?.hint
      });

      if (error) {
        console.error('[TEMPLATES] Database error details:', error);
        throw error;
      }
      
      setEmailTemplates(templates || []);
      console.log('[TEMPLATES] Templates set in state:', templates?.length || 0);
    } catch (error) {
      console.error('[TEMPLATES] Error loading email templates:', error);
      setEmailTemplates([]);
    } finally {
      setLoadingTemplates(false);
      console.log('[TEMPLATES] Loading finished');
    }
  };

  // templateRecord lets a just-created template be applied before React state
  // catches up (emailTemplates in this closure would still be stale).
  const handleTemplateSelect = (templateId: string, templateRecord?: any) => {
    console.log('[TEMPLATES] Template selected:', { templateId, availableTemplates: emailTemplates.length });
    
    const template = templateRecord || emailTemplates.find(t => t.id === templateId);
    console.log('[TEMPLATES] Found template:', template);
    
    if (template) {
      // Extract HTML content from different formats
      let htmlContent = '';
      
      if (typeof template.content === 'string') {
        htmlContent = template.content;
      } else if (template.content && typeof template.content === 'object') {
        // If content is JSONB, try to extract HTML
        if (template.content.html) {
          htmlContent = template.content.html;
        } else if (template.content.body) {
          htmlContent = template.content.body;
        } else if (template.content.sections && Array.isArray(template.content.sections)) {
          htmlContent = renderEmailTemplateToHtml({
            sections: template.content.sections,
            businessName: selectedBusiness?.name || 'Your Business',
            logoUrl: selectedBusiness?.booking_settings?.logo_url,
          });
        }
      } else if (template.body) {
        htmlContent = template.body;
      } else if (template.html_content) {
        htmlContent = template.html_content;
      }
      
      const newFormData = {
        html_content: htmlContent || '<p>No content available</p>',
        subject: template.subject || template.name // Use template subject or name as fallback
      };
      console.log('[TEMPLATES] Setting form data:', newFormData);
      
      setFormData(prev => ({
        ...prev,
        ...newFormData
      }));
    }
    setSelectedTemplate(templateId);
  };

  // Save a template created inside the campaign flow, then select it for this
  // campaign — the campaign form stays mounted the whole time, so no progress
  // is lost.
  const handleSaveNewTemplate = async (template: any) => {
    if (!businessId) return;
    try {
      const created = await emailTemplateService.createTemplate(businessId, {
        name: template.name,
        content: template.content,
        category: template.category || 'Custom',
        status: 'draft',
        template_type: template.template_type,
      });
      setEmailTemplates(prev => [created, ...prev.filter(t => t.id !== created.id)]);
      setShowTemplateBuilder(false);
      setBuilderTemplate(null);
      setShowTemplatePicker(false);
      handleTemplateSelect(created.id, created);
      toast.success(`Template "${created.name}" saved and selected.`);
    } catch (err) {
      console.error('Error saving template from campaign flow:', err);
      toast.error('Failed to save template. Please try again.');
    }
  };

  // AI-generate a template inside the campaign flow — same edge function and
  // brand-guideline plumbing as the Email Templates page's "AI Create".
  const handleAiCreateTemplate = async () => {
    if (!aiTemplatePrompt.trim() || !businessId) return;

    setAiTemplateGenerating(true);
    setShowAiTemplatePrompt(false);

    const logoUrl = (selectedBusiness as any)?.booking_settings?.logo_url;

    // Open the builder with a loading placeholder while the AI works.
    setBuilderTemplate({
      id: undefined,
      name: 'AI Generated Template',
      content: { subject: '', preheader: '', sections: [] },
      _aiLoading: true,
    });
    setShowTemplateBuilder(true);

    try {
      let brandGuidelines = '';
      let brandButtonColor = '';
      let brandHeaderColor = '';
      let brandPrimaryColor = '';
      try {
        const guidelines = await getBrandGuidelines(businessId);
        brandButtonColor = (guidelines?.button_color || '').trim();
        brandHeaderColor = (guidelines?.header_color || '').trim();
        brandPrimaryColor = (guidelines?.primary_color || '').trim();
        let onlineBookingUrlFallback: string | undefined;
        if (!(guidelines?.online_booking_url || '').trim()) {
          onlineBookingUrlFallback =
            (await resolveDefaultOnlineBookingUrl(businessId, (selectedBusiness as any)?.slug)) ||
            undefined;
        }
        brandGuidelines = formatBrandGuidelinesForPrompt(guidelines, {
          logoUrl,
          websiteUrlFallback: (selectedBusiness as any)?.nexus_website_url,
          onlineBookingUrlFallback,
        });
      } catch (err) {
        console.warn('Could not load brand guidelines for AI template create:', err);
      }

      let availableImages: { id: string; url: string; label: string; source: string }[] = [];
      try {
        availableImages = await gatherEmailTemplateAiImages(businessId);
      } catch (err) {
        console.warn('Could not gather images for AI template create:', err);
      }

      const { data, error } = await supabase.functions.invoke('ai-create-email-template', {
        body: {
          prompt: aiTemplatePrompt.trim(),
          businessId,
          businessName: (selectedBusiness as any)?.name || '',
          brandGuidelines: brandGuidelines || undefined,
          brandButtonColor: brandButtonColor || brandPrimaryColor || undefined,
          brandHeaderColor: brandHeaderColor || undefined,
          availableImages,
        },
      });

      if (error) {
        const ctx = (error as any)?.context;
        let detail = (error as Error).message || 'Failed to generate template';
        try {
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }

      if (data?.insufficientTokens) {
        setShowTemplateBuilder(false);
        setBuilderTemplate(null);
        setShowTokenPurchase(true);
        return;
      }

      if (!data?.success || !data?.template) {
        throw new Error(data?.error || 'Failed to generate template');
      }

      const generated = data.template;
      const sections = applyBrandColorsToEmailTemplateSections(generated.sections || [], {
        buttonColor: brandButtonColor,
        headerColor: brandHeaderColor,
        primaryColor: brandPrimaryColor,
      });
      setBuilderTemplate({
        id: undefined,
        name: generated.name || 'AI Generated Template',
        content: {
          subject: generated.subject || '',
          preheader: generated.preheader || '',
          sections,
        },
      });

      toast.success(`Template created! (${data.tokensUsed} tokens used)`);
    } catch (err: any) {
      console.error('AI template generation error:', err);
      toast.error(err.message || 'Failed to generate template');
      setShowTemplateBuilder(false);
      setBuilderTemplate(null);
    } finally {
      setAiTemplateGenerating(false);
      setAiTemplatePrompt('');
    }
  };

  const handleVariantTemplateSelect = (variantName: string, templateId: string) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      let htmlContent = '';
      if (typeof template.content === 'string') {
        htmlContent = template.content;
      } else if (template.content && typeof template.content === 'object') {
        if (template.content.html) {
          htmlContent = template.content.html;
        } else if (template.content.body) {
          htmlContent = template.content.body;
        } else if (template.content.sections && Array.isArray(template.content.sections)) {
          htmlContent = renderEmailTemplateToHtml({
            sections: template.content.sections,
            businessName: selectedBusiness?.name || 'Your Business',
            logoUrl: selectedBusiness?.booking_settings?.logo_url,
          });
        }
      } else if (template.body) {
        htmlContent = template.body;
      } else if (template.html_content) {
        htmlContent = template.html_content;
      }
      setVariants(prev => prev.map(v => v.name === variantName ? {
        ...v,
        selectedTemplateId: templateId,
        html_content: htmlContent || '<p>No content available</p>',
        subject: template.subject || template.name || v.subject
      } : v));
    } else {
      setVariants(prev => prev.map(v => v.name === variantName ? { ...v, selectedTemplateId: templateId } : v));
    }
  };

  const handleNextStep = () => {
    setCurrentStep(2);
  };

  const handlePreviousStep = () => {
    if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
    setCurrentStep(1);
    }
  };

  const handleReviewAndSend = () => {
    setCurrentStep(3);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim() || !businessId || !supabase) {
      alert('Please enter a valid email address');
      return;
    }

    setSendingTestEmail(true);
    try {
      // Get HTML content based on email type
      let htmlContent = '';
      let emailSubject = '';

      if (emailType === 'template' && selectedTemplate) {
        const template = emailTemplates.find(t => t.id === selectedTemplate);
        if (template) {
          emailSubject = template.subject || template.name || 'Test Email';
          
          // Extract HTML from template content - use the same logic as handleTemplateSelect
          if (typeof template.content === 'string') {
            htmlContent = template.content;
          } else if (template.content && typeof template.content === 'object') {
            if (template.content.html) {
              htmlContent = template.content.html;
            } else if (template.content.body) {
              htmlContent = template.content.body;
            } else if (template.content.sections && Array.isArray(template.content.sections)) {
              htmlContent = renderEmailTemplateToHtml({
                sections: template.content.sections,
                businessName: selectedBusiness?.name || 'Your Business',
                logoUrl: selectedBusiness?.booking_settings?.logo_url,
              });
            }
          }
        }
      } else {
        emailSubject = formData.subject || 'Test Email';
        htmlContent = formData.html_content || '<p>No content available</p>';
      }

      if (!htmlContent) {
        throw new Error('No email content available to send');
      }

      // Send test email using Gmail
      const { data, error } = await supabase.functions.invoke('gmail-send-email', {
        body: {
          to: testEmail.trim(),
          subject: `[TEST] ${emailSubject}`,
          body: htmlContent,
          businessId: businessId
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to send test email');
      }

      if (data?.success) {
        alert('Test email sent successfully!');
        setTestEmail('');
        setShowTestEmailModal(false);
      } else {
        throw new Error(data?.error || 'Failed to send test email');
      }
    } catch (error: any) {
      console.error('Error sending test email:', error);
      alert(`Failed to send test email: ${error.message || 'Please try again.'}`);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    
    try {
      // Calculate scheduled_at — parse as local time, store as UTC
      let scheduled_at: string | null = null;
      if (sendOption === 'schedule' && scheduledDate && scheduledTime) {
        // No "Z" suffix → parsed as browser local time → .toISOString() converts to UTC
        scheduled_at = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      }

      // Prepare recipient filters for storage
      const filtersToStore = recipientType === 'all_customers' 
        ? {
            hasEmail: recipientFilters.hasEmail !== false,
            includeTags: recipientFilters.includeTags || undefined,
            excludeTags: recipientFilters.excludeTags || undefined,
            excludeBookingPeriods: recipientFilters.excludeBookingPeriods || undefined,
            manualCustomerIds: recipientFilters.manualCustomerIds || undefined,
            segmentIds: recipientFilters.segmentIds || undefined,
          }
        : {
            tags: recipientFilters.tags || [],
            status: recipientFilters.status || 'active',
            source: recipientFilters.source || null,
            hasEmail: recipientFilters.hasEmail !== false,
            includeTags: recipientFilters.includeTags || undefined,
            excludeTags: recipientFilters.excludeTags || undefined,
            excludeBookingPeriods: recipientFilters.excludeBookingPeriods || undefined,
            manualCustomerIds: recipientFilters.manualCustomerIds || undefined,
            segmentIds: recipientFilters.segmentIds || undefined,
            minSpent: recipientFilters.minSpent || null,
            createdAfter: recipientFilters.createdAfter || null,
            createdBefore: recipientFilters.createdBefore || null,
            lastInteractionAfter: recipientFilters.lastInteractionAfter || null
          };

      const campaignData: any = {
        business_id: selectedBusiness?.id,
        name: formData.name,
        channel_type: channelType,
        status: sendOption === 'now' ? 'draft' : (scheduled_at ? 'scheduled' : 'draft'),
        campaign_type: isSplitTest ? 'ab_test' : 'standard',
        is_ab_test: isSplitTest,
        recipient_type: recipientType,
        recipient_filters: filtersToStore,
        // Estimated count so multi-media drafts keep their audience summary on
        // re-edit; the real count is rebuilt at launch/schedule.
        recipient_count: appliedRecipientCount ?? 0,
        scheduled_at: scheduled_at,
        send_immediately: sendOption === 'now',
        timezone: timezone,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0
      };

      // Add channel-specific fields — primary message is always formData
      if (channelType === 'email') {
        campaignData.subject = formData.subject;
        campaignData.html_content = formData.html_content;
        campaignData.from_name = formData.from_name;
        campaignData.from_email = formData.from_email;
        campaignData.from_phone_number = null;
        campaignData.sms_content = null;
      } else if (channelType === 'sms') {
        campaignData.sms_content = formData.sms_content;
        campaignData.from_phone_number = twilioSettings?.VITE_TWILIO_PHONE_NUMBER || formData.from_phone_number;
        campaignData.subject = null;
        campaignData.from_email = null;
        campaignData.from_name = null;
        campaignData.html_content = null;
      }

      // Attach split test variants (primary = formData as "a", plus additional variants)
      if (isSplitTest && variants.length > 0) {
        const primaryAsVariant: CampaignVariant = {
          name: 'a',
          subject: formData.subject,
          html_content: formData.html_content,
          sms_content: formData.sms_content,
          from_name: formData.from_name,
          emailType: emailType,
          selectedTemplateId: selectedTemplate,
        };
        campaignData._variants = [primaryAsVariant, ...variants];
      }

      // Extended campaign: stage follow-up steps (email channel only)
      if (channelType === 'email' && extendedSteps.length > 0) {
        campaignData._sequenceSteps = extendedSteps.map((step, index) => ({
          step_number: index + 2,
          delay_hours: step.delayUnit === 'days' ? step.delayValue * 24 : step.delayValue,
          condition: step.condition,
          subject: step.subject,
          html_content: step.html_content,
          variants: step.variants,
        }));
      }

      await onSave(campaignData);
      if (!campaignData.send_immediately && !campaignData.scheduled_at) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign. Please try again.');
      setSaving(false);
    }
  };

  // Progressive disclosure flags
  const dateSelected = sendOption === 'now' || (sendOption === 'schedule' && scheduledDate !== '');
  const channelSelected =
    campaignKind === 'single' &&
    (currentStep >= 2 || !!editingCampaign?.id || !!multiMediaStep) &&
    dateSelected;

  const emailConnected = verifiedEmailsLoaded && verifiedEmails.length > 0;
  const smsConnected = Boolean(
    twilioSettings?.twilio_enabled && twilioSettings?.VITE_TWILIO_PHONE_NUMBER,
  );
  const connectedChannelCount = [
    emailConnected,
    smsConnected,
    mediaConnections.gbp,
    mediaConnections.facebook,
    mediaConnections.instagram,
  ].filter(Boolean).length;
  const multiMediaReady =
    mediaConnectionsLoaded && verifiedEmailsLoaded && connectedChannelCount > 1;

  const multiMediaChannelRows: {
    id: keyof typeof multiChannelToggles;
    label: string;
    description: string;
    connected: boolean;
    icon: typeof Mail | typeof GoogleBusinessIcon;
    accent: string;
  }[] = [
    {
      id: 'email',
      label: 'Email',
      description: emailConnected ? 'Verified sender ready' : 'Not connected',
      connected: emailConnected,
      icon: Mail,
      accent: 'blue',
    },
    {
      id: 'sms',
      label: 'SMS',
      description: smsConnected ? 'Twilio number ready' : 'Not connected',
      connected: smsConnected,
      icon: MessageCircle,
      accent: 'green',
    },
    {
      id: 'gbp',
      label: 'Google Business',
      description: mediaConnections.gbp ? 'Connected' : 'Not connected',
      connected: mediaConnections.gbp,
      icon: GoogleBusinessIcon,
      accent: 'sky',
    },
    {
      id: 'facebook',
      label: 'Facebook',
      description: mediaConnections.facebook ? 'Connected' : 'Not connected',
      connected: mediaConnections.facebook,
      icon: FacebookIcon,
      accent: 'indigo',
    },
    {
      id: 'instagram',
      label: 'Instagram',
      description: mediaConnections.instagram ? 'Connected' : 'Not connected',
      connected: mediaConnections.instagram,
      icon: InstagramIcon,
      accent: 'pink',
    },
  ];

  const scheduledInPast = useMemo(() => {
    if (sendOption !== 'schedule' || !scheduledDate || !scheduledTime) return false;
    return new Date(`${scheduledDate}T${scheduledTime}:00`).getTime() <= Date.now();
  }, [sendOption, scheduledDate, scheduledTime]);

  return (
    <div className="space-y-5 sm:space-y-6">
      {currentStep !== 3 && (<>
      {/* Section 1: When to Send — hidden in multi-media step mode (wizard owns the schedule) */}
      {!multiMediaStep && (
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3" style={{ letterSpacing: '-0.01em' }}>
          When to Send
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setSendOption('now');
              setScheduledDate('');
              setScheduledTime('');
              if (currentStep < 2 && !editingCampaign?.id) {
                setCurrentStep(1);
                setCampaignKind('single');
              }
            }}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              sendOption === 'now'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Send Now
          </button>
          <button
            onClick={() => {
              setSendOption('schedule');
              if (!scheduledDate) {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setScheduledDate(tomorrow.toISOString().split('T')[0]);
              }
              if (!scheduledTime) setScheduledTime('10:00');
            }}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              sendOption === 'schedule'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Custom Date
          </button>
        </div>

        {/* Date/Time picker - shown when "Custom Date" is selected */}
        {sendOption === 'schedule' && (
          <div className={`mt-3 p-4 border rounded-xl space-y-3 min-w-0 ${scheduledInPast ? 'bg-red-50/50 border-red-300' : 'bg-blue-50/50 border-blue-200'}`}>
            {/* flex + basis-0/min-w-0: iOS WKWebView date/time inputs overflow grid and overlap */}
            <div className="flex gap-3 min-w-0">
              <div className="flex-1 basis-0 min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className={`w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${scheduledInPast ? 'border-red-300' : 'border-gray-200'}`}
                />
              </div>
              <div className="flex-1 basis-0 min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className={`w-full min-w-0 max-w-full box-border px-3 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${scheduledInPast ? 'border-red-300' : 'border-gray-200'}`}
                />
              </div>
            </div>
            {scheduledInPast ? (
              <p className="text-[12px] text-red-600 font-medium">
                This date and time is in the past. Select a future time or use Send Now.
              </p>
            ) : scheduledDate && scheduledTime ? (
              <p className="text-[11px] text-gray-500">
                Scheduled for {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
                })}
              </p>
            ) : null}
          </div>
        )}
      </div>
      )}

      {/* Section 2: Channel Selection — visible after date is set; hidden in multi-media step mode */}
      {dateSelected && !multiMediaStep && (
        <>
          <div className="border-t border-gray-100" />
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3" style={{ letterSpacing: '-0.01em' }}>
              Campaign Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {(() => {
                const emailDisabled = verifiedEmailsLoaded && verifiedEmails.length === 0;
                return (
              <button
                type="button"
                onClick={() => {
                  if (emailDisabled) return;
                  setCampaignKind('single');
                  setChannelType('email');
                  setCurrentStep(2);
                }}
                disabled={emailDisabled}
                title={emailDisabled ? 'Verify a sender email in Connected Channels to send email campaigns' : undefined}
                className={`flex items-center gap-3 p-3.5 border-2 rounded-xl transition-all duration-200 ${
                  emailDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : campaignKind === 'single' && channelType === 'email' && channelSelected
                    ? 'border-blue-500 bg-blue-50 cursor-pointer'
                    : 'border-gray-200 hover:border-blue-300 bg-white cursor-pointer'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  emailDisabled ? 'bg-gray-200' : campaignKind === 'single' && channelType === 'email' && channelSelected ? 'bg-blue-600' : 'bg-blue-100'
                }`}>
                  <Mail className={`h-5 w-5 ${emailDisabled ? 'text-gray-400' : campaignKind === 'single' && channelType === 'email' && channelSelected ? 'text-white' : 'text-blue-600'}`} />
                </div>
                <div className="text-left">
                  <div className={`font-semibold text-sm ${emailDisabled ? 'text-gray-400' : 'text-gray-900'}`}>Email</div>
                  <div className={`text-[11px] ${emailDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                    {emailDisabled ? 'Verify a sender email first' : 'HTML emails'}
                  </div>
                </div>
              </button>
                );
              })()}

              <button
                type="button"
                onClick={() => {
                  setCampaignKind('single');
                  setChannelType('sms');
                  setCurrentStep(2);
                }}
                className={`flex items-center gap-3 p-3.5 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  campaignKind === 'single' && channelType === 'sms' && channelSelected
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300 bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  campaignKind === 'single' && channelType === 'sms' && channelSelected ? 'bg-green-600' : 'bg-green-100'
                }`}>
                  <MessageCircle className={`h-5 w-5 ${campaignKind === 'single' && channelType === 'sms' && channelSelected ? 'text-white' : 'text-green-600'}`} />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-sm text-gray-900">SMS</div>
                  <div className="text-[11px] text-gray-500">Text messages</div>
                </div>
              </button>

              {onStartMultiMedia && (
              <button
                type="button"
                onClick={() => {
                  if (!multiMediaReady) return;
                  setCampaignKind('multimedia');
                  setCurrentStep(1);
                  setMultiChannelToggles({
                    email: false,
                    sms: false,
                    gbp: false,
                    facebook: false,
                    instagram: false,
                  });
                }}
                disabled={!multiMediaReady}
                title={
                  multiMediaReady
                    ? 'Post across multiple connected channels'
                    : 'Connect more than one channel to unlock Multi Media Campaign'
                }
                className={`col-span-2 flex items-center gap-3 p-3.5 border-2 rounded-xl transition-all duration-200 ${
                  !multiMediaReady
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : campaignKind === 'multimedia'
                      ? 'border-violet-500 bg-violet-50 cursor-pointer'
                      : 'border-gray-200 hover:border-violet-300 bg-white cursor-pointer'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                    !multiMediaReady
                      ? 'bg-gray-200'
                      : campaignKind === 'multimedia'
                        ? 'bg-violet-600'
                        : 'bg-violet-100'
                  }`}
                >
                  <Images
                    className={`h-5 w-5 ${
                      !multiMediaReady
                        ? 'text-gray-400'
                        : campaignKind === 'multimedia'
                          ? 'text-white'
                          : 'text-violet-600'
                    }`}
                  />
                </div>
                <div className="text-left min-w-0">
                  <div
                    className={`font-semibold text-sm ${!multiMediaReady ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    Multi Media
                  </div>
                  <div className={`text-[11px] ${!multiMediaReady ? 'text-gray-400' : 'text-gray-500'}`}>
                    {multiMediaReady
                      ? 'Email, SMS & social'
                      : 'Connect 2+ channels'}
                  </div>
                </div>
              </button>
              )}
            </div>

            {campaignKind === 'multimedia' && multiMediaReady ? (
              <div className="mt-4 space-y-2">
                <p className="text-[13px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                  Turn on the channels you want to include. Disconnected channels stay unavailable.
                </p>
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
                  {multiMediaChannelRows.map((row) => {
                    const Icon = row.icon;
                    const on = multiChannelToggles[row.id];
                    return (
                      <div
                        key={row.id}
                        className={`flex items-center justify-between gap-3 px-3.5 py-3 ${
                          row.connected ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
                              row.id === 'gbp' || row.id === 'facebook' || row.id === 'instagram'
                                ? `bg-white border border-gray-200 ${row.connected ? '' : 'opacity-50 grayscale'}`
                                : row.connected
                                  ? 'bg-gray-100'
                                  : 'bg-gray-200'
                            }`}
                          >
                            <Icon
                              className={
                                row.id === 'gbp' || row.id === 'facebook' || row.id === 'instagram'
                                  ? 'h-9 w-9'
                                  : `h-4 w-4 ${row.connected ? 'text-gray-800' : 'text-gray-400'}`
                              }
                            />
                          </div>
                          <div className="min-w-0">
                            <div
                              className={`text-[14px] font-semibold ${
                                row.connected ? 'text-gray-900' : 'text-gray-400'
                              }`}
                            >
                              {row.label}
                            </div>
                            <div
                              className={`text-[11px] ${
                                row.connected ? 'text-gray-500' : 'text-gray-400'
                              }`}
                            >
                              {row.description}
                            </div>
                          </div>
                        </div>
                        {row.connected ? (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            onClick={() =>
                              setMultiChannelToggles((prev) => ({
                                ...prev,
                                [row.id]: !prev[row.id],
                              }))
                            }
                            className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
                              on ? 'bg-violet-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                                on ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-[11px] font-semibold text-gray-400 px-2 py-1 rounded-md bg-gray-100 border border-gray-200">
                            Not connected
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Continue → multi-media wizard with the toggled channels */}
                {(() => {
                  const chosenChannels = multiMediaChannelRows
                    .filter((row) => row.connected && multiChannelToggles[row.id])
                    .map((row) => row.id) as MultiMediaChannel[];
                  return (
                    <button
                      type="button"
                      disabled={chosenChannels.length === 0 || scheduledInPast}
                      onClick={() =>
                        onStartMultiMedia?.(chosenChannels, {
                          sendOption,
                          scheduledDate,
                          scheduledTime,
                          timezone,
                        })
                      }
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      Continue
                      {chosenChannels.length > 0
                        ? ` · ${chosenChannels.length} channel${chosenChannels.length !== 1 ? 's' : ''}`
                        : ''}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  );
                })()}
              </div>
            ) : null}
          </div>
        </>
      )}

      {/* Section 3: Target — only visible after single email/SMS channel is selected */}
      {channelSelected && (
        <>
          <div className="border-t border-gray-100" />
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3" style={{ letterSpacing: '-0.01em' }}>
              Target Audience
            </label>

            {appliedRecipientCount === null ? (
              <button
                type="button"
                onClick={() => setIsAddCustomersModalOpen(true)}
                className="w-full p-5 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-blue-600"
              >
                <Users className="h-5 w-5" />
                <span className="font-medium text-sm">Add Target Audience</span>
              </button>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Recipients:</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-base font-bold text-blue-600">
                      {appliedRecipientCount.toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsAddCustomersModalOpen(true)}
                      className="px-2.5 py-1 text-xs font-medium bg-white border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  {audienceSummaryText}
                  {conditions.length > 0 ? ' · extra filters applied' : ''}
                  {channelType === 'email' ? ' · unsubscribed excluded' : ''}
                </p>
              </div>
            )}
          </div>

          {/* Section 4: Campaign Content — visible after target is set */}
          {appliedRecipientCount !== null && appliedRecipientCount > 0 && (
            <>
              <div className="border-t border-gray-100" />
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3" style={{ letterSpacing: '-0.01em' }}>
                  Campaign Details
                </label>

                <div className="space-y-4">
                  {/* Campaign Name */}
                  <div>
                    <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Campaign Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Spring Sale 2026"
                      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                    />
                  </div>

                  {/* AI writer */}
                  <div className="flex items-center justify-between gap-2 rounded-xl border border-violet-100 bg-violet-50/60 px-3.5 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkles className="h-4 w-4 text-violet-600 flex-shrink-0" />
                      <span className="text-[12px] text-violet-900 truncate">Let AI write your {channelType === 'sms' ? 'SMS' : 'email'} — 3 options to pick from or split test.</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAiWriterOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold hover:bg-violet-700 flex-shrink-0"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Write with AI
                    </button>
                  </div>

                  {/* Message — primary (always shown) */}
                  {channelType === 'email' ? (
                    <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                      {/* Email Type Toggle */}
                      <div>
                        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Email Type</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEmailType('custom')}
                            className={`flex-1 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                              emailType === 'custom' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            Custom Email
                          </button>
                          <button
                            type="button"
                            onClick={() => setEmailType('template')}
                            className={`flex-1 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                              emailType === 'template' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            Use Template
                          </button>
                        </div>
                      </div>

                      {emailType === 'template' ? (
                        <>
                          {/* Template Picker — opens a right-side sheet */}
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Template</label>
                            {selectedTemplate ? (
                              <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-white">
                                  <div className="min-w-0">
                                    <div className="text-[13px] font-medium text-gray-900 truncate">
                                      {emailTemplates.find(t => t.id === selectedTemplate)?.name || 'Selected Template'}
                                    </div>
                                    {formData.subject && (
                                      <div className="text-[11px] text-gray-500 truncate mt-0.5">{formData.subject}</div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setShowTemplatePicker(true)}
                                    className="flex-shrink-0 px-3 py-1.5 text-[12px] font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                  >
                                    Change
                                  </button>
                                </div>
                                <div className="border-t border-gray-100 p-3 bg-gray-50 max-h-40 overflow-y-auto">
                                  <div className="text-[12px] text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: formData.html_content || '' }} />
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setShowTemplatePicker(true)}
                                className="w-full flex items-center justify-center gap-2 px-3.5 py-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/40 transition-all"
                              >
                                <FileText className="h-4 w-4" />
                                <span className="text-[13px] font-semibold">Select Template</span>
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Subject Line */}
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Subject Line</label>
                            <input
                              type="text"
                              value={formData.subject}
                              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                              placeholder="Enter email subject..."
                              className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                            />
                          </div>

                          {/* Email Body */}
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Email Body</label>
                            <CampaignRichTextEditor
                              value={formData.html_content}
                              onChange={(html) => setFormData(prev => ({ ...prev, html_content: html }))}
                              placeholder="Enter your email content..."
                              rows={6}
                              businessId={selectedBusiness?.id}
                            />
                          </div>
                        </>
                      )}

                      {/* From Name & Email (always shown) */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1.5">From Name</label>
                          <input
                            type="text"
                            value={formData.from_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, from_name: e.target.value }))}
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-[12px] font-medium text-gray-500 mb-1.5">From Email</label>
                          <input
                            type="email"
                            value={formData.from_email}
                            onChange={(e) => setFormData(prev => ({ ...prev, from_email: e.target.value }))}
                            className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[12px] font-medium text-gray-500">Message</label>
                        <button
                          type="button"
                          onClick={() => setIsSnippetsModalOpen(true)}
                          className="text-[11px] font-medium text-blue-600 hover:text-blue-700"
                        >
                          + Insert Snippet
                        </button>
                      </div>
                      <textarea
                        value={formData.sms_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, sms_content: e.target.value }))}
                        placeholder="Enter your SMS message..."
                        rows={4}
                        maxLength={1600}
                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all resize-y"
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[11px] text-gray-400">{(formData.sms_content || '').length} / 1,600 characters</span>
                        {twilioSettings?.VITE_TWILIO_PHONE_NUMBER && (
                          <span className="text-[11px] text-gray-400">From: {twilioSettings.VITE_TWILIO_PHONE_NUMBER}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Split Test Messages */}
                  {isSplitTest && variants.map((variant, idx) => (
                    <div key={variant.name} className="border border-blue-200 bg-blue-50/30 rounded-xl p-4 relative">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                            {variant.name.toUpperCase()}
                          </span>
                          <span className="text-[12px] font-semibold text-gray-700">Split Test {variant.name.toUpperCase()}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            removeVariant(variant.name);
                            if (variants.length <= 1) setIsSplitTest(false);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {channelType === 'email' ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Subject</label>
                            <input
                              type="text"
                              value={variant.subject}
                              onChange={(e) => updateVariant(variant.name, 'subject', e.target.value)}
                              placeholder="Email subject line..."
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-[13px] text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-500 mb-1">Body</label>
                            <CampaignRichTextEditor
                              value={variant.html_content}
                              onChange={(html) => updateVariant(variant.name, 'html_content', html)}
                              placeholder="Email content..."
                              rows={4}
                              businessId={selectedBusiness?.id}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <textarea
                            value={variant.sms_content}
                            onChange={(e) => updateVariant(variant.name, 'sms_content', e.target.value)}
                            placeholder="Enter alternate SMS message..."
                            rows={3}
                            maxLength={1600}
                            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-[13px] text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-y"
                          />
                          <div className="text-[11px] text-gray-400 mt-1">
                            {(variant.sms_content || '').length} / 1,600
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Split Test button */}
                  {(() => {
                    const variantsAfterAdd = variants.length + 2; // primary + existing + new
                    const tooFewRecipients = (appliedRecipientCount || 0) < variantsAfterAdd;
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setIsSplitTest(true);
                            addVariant();
                          }}
                          disabled={tooFewRecipients}
                          className={`w-full py-2.5 border-2 border-dashed rounded-xl text-[13px] font-medium transition-all ${
                            tooFewRecipients
                              ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                              : 'border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50'
                          }`}
                        >
                          + Add Split Test
                        </button>
                        {tooFewRecipients && (
                          <p className="text-[12px] text-red-500 text-center">
                            You need at least {variantsAfterAdd} recipients to split test {variantsAfterAdd} variants. Current target: {appliedRecipientCount || 0}.
                          </p>
                        )}
                      </>
                    );
                  })()}

                  {variants.length > 0 && (appliedRecipientCount || 0) >= variants.length + 1 && (
                    <p className="text-[11px] text-gray-400 text-center">
                      Audience will be split equally — {Math.round(100 / (variants.length + 1))}% per message
                    </p>
                  )}
                </div>
              </div>

              {/* Extended campaign (email only): follow-up emails on engagement conditions */}
              {channelType === 'email' && (
                <div>
                  {extendedSteps.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setShowExtendedEditor(true)}
                      className="w-full py-2.5 border-2 border-dashed border-purple-300 rounded-xl text-[13px] font-medium text-purple-600 hover:border-purple-400 hover:bg-purple-50/50 transition-all"
                    >
                      + Create Advanced Campaign (automatic follow-up emails)
                    </button>
                  ) : (
                    <div className="rounded-xl border-2 border-purple-200 bg-purple-50/40 p-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[13px] font-semibold text-purple-900">
                          Extended campaign · {extendedSteps.length + 1} emails
                        </p>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setShowExtendedEditor(true)}
                            className="rounded-lg px-2.5 py-1 text-[12px] font-medium text-purple-700 hover:bg-purple-100 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setExtendedSteps([])}
                            className="rounded-lg px-2.5 py-1 text-[12px] font-medium text-red-500 hover:bg-red-50 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        {extendedSteps.map((step, i) => (
                          <p key={step.id} className="truncate text-[12px] text-purple-800/80">
                            <span className="font-semibold">Email {i + 2}:</span>{' '}
                            wait {stepDelayLabel(step)} → {STEP_CONDITION_LABELS[step.condition]} →{' '}
                            {step.subject || 'Untitled'}
                            {step.variants.length > 0 ? ` (${step.variants.length + 1}-way split)` : ''}
                          </p>
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] text-purple-700/70">
                        You'll be charged for the initial email now. Each follow-up is charged separately
                        when it sends, based on how many people match its condition.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReviewAndSend}
              disabled={
                appliedRecipientCount === null || 
                appliedRecipientCount === 0 ||
                !formData.name.trim() ||
                scheduledInPast
              }
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-semibold flex items-center justify-center gap-2"
            >
              {multiMediaStep ? 'Next' : 'Review & Send'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
      </>)}

      {/* Review Step */}
      {currentStep === 3 && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 mb-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${sendOption === 'now' ? 'bg-blue-100' : 'bg-purple-100'}`}>
              {sendOption === 'now' ? <Send className="h-4 w-4 text-blue-600" /> : <Clock className="h-4 w-4 text-purple-600" />}
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900">
                {multiMediaStep
                  ? `Review ${channelType === 'sms' ? 'SMS' : 'Email'}`
                  : sendOption === 'now' ? 'Ready to Send' : 'Ready to Schedule'}
              </h3>
              <p className="text-[12px] text-gray-500">
                {multiMediaStep
                  ? 'Nothing sends yet — you\'ll create the full multi media campaign at the end'
                  : sendOption === 'now' 
                  ? 'Review your campaign before sending' 
                  : `Scheduled for ${scheduledDate} at ${scheduledTime}`
                }
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
            {/* Campaign Title */}
            <div>
              <label className="block text-[12px] font-medium text-gray-500 mb-1">
                Campaign Title
              </label>
              <div className="text-[14px] text-gray-900 font-semibold">
                {formData.name || <span className="text-red-500 italic">No title set</span>}
              </div>
            </div>

            {/* Extended campaign summary + billing disclaimer */}
            {channelType === 'email' && extendedSteps.length > 0 && (
              <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-3.5">
                <p className="text-[12px] font-semibold text-purple-900">
                  Extended campaign · {extendedSteps.length + 1} emails
                </p>
                <div className="mt-1.5 space-y-1">
                  {extendedSteps.map((step, i) => (
                    <p key={step.id} className="truncate text-[12px] text-purple-800/80">
                      <span className="font-semibold">Email {i + 2}:</span>{' '}
                      wait {stepDelayLabel(step)} → {STEP_CONDITION_LABELS[step.condition]} →{' '}
                      {step.subject || 'Untitled'}
                    </p>
                  ))}
                </div>
                <p className="mt-2 text-[11px] font-medium text-purple-900">
                  Billing: you're charged for the initial email now ($0.01 per recipient).
                  Each follow-up email is charged separately when it sends, based on how many
                  recipients match its condition at that time.
                </p>
              </div>
            )}

            {/* Email/SMS Content */}
            {isSplitTest ? (() => {
              // Include the primary message ("A") alongside the added variants so the
              // review shows every split (e.g. A + B + C), matching what gets sent.
              const splitPreview = [
                {
                  name: 'a',
                  subject: formData.subject,
                  html_content: formData.html_content,
                  sms_content: formData.sms_content,
                  from_name: formData.from_name,
                  emailType,
                  selectedTemplateId: selectedTemplate,
                },
                ...variants,
              ];
              return (
              /* ---- Split test: show each variant ---- */
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  Split Test Variants ({splitPreview.length})
                </label>
                <p className="text-xs text-gray-500">
                  Audience will be split equally &mdash; {Math.round(100 / splitPreview.length)}% per variant
                </p>
                {splitPreview.map((v, idx) => (
                  <div key={v.name} className="border-2 border-blue-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">{v.name.toUpperCase()}</span>
                      <span className="text-sm font-semibold text-gray-900">{idx === 0 ? 'Original Message' : `Variant ${v.name.toUpperCase()}`}</span>
                    </div>

                    {channelType === 'email' ? (
                      v.emailType === 'template' && v.selectedTemplateId ? (
                        <div className="space-y-2">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Template:</span>{' '}
                            {emailTemplates.find(t => t.id === v.selectedTemplateId)?.name || 'Selected Template'}
                          </div>
                          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                            <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: v.html_content || '<p class="text-gray-400 italic">Template content</p>' }} />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500">Subject:</span>
                            <div className="text-sm text-gray-900 mt-0.5">
                              {v.subject || <span className="text-red-500 italic">No subject set</span>}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">Body:</span>
                            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto mt-0.5">
                              <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: v.html_content || '<p class="text-red-500 italic">No content set</p>' }} />
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      <div>
                        <span className="text-xs font-medium text-gray-500">Message:</span>
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto mt-0.5">
                          <div className="text-sm text-gray-700 whitespace-pre-wrap">
                            {v.sms_content || <span className="text-red-500 italic">No message set</span>}
                          </div>
                          {v.sms_content && (
                            <div className="text-xs text-gray-500 mt-2">{v.sms_content.length} / 1600 characters</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              );
            })() : channelType === 'email' ? (
              /* ---- Single email ---- */
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content
                </label>
                {emailType === 'template' && selectedTemplate ? (
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Template:</span>{' '}
                      {emailTemplates.find(t => t.id === selectedTemplate)?.name || 'Selected Template'}
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                      {(() => {
                        const template = emailTemplates.find(t => t.id === selectedTemplate);
                        if (template?.content && typeof template.content === 'object' && template.content.sections) {
                          return (
                            <div className="space-y-2">
                              {template.content.sections.map((section: any, idx: number) => (
                                <div key={idx}>
                                  {section.type === 'header' && (
                                    <div 
                                      className="px-4 py-2 text-center text-sm font-bold"
                                      style={{ 
                                        backgroundColor: section.content?.backgroundColor || '#6366f1',
                                        color: section.content?.textColor || '#ffffff'
                                      }}
                                    >
                                      {selectedBusiness?.name || 'Business Name'}
                                    </div>
                                  )}
                                  {section.type === 'hero' && (
                                    <div className="px-4 py-2">
                                      {section.content?.heading && (
                                        <div className="text-base font-bold text-gray-900 mb-1">
                                          {section.content.heading}
                                        </div>
                                      )}
                                      {section.content?.subheading && (
                                        <div className="text-sm text-gray-600 mb-2">
                                          {section.content.subheading}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {section.type === 'text' && section.content?.text && (
                                    <div className="px-4 py-2 text-sm text-gray-700">
                                      {section.content.text}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return <div className="text-sm text-gray-500">Template preview</div>;
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Subject:</span>
                      <div className="text-base text-gray-900 mt-1">
                        {formData.subject || <span className="text-red-500 italic">No subject set</span>}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Body:</span>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto mt-1">
                        <div 
                          className="text-sm text-gray-700 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: formData.html_content || '<p class="text-red-500 italic">No content set</p>' }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ---- Single SMS ---- */
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Content
                </label>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">From Phone Number:</span>
                    <div className="text-base text-gray-900 mt-1">
                      {twilioSettings?.VITE_TWILIO_PHONE_NUMBER || <span className="text-red-500 italic">No phone number set</span>}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Message:</span>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto mt-1">
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {formData.sms_content || <span className="text-red-500 italic">No message set</span>}
                      </div>
                      {formData.sms_content && (
                        <div className="text-xs text-gray-500 mt-2">
                          {formData.sms_content.length} / 1600 characters
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recipients */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipients
              </label>
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="text-base text-gray-900 font-semibold">
                  {appliedRecipientCount?.toLocaleString() || 0} {appliedRecipientCount === 1 ? 'customer' : 'customers'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {audienceSummaryText}
                {conditions.length > 0 ? ' · extra filters applied' : ''}
                {channelType === 'email' ? ' · unsubscribed excluded' : ''}
              </p>
            </div>

            {/* Send Option */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send Schedule
              </label>
              <div className="text-base text-gray-900">
                {sendOption === 'now' ? (
                  'Send immediately after campaign is created'
                ) : (
                  `Scheduled for ${scheduledDate} at ${scheduledTime} (${timezone})`
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePreviousStep}
              className="w-full sm:w-auto px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors flex items-center justify-center sm:justify-start order-2 sm:order-1 text-sm font-medium gap-2"
            >
              <ArrowRight className="h-4 w-4 rotate-180" />
              Back
            </button>
            <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2 w-full sm:w-auto">
              {channelType === 'email' && (
                <button
                  type="button"
                  onClick={() => setShowTestEmailModal(true)}
                  className="w-full sm:w-auto px-5 py-2.5 text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors text-sm font-medium"
                >
                  Send Preview
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  // Multi-media wizard step: hand campaignData back to the wizard
                  // (nothing sends yet) — the wizard has its own final review.
                  if (multiMediaStep) {
                    void handleSubmit();
                  } else {
                    setShowFinalConfirm(true);
                  }
                }}
                disabled={saving}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center sm:justify-start font-semibold text-sm gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {multiMediaStep ? 'Saving...' : sendOption === 'now' ? 'Sending...' : 'Scheduling...'}
                  </>
                ) : multiMediaStep ? (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </>
                ) : sendOption === 'now' ? (
                  <>
                    <Send className="h-4 w-4" />
                    Send
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4" />
                    Schedule · {scheduledDate} at {scheduledTime}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final send confirmation */}
      {showFinalConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[10002] flex items-center justify-center p-4" onClick={() => !saving && setShowFinalConfirm(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${sendOption === 'now' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                {sendOption === 'now' ? <Send className="h-4 w-4 text-blue-600" /> : <Clock className="h-4 w-4 text-purple-600" />}
              </div>
              <div className="min-w-0">
                <h3 className="text-[16px] font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                  {sendOption === 'now' ? 'Send this campaign now?' : 'Schedule this campaign?'}
                </h3>
                <p className="text-[12px] text-gray-500">
                  {channelType === 'sms' ? 'SMS' : 'Email'} · {(appliedRecipientCount || 0).toLocaleString()} recipient{(appliedRecipientCount || 0) !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 mb-4">
              <p className="text-[13px] text-gray-700">
                {sendOption === 'now' ? (
                  <>This will <span className="font-semibold text-gray-900">start sending immediately</span> and can't be undone.</>
                ) : (
                  <>This will send on{' '}
                    <span className="font-semibold text-gray-900">
                      {scheduledDate && scheduledTime
                        ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : '—'}
                    </span>{' '}({timezone}).
                  </>
                )}
              </p>
            </div>

            {/* One last chance to change the send time */}
            <div className="mb-4">
              <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Send time</label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => { setSendOption('now'); setScheduledDate(''); setScheduledTime(''); }}
                  className={`flex-1 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${sendOption === 'now' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Send now
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSendOption('schedule');
                    if (!scheduledDate) { const t = new Date(); t.setDate(t.getDate() + 1); setScheduledDate(t.toISOString().split('T')[0]); }
                    if (!scheduledTime) setScheduledTime('10:00');
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${sendOption === 'schedule' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Schedule
                </button>
              </div>
              {sendOption === 'schedule' && (
                <>
                  <div className="flex gap-2 min-w-0">
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="flex-1 basis-0 min-w-0 max-w-full box-border px-3 py-2 border border-gray-200 rounded-lg text-[13px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="flex-1 basis-0 min-w-0 max-w-full box-border px-3 py-2 border border-gray-200 rounded-lg text-[13px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  {scheduledInPast && (
                    <p className="text-[12px] text-red-600 mt-1.5">That time is in the past — pick a future time.</p>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setShowFinalConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || (sendOption === 'schedule' && (!scheduledDate || !scheduledTime || scheduledInPast))}
                onClick={async () => { await handleSubmit(); setShowFinalConfirm(false); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> {sendOption === 'now' ? 'Sending…' : 'Scheduling…'}</>
                ) : sendOption === 'now' ? (
                  <><Send className="h-4 w-4" /> Send now</>
                ) : (
                  <><Clock className="h-4 w-4" /> Confirm schedule</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[10001] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Send Test Email</h3>
              <button
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmail('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
    </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email address to send test to"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmail('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSendTestEmail}
                  disabled={!testEmail.trim() || sendingTestEmail}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sendingTestEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Preview'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customers Modal - Right Side Mounted (iOS Style) */}
      {(isAddCustomersModalOpen || isAddCustomersAnimating) && (
        <div 
          className={`fixed inset-0 transition-opacity duration-300 ${
            isAddCustomersModalOpen ? 'bg-black/50' : 'bg-transparent'
          }`}
          style={{
            zIndex: 100060,
            backdropFilter: isAddCustomersModalOpen ? 'blur(8px)' : 'none',
            WebkitBackdropFilter: isAddCustomersModalOpen ? 'blur(8px)' : 'none',
          }}
          onClick={handleAddCustomersModalClose}
        >
          <div 
            className={`bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col overflow-hidden ${
              isAddCustomersModalOpen ? 'translate-x-0' : 'translate-x-full'
            } ${
              typeof window !== 'undefined' && window.innerWidth < 768
                ? 'fixed inset-0 w-full'
                : 'fixed inset-y-0 right-0 w-full md:w-[800px] lg:w-[900px]'
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onTransitionEnd={handleAddCustomersTransitionEnd}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-xl border-b border-gray-200/60 px-4 sm:px-5 py-3.5 flex items-center justify-between flex-shrink-0"
              style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={handleAddCustomersModalClose}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4 text-gray-600" />
                </button>
                <h2 className="text-[17px] font-semibold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                  Target Audience
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {channelType === 'sms' ? (
                  <span className="px-2.5 py-1 bg-green-100 text-green-700 text-[11px] font-semibold rounded-full">SMS</span>
                ) : (
                  <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-[11px] font-semibold rounded-full">Email</span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50/50" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-0">
                {/* Info Section */}
                <div className="bg-white border-b border-gray-100 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                      {channelType === 'sms' ? (
                        <Phone className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Mail className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[14px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                        {channelType === 'sms' ? 'SMS audience' : 'Email audience'}
                      </h3>
                      <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
                        Pick one or more segments. Extra filters below apply to whoever is in those segments.
                      </p>
                    </div>
                  </div>
                </div>

                  {/* Segments */}
                  <div className="bg-white border-b border-gray-100 px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[12px] font-medium text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                        Segments
                      </div>
                      <div className="text-[13px] font-semibold text-blue-600 tabular-nums">
                        {loadingRecipientCount ? '…' : displayTotalRecipients.toLocaleString()}
                      </div>
                    </div>

                    {loadingAudienceSegments ? (
                      <div className="flex items-center justify-center gap-2 text-gray-400 py-4">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span className="text-[13px]">Loading segments...</span>
                      </div>
                    ) : pickerSegments.length === 0 ? (
                      <div className="text-[13px] text-gray-400 py-2">
                        {audienceSegmentError || 'No segments yet'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {pickerSegments.map((segment) => {
                          const checked = selectedSegmentIds.includes(segment.id);
                          return (
                            <label
                              key={segment.id}
                              className={`flex items-center justify-between px-3.5 py-3 rounded-xl border transition-all cursor-pointer ${
                                checked ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                  checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                                }`}>
                                  {checked && (
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAudienceSegment(segment)}
                                  className="sr-only"
                                />
                                <div className="min-w-0">
                                  <div className="text-[13px] font-semibold text-gray-900 truncate">{segment.name}</div>
                                  <div className="text-[11px] text-gray-500">
                                    {segment.system_key === 'all_contacts'
                                      ? 'Everyone'
                                      : segment.is_lead_list
                                        ? 'Lead list'
                                        : 'Saved segment'}
                                  </div>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Selection Mode Toggle */}
                  <div className="bg-white border-b border-gray-100 px-5 py-4">
                    <label className="block text-[12px] font-medium text-gray-500 mb-3" style={{ letterSpacing: '-0.01em' }}>
                      Selection Method
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setManualSelectionMode(false);
                          setSelectedCustomerIds([]);
                        }}
                        className={`flex-1 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 touch-manipulation ${
                          !manualSelectionMode
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        style={{ minHeight: '40px' }}
                      >
                        Filter
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setManualSelectionMode(true);
                          setConditions([]);
                          resetAddConditionDraft();
                        }}
                        className={`flex-1 px-3 py-2 rounded-[10px] text-[13px] font-medium transition-all duration-150 touch-manipulation ${
                          manualSelectionMode
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        style={{ minHeight: '40px' }}
                      >
                        Manual Selection
                      </button>
                    </div>
                  </div>

                  {/* Manual Customer Selection Section */}
                  {manualSelectionMode && (
                    <div className="bg-white border-b border-gray-100 px-5 py-4">
                      <div className="mb-3">
                        <label className="block text-[12px] font-medium text-gray-500 mb-2" style={{ letterSpacing: '-0.01em' }}>
                          Search and Select Customers
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                          <input
                            type="text"
                            value={customerSearchQuery}
                            onChange={(e) => setCustomerSearchQuery(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full pl-9 pr-4 py-2.5 bg-gray-100 border-0 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:bg-white text-[14px] text-gray-900 placeholder:text-gray-400 transition-all"
                          />
                        </div>
                      </div>

                      {loadingCustomers ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                          <span className="ml-2 text-[13px] text-gray-400">Loading customers...</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[12px] text-gray-500">
                              {selectedCustomerIds.length > 0 && (
                                <span className="font-semibold text-gray-900">
                                  {selectedCustomerIds.length} selected
                                </span>
                              )}
                            </div>
                            {filteredCustomers.length > 0 && (
                              <button
                                type="button"
                                onClick={handleSelectAllFiltered}
                                className="text-[12px] text-blue-600 hover:text-blue-700 font-semibold"
                              >
                                {filteredCustomers.every(c => selectedCustomerIds.includes(c.id))
                                  ? 'Deselect All'
                                  : 'Select All'}
                              </button>
                            )}
                          </div>

                          <div className="max-h-80 overflow-y-auto rounded-xl bg-gray-50 border border-gray-100">
                            {filteredCustomers.length === 0 ? (
                              <div className="p-8 text-center text-[13px] text-gray-400">
                                {customerSearchQuery.trim()
                                  ? 'No customers found matching your search'
                                  : 'No customers available'}
                              </div>
                            ) : (
                              <div className="divide-y divide-gray-100">
                                {filteredCustomers.map((customer) => {
                                  const isSelected = selectedCustomerIds.includes(customer.id);
                                  
                                  return (
                                    <label
                                      key={customer.id}
                                      className={`flex items-center px-3.5 py-3 cursor-pointer transition-colors ${
                                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-100/60'
                                      }`}
                                    >
                                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                        isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                                      }`}>
                                        {isSelected && (
                                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </div>
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleToggleCustomerSelection(customer.id)}
                                        className="sr-only"
                                      />
                                      <div className="ml-3 flex-1 min-w-0">
                                        <div className="text-[13px] font-medium text-gray-900 truncate">
                                          {customer.first_name} {customer.last_name}
                                        </div>
                                        <div className="text-[11px] text-gray-500 truncate">
                                          {channelType === 'sms' ? (customer.phone || 'No phone number') : (customer.email || 'No email')}
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Conditions Section */}
                  {!manualSelectionMode && (
                    <div className="bg-white border-b border-gray-100 px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-[12px] font-medium text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                          Filters (Optional)
                        </label>
                        {addConditionStep === null && (
                          <button
                            type="button"
                            onClick={() => setAddConditionStep('choose-type')}
                            className="px-3 py-1.5 text-[12px] font-medium bg-blue-600 text-white rounded-[8px] hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 flex items-center gap-1.5 touch-manipulation"
                          >
                            <Plus className="h-4 w-4" />
                            Add Condition
                          </button>
                        )}
                      </div>

                      {/* Existing Conditions */}
                      {conditions.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {conditions.map((condition, index) => (
                            <div
                              key={
                                condition.kind === 'tags'
                                  ? `tags-${index}-${condition.tagIds.join('-')}`
                                  : `booking-${index}-${condition.startDate}-${condition.endDate}`
                              }
                              className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-blue-100 text-blue-800">
                                  {audienceConditionKindLabel(condition.kind)}
                                </span>
                                {condition.kind === 'tags' ? (
                                  <>
                                    <span
                                      className={`px-2 py-0.5 text-[10px] font-semibold rounded-md ${
                                        condition.type === 'includes'
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-red-100 text-red-700'
                                      }`}
                                    >
                                      {condition.type === 'includes' ? 'Includes' : 'Excludes'}
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {condition.tagNames.map((tagName, tagIndex) => (
                                        <span
                                          key={tagIndex}
                                          className="px-2 py-0.5 text-[11px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md"
                                        >
                                          {tagName}
                                        </span>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-[11px] text-gray-700 leading-snug">
                                    {bookingExcludeConditionSummary(condition)}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveCondition(index)}
                                className="text-gray-300 hover:text-red-500 transition-colors ml-2 shrink-0"
                                aria-label="Remove condition"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Step 1: choose condition type */}
                      {addConditionStep === 'choose-type' && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-2">
                              Condition type
                            </label>
                            <select
                              value=""
                              onChange={(e) => {
                                const value = e.target.value as AudienceCondition['kind'] | '';
                                if (value === 'tags' || value === 'booking_exclude') {
                                  setAddConditionStep(value);
                                }
                              }}
                              className="w-full px-3 py-2.5 text-[13px] font-medium text-gray-900 bg-white border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                              aria-label="Select condition type"
                            >
                              <option value="" disabled>
                                Select a condition…
                              </option>
                              {AUDIENCE_CONDITION_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={resetAddConditionDraft}
                            className="w-full px-4 py-2 text-[13px] font-medium bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition-all duration-150"
                            style={{ minHeight: '38px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {/* Step 2: Booking exclude condition */}
                      {addConditionStep === 'booking_exclude' && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[13px] font-semibold text-gray-900">
                              Has booking in period
                            </span>
                            <button
                              type="button"
                              onClick={() => setAddConditionStep('choose-type')}
                              className="text-[12px] font-medium text-blue-600 hover:text-blue-700"
                            >
                              Change type
                            </button>
                          </div>
                          <p className="text-[12px] text-gray-500 leading-relaxed">
                            Remove customers who already have a booking during the selected time frame.
                          </p>
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-2">
                              Time frame
                            </label>
                            <select
                              value={bookingExcludePreset}
                              onChange={(e) =>
                                setBookingExcludePreset(e.target.value as BookingExcludePreset)
                              }
                              className="w-full px-3 py-2.5 text-[13px] font-medium text-gray-900 bg-white border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                            >
                              {BOOKING_EXCLUDE_PRESET_OPTIONS.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {bookingExcludePreset === 'custom' ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
                              <div className="min-w-0">
                                <label className="block text-[12px] font-medium text-gray-500 mb-1.5">
                                  Start date
                                </label>
                                <input
                                  type="date"
                                  value={bookingExcludeCustomStart}
                                  onChange={(e) => setBookingExcludeCustomStart(e.target.value)}
                                  className="w-full min-w-0 max-w-full box-border px-3 py-2 text-[13px] border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                />
                              </div>
                              <div className="min-w-0">
                                <label className="block text-[12px] font-medium text-gray-500 mb-1.5">
                                  End date
                                </label>
                                <input
                                  type="date"
                                  value={bookingExcludeCustomEnd}
                                  onChange={(e) => setBookingExcludeCustomEnd(e.target.value)}
                                  className="w-full min-w-0 max-w-full box-border px-3 py-2 text-[13px] border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                />
                              </div>
                            </div>
                          ) : (
                            <p className="text-[12px] text-gray-600 bg-white border border-gray-100 rounded-lg px-3 py-2">
                              {formatBookingExcludePeriodLabel(
                                resolveBookingExcludePeriod(bookingExcludePreset)
                              )}
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleAddBookingExcludeCondition}
                              className="flex-1 px-4 py-2 text-[13px] font-medium bg-blue-600 text-white rounded-[10px] hover:bg-blue-700 active:bg-blue-800 transition-all duration-150"
                              style={{ minHeight: '38px' }}
                            >
                              Add condition
                            </button>
                            <button
                              type="button"
                              onClick={resetAddConditionDraft}
                              className="px-4 py-2 text-[13px] font-medium bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition-all duration-150"
                              style={{ minHeight: '38px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 2: Tags condition builder */}
                      {addConditionStep === 'tags' && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[13px] font-semibold text-gray-900">Tags</span>
                            <button
                              type="button"
                              onClick={() => setAddConditionStep('choose-type')}
                              className="text-[12px] font-medium text-blue-600 hover:text-blue-700"
                            >
                              Change type
                            </button>
                          </div>
                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-2">Match</label>
                            <div className="flex gap-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="newConditionType"
                                  value="includes"
                                  checked={newConditionType === 'includes'}
                                  onChange={() => setNewConditionType('includes')}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-[13px] text-gray-700">Includes tag</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="newConditionType"
                                  value="excludes"
                                  checked={newConditionType === 'excludes'}
                                  onChange={() => setNewConditionType('excludes')}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-[13px] text-gray-700">Excludes tag</span>
                              </label>
                            </div>
                          </div>

                          <div>
                            <label className="block text-[12px] font-medium text-gray-500 mb-2">Select Tags</label>
                            {loadingCustomerTags ? (
                              <div className="flex items-center gap-2 text-gray-400">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                <span className="text-[13px]">Loading tags...</span>
                              </div>
                            ) : customerTags.length === 0 ? (
                              <div className="text-[13px] text-gray-400 py-2">No tags available</div>
                            ) : (
                              <div className="max-h-44 overflow-y-auto bg-white rounded-xl border border-gray-100 p-2 space-y-0.5">
                                {customerTags.map((tag) => (
                                  <label
                                    key={tag.id}
                                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                                      selectedTagsForCondition.includes(tag.id) ? 'bg-blue-50' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedTagsForCondition.includes(tag.id)}
                                      onChange={() => handleToggleTagSelection(tag.id)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                                    />
                                    <span className="text-[13px] text-gray-700">{tag.name}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {selectedTagsForCondition.length > 0 && (
                              <div className="mt-1.5 text-[11px] text-gray-400">
                                {selectedTagsForCondition.length} tag
                                {selectedTagsForCondition.length !== 1 ? 's' : ''} selected
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleAddCondition}
                              disabled={selectedTagsForCondition.length === 0}
                              className="flex-1 px-4 py-2 text-[13px] font-medium bg-blue-600 text-white rounded-[10px] hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all duration-150"
                              style={{ minHeight: '38px' }}
                            >
                              Add condition
                            </button>
                            <button
                              type="button"
                              onClick={resetAddConditionDraft}
                              className="px-4 py-2 text-[13px] font-medium bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition-all duration-150"
                              style={{ minHeight: '38px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {conditions.length === 0 && addConditionStep === null && (
                        <div className="text-[12px] text-gray-400 text-center py-4">
                          No conditions added. All customers will receive this campaign. Tap &quot;Add Condition&quot; to add filters.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recipient list preview */}
                  <div className="bg-white border-b border-gray-100">
                    <div className="px-5 py-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                            Total recipients
                          </p>
                          <p
                            className="text-[22px] font-bold text-gray-900 tabular-nums leading-tight"
                            style={{ letterSpacing: '-0.02em' }}
                          >
                            {loadingRecipientCount ? (
                              <span className="text-gray-400 text-base font-medium">Calculating…</span>
                            ) : (
                              displayTotalRecipients.toLocaleString()
                            )}
                          </p>
                        </div>
                        <select
                          value={recipientListView}
                          onChange={(e) =>
                            setRecipientListView(e.target.value as 'selected' | 'removed')
                          }
                          className="shrink-0 max-w-[min(100%,200px)] px-3 py-2 text-[13px] font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                          aria-label="Recipient list filter"
                        >
                          <option value="selected">Selected Recipients</option>
                          <option value="removed">Removed Recipients</option>
                        </select>
                      </div>

                      {loadingEmailLists ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
                          <span className="ml-2 text-[13px] text-gray-400">Loading...</span>
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-y-auto rounded-xl bg-gray-50 border border-gray-100">
                          {activeRecipientList.length === 0 ? (
                            <div className="p-8 text-center text-[13px] text-gray-400">
                              {recipientListView === 'selected'
                                ? 'No recipients selected'
                                : 'No removed recipients'}
                            </div>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {activeRecipientList.map((customer: any, index: number) => (
                                <div
                                  key={customer.id || index}
                                  className="px-3.5 py-2.5 hover:bg-gray-100/60 transition-colors"
                                >
                                  <div className="flex items-center gap-2.5">
                                    {recipientListView === 'selected' ? (
                                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="text-[13px] font-medium text-gray-900 truncate">
                                        {customer.first_name} {customer.last_name}
                                      </div>
                                      <div className="text-[11px] text-gray-500 truncate">
                                        {channelType === 'sms'
                                          ? customer.phone || customer.email
                                          : customer.email || customer.phone}
                                      </div>
                                      {recipientListView === 'removed' &&
                                        customer.exclusionReasons &&
                                        customer.exclusionReasons.length > 0 && (
                                          <div className="mt-1 flex flex-wrap gap-1">
                                            {customer.exclusionReasons.map((reason: string, idx: number) => (
                                              <span
                                                key={idx}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-red-100 text-red-700"
                                              >
                                                {reason}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-white/95 backdrop-blur-xl border-t border-gray-200/60 px-5 py-3.5 flex-shrink-0"
                style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
              >
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleAddCustomersModalCancel}
                    className="flex-1 px-4 py-2.5 text-[14px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-[10px] transition-all duration-150 touch-manipulation"
                    style={{ minHeight: '44px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyCustomerSelection}
                    className="flex-1 px-4 py-2.5 text-[14px] font-semibold bg-blue-600 text-white rounded-[10px] hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 touch-manipulation shadow-sm"
                    style={{ minHeight: '44px', letterSpacing: '-0.01em' }}
                  >
                    Apply Selection
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Snippets Modal */}
      <SnippetsModal
        isOpen={isSnippetsModalOpen}
        onClose={() => setIsSnippetsModalOpen(false)}
        onSelectSnippet={handleSnippetSelect}
      />

      {aiWriterOpen && (
        <AICampaignWriterModal
          channel={channelType}
          businessId={businessId || ''}
          businessName={(selectedBusiness as any)?.name}
          logoUrl={(selectedBusiness as any)?.booking_settings?.logo_url}
          websiteUrlFallback={(selectedBusiness as any)?.nexus_website_url}
          businessSlug={(selectedBusiness as any)?.slug}
          onClose={() => setAiWriterOpen(false)}
          onUseOne={applyAiOption}
          onUseAll={applyAiSplitTest}
        />
      )}

      <ExtendedCampaignEditor
        isOpen={showExtendedEditor}
        onClose={() => setShowExtendedEditor(false)}
        onSave={(steps) => setExtendedSteps(steps)}
        initialSteps={extendedSteps}
        businessId={businessId}
        businessName={(selectedBusiness as any)?.name}
        logoUrl={(selectedBusiness as any)?.booking_settings?.logo_url}
        initialEmailSummary={{
          subject: formData.subject || '',
          variantCount: isSplitTest ? variants.length + 1 : 1,
        }}
      />

      {/* Template Picker — right-side mounted sheet (stacks over the campaign builder) */}
      {showTemplatePicker && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => setShowTemplatePicker(false)}
        >
          <div
            className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
              typeof window !== 'undefined' && window.innerWidth < 768
                ? 'fixed inset-0 w-full'
                : 'fixed inset-y-0 right-0 w-full md:w-[560px]'
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100050,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sheet Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900" style={{ letterSpacing: '-0.02em' }}>Select Template</h2>
                <p className="text-[12px] text-gray-500">Pick a saved design or create a new one</p>
              </div>
              <button
                onClick={() => setShowTemplatePicker(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Sheet Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5" style={{ WebkitOverflowScrolling: 'touch' }}>
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-16 text-gray-400">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {/* Create tile — double-stack: manual builder + AI create.
                      Both open on top of this sheet; campaign progress is kept. */}
                  <div className="flex flex-col gap-3 aspect-[4/5]">
                    <button
                      type="button"
                      onClick={() => {
                        setBuilderTemplate(null);
                        setShowTemplateBuilder(true);
                      }}
                      className="flex-1 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/40 transition-all p-3"
                    >
                      <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                        <Plus className="h-4 w-4" />
                      </div>
                      <span className="text-[12px] font-semibold text-center leading-tight">Create New Template</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAiTemplatePrompt(true)}
                      disabled={aiTemplateGenerating}
                      className="flex-1 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-violet-300 text-violet-600 hover:border-violet-400 hover:bg-violet-50/50 transition-all p-3 disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center">
                        {aiTemplateGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      </div>
                      <span className="text-[12px] font-semibold text-center leading-tight">Create with AI</span>
                    </button>
                  </div>

                  {emailTemplates.filter((template) => !template.one_off).map((template) => {
                    const isSelected = selectedTemplate === template.id;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          handleTemplateSelect(template.id);
                          setShowTemplatePicker(false);
                        }}
                        className={`text-left rounded-2xl border overflow-hidden transition-all flex flex-col aspect-[4/5] ${
                          isSelected
                            ? 'border-blue-500 ring-2 ring-blue-500/30 shadow-md'
                            : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex-1 overflow-hidden bg-white pointer-events-none">
                          <TemplateMiniPreview
                            template={template}
                            businessName={(selectedBusiness as any)?.name}
                            logoUrl={(selectedBusiness as any)?.booking_settings?.logo_url}
                          />
                        </div>
                        <div className={`border-t px-3 py-2 flex-shrink-0 ${isSelected ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
                          <div className="text-[12px] font-semibold text-gray-900 truncate">{template.name}</div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {template.subject || template.content?.subject || 'No subject'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {!loadingTemplates && emailTemplates.filter((template) => !template.one_off).length === 0 && (
                <p className="text-center text-[13px] text-gray-500 mt-6">
                  No saved templates yet — create your first one above.
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* In-flow template builder — mounts above the picker; campaign state is untouched */}
      <EmailTemplateBuilder
        isOpen={showTemplateBuilder}
        onClose={() => {
          setShowTemplateBuilder(false);
          setBuilderTemplate(null);
        }}
        onSave={handleSaveNewTemplate}
        initialTemplate={builderTemplate}
      />

      {/* AI template prompt — stacks above the picker sheet */}
      {showAiTemplatePrompt && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          style={{ zIndex: 100060 }}
          onClick={() => { if (!aiTemplateGenerating) setShowAiTemplatePrompt(false); }}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-lg w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sm:hidden w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3" />
            <div className="p-5 sm:p-6">
              <div className="flex items-center space-x-3 mb-5">
                <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Create with AI</h3>
                  <p className="text-xs sm:text-sm text-gray-500">Describe the email template you want to create</p>
                </div>
                <button
                  onClick={() => setShowAiTemplatePrompt(false)}
                  className="p-2 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <textarea
                value={aiTemplatePrompt}
                onChange={(e) => setAiTemplatePrompt(e.target.value)}
                placeholder="e.g. A promotional email for a fall detailing special with a dark theme..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none touch-manipulation"
                rows={4}
                style={{ fontSize: '16px' }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && aiTemplatePrompt.trim()) {
                    e.preventDefault();
                    handleAiCreateTemplate();
                  }
                }}
              />

              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  'Welcome email for new customers',
                  'Promotional offer / sale',
                  'Booking confirmation',
                  'Feedback request',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setAiTemplatePrompt(suggestion)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-violet-50 hover:text-violet-700 text-gray-600 rounded-lg text-xs font-medium transition-colors touch-manipulation"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => setShowAiTemplatePrompt(false)}
                  className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 font-medium text-sm transition-colors touch-manipulation"
                  style={{ minHeight: '48px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAiCreateTemplate}
                  disabled={!aiTemplatePrompt.trim() || aiTemplateGenerating}
                  className={`flex-1 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center space-x-2 touch-manipulation ${
                    aiTemplatePrompt.trim() && !aiTemplateGenerating
                      ? 'bg-violet-600 text-white hover:bg-violet-700 active:bg-violet-800 shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  {aiTemplateGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <span>Generate Template</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <TokenPurchaseModal
        isOpen={showTokenPurchase}
        onClose={() => setShowTokenPurchase(false)}
        businessId={businessId || ''}
        onSuccess={() => {
          setShowTokenPurchase(false);
          toast.success('Tokens added! You can now use Create with AI.');
        }}
      />
    </div>
  );
}

export default function Campaigns({
  onOpenMarketingPage,
  marketingNavPages = [],
}: {
  onOpenMarketingPage?: (pageId: MarketingPageId) => void;
  marketingNavPages?: MarketingNavPage[];
} = {}) {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const supabase = useSupabase();
  const businessId = useBusinessId();
  
  // State
  type MarketingTab = 'overview' | 'campaigns' | 'lists' | 'discounts' | 'templates' | 'referral' | 'rewards' | 'brand';
  // Media/platform sections opened from the bottom nav (Library / Platforms / More).
  type MarketingView =
    | 'media'
    | 'automate'
    | 'history'
    | 'google-business'
    | 'facebook'
    | 'instagram'
    | 'linkedin';

  const isMarketingTab = (tab: string | null): tab is MarketingTab =>
    tab === 'overview' ||
    tab === 'campaigns' ||
    tab === 'lists' ||
    tab === 'discounts' ||
    tab === 'templates' ||
    tab === 'referral' ||
    tab === 'rewards' ||
    tab === 'brand';

  const isMarketingView = (view: string | null): view is MarketingView =>
    view === 'media' ||
    view === 'automate' ||
    view === 'history' ||
    view === 'google-business' ||
    view === 'facebook' ||
    view === 'instagram' ||
    view === 'linkedin';

  // State
  const [activeTab, setActiveTab] = useState<MarketingTab>(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get('tab');
      if (isMarketingTab(tab)) return tab;
    } catch {
      /* ignore */
    }
    return 'overview';
  });
  const [activeView, setActiveView] = useState<MarketingView | null>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const view = params.get('view');
      if (isMarketingView(view)) return view;
      // Legacy deep link: gbpTab without view implies the GBP workspace.
      if (params.get('gbpTab')) return 'google-business';
    } catch {
      /* ignore */
    }
    return null;
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const view = params.get('view');
    if (isMarketingView(view)) {
      setActiveView(view);
      setActiveTab('overview');
      return;
    }
    if (params.get('gbpTab')) {
      setActiveView('google-business');
      setActiveTab('overview');
      return;
    }
    setActiveView(null);
    if (!tab) {
      setActiveTab('overview');
      return;
    }
    if (isMarketingTab(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const selectTab = useCallback(
    (tab: MarketingTab) => {
      setActiveTab(tab);
      setActiveView(null);
      const params = new URLSearchParams(location.search);
      if (tab === 'overview') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      params.delete('view');
      params.delete('gbpTab');
      params.delete('page');
      const search = params.toString();
      navigate(search ? `/marketing?${search}` : '/marketing', { replace: true });
    },
    [location.search, navigate]
  );

  const selectView = useCallback(
    (view: MarketingView) => {
      setActiveView(view);
      setActiveTab('overview');
      const params = new URLSearchParams(location.search);
      params.set('view', view);
      if (view !== 'google-business') params.delete('gbpTab');
      params.delete('tab');
      params.delete('page');
      const search = params.toString();
      navigate(search ? `/marketing?${search}` : '/marketing', { replace: true });
    },
    [location.search, navigate]
  );
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [settings, setSettings] = useState<EmailMarketingSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<EmailCampaign | null>(null);
  const [initialCampaignDate, setInitialCampaignDate] = useState<string | null>(null);
  // Multi-media campaign wizard (channels + shared schedule chosen in the builder)
  const [multiMediaSetup, setMultiMediaSetup] = useState<{
    channels: MultiMediaChannel[];
    schedule: MultiMediaSchedule;
  } | null>(null);
  const [detailsCampaign, setDetailsCampaign] = useState<EmailCampaign | null>(null);
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  // Bottom-nav Platforms modal + header ⋯ overflow menu
  const [showPlatformsModal, setShowPlatformsModal] = useState(false);
  const [platformConnect, setPlatformConnect] = useState<MarketingConnectTarget | null>(null);
  const [connectAutoStart, setConnectAutoStart] = useState(false);
  // Bottom-nav [+] menu targets
  const [plusGbpOpen, setPlusGbpOpen] = useState(false);
  const [plusSocialPlatform, setPlusSocialPlatform] = useState<
    'facebook' | 'instagram' | 'linkedin' | null
  >(null);
  const [showUploadSheet, setShowUploadSheet] = useState(false);
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showOverflowMenu) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [showOverflowMenu]);
  const [newEmail, setNewEmail] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  
  // Metrics state
  const [avgOpenRate, setAvgOpenRate] = useState<number | null>(null);
  const [avgClickRate, setAvgClickRate] = useState<number | null>(null);
  const [openRateChange, setOpenRateChange] = useState<number | null>(null);
  const [clickRateChange, setClickRateChange] = useState<number | null>(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [emailsSentThisMonth, setEmailsSentThisMonth] = useState<number>(0);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  
  // Billing confirmation modal state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [campaignToLaunch, setCampaignToLaunch] = useState<EmailCampaign | null>(null);
  const [billingAmount, setBillingAmount] = useState<number>(0);
  const [processingBilling, setProcessingBilling] = useState(false);
  const [isScheduledLaunch, setIsScheduledLaunch] = useState(false);
  const [retryBillingCampaign, setRetryBillingCampaign] =
    useState<CampaignBillingRetryTarget | null>(null);
  
  // Twilio settings state
  const [twilioSettings, setTwilioSettings] = useState<{
    twilio_enabled: boolean;
    VITE_TWILIO_PHONE_NUMBER: string | null;
    twilio_status: string | null;
  } | null>(null);
  const [loadingTwilioSettings, setLoadingTwilioSettings] = useState(false);

  // Marketing calendar data (shared Overview + Control Centre calendar)
  const { posts: gbpCalendarPosts, refresh: refreshGbpCalendarPosts } =
    useUpcomingGbpPostsSnippet({ limit: 100, includePublished: true });
  const { posts: socialCalendarPosts, refresh: refreshSocialCalendarPosts } =
    useUpcomingSocialPostsSnippet({ limit: 100, includePublished: true });

  const [socialChannels, setSocialChannels] = useState({
    gbp: false,
    facebook: false,
    instagram: false,
    linkedin: false,
  });
  // Connected profile identity (name/@handle) for the Platforms sheet.
  const [channelProfiles, setChannelProfiles] = useState<{
    gbp: string | null;
    facebook: { name: string | null; username: string | null } | null;
    instagram: { name: string | null; username: string | null } | null;
    linkedin: { name: string | null; username: string | null } | null;
  }>({ gbp: null, facebook: null, instagram: null, linkedin: null });
  /** Every enabled Google Business location (one Platforms row each). */
  const [gbpLocations, setGbpLocations] = useState<Array<{ id: string; name: string | null }>>([]);

  const refreshSocialChannelStatus = useCallback(async () => {
    if (!businessId || !supabase) {
      setSocialChannels({ gbp: false, facebook: false, instagram: false, linkedin: false });
      setChannelProfiles({ gbp: null, facebook: null, instagram: null, linkedin: null });
      return;
    }
    try {
      const [gbpRes, socialRes] = await Promise.all([
        GBP_ENABLED
          ? supabase
              .from('google_business_connections')
              .select('id, location_name, provider')
              .eq('business_id', businessId)
              .in('provider', ['ayrshare', 'google'])
              .eq('is_active', true)
              .order('created_at', { ascending: true })
          : Promise.resolve({ data: [] as { id: string; location_name: string | null }[] }),
        SOCIAL_ENABLED
          ? supabase
              .from('social_media_connections')
              .select('platform, display_name, username')
              .eq('business_id', businessId)
              .eq('provider', 'ayrshare')
              .eq('is_active', true)
          : Promise.resolve({
              data: [] as { platform: string; display_name: string | null; username: string | null }[],
            }),
      ]);
      const gbpRows = gbpRes.data || [];
      const socialRows = socialRes.data || [];
      const fb = socialRows.find((r) => r.platform === 'facebook');
      const ig = socialRows.find((r) => r.platform === 'instagram');
      const li = socialRows.find((r) => r.platform === 'linkedin');
      setSocialChannels({
        gbp: gbpRows.length > 0,
        facebook: !!fb,
        instagram: !!ig,
        linkedin: !!li,
      });
      setGbpLocations(
        [...gbpRows]
          .sort((a, b) => (a.provider === b.provider ? 0 : a.provider === 'google' ? -1 : 1))
          .map((r) => ({ id: r.id, name: r.location_name || null })),
      );
      setChannelProfiles({
        gbp: gbpRows[0]?.location_name || null,
        facebook: fb ? { name: fb.display_name || null, username: fb.username || null } : null,
        instagram: ig ? { name: ig.display_name || null, username: ig.username || null } : null,
        linkedin: li ? { name: li.display_name || null, username: li.username || null } : null,
      });
    } catch {
      setSocialChannels({ gbp: false, facebook: false, instagram: false, linkedin: false });
      setChannelProfiles({ gbp: null, facebook: null, instagram: null, linkedin: null });
      setGbpLocations([]);
    }
  }, [businessId, supabase]);

  // Load data
  useEffect(() => {
    if (businessId) {
      loadEmailSettings();
      loadCampaigns();
      loadMetrics();
      loadEmailsSentThisMonth();
      loadTwilioSettings();
      void refreshSocialChannelStatus();
    }
  }, [businessId, refreshSocialChannelStatus]);

  // OAuth that started on this page comes back here. Reopen the same connect
  // UI so location picking / number setup can finish. Do not auto-start again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let next: MarketingConnectTarget | null = null;

    const gbpReturn =
      params.get('gbp_oauth') === '1' ||
      params.get('gbp_returning') === '1' ||
      (params.get('success') || '').startsWith('google_business_');
    let openGbpSettings = false;
    try {
      openGbpSettings = sessionStorage.getItem('gbp_open_settings') === '1';
    } catch {
      /* ignore */
    }
    if (gbpReturn || openGbpSettings) {
      next = 'gbp';
      try {
        sessionStorage.removeItem('gbp_open_settings');
      } catch {
        /* ignore */
      }
    }

    const socialPlatform = params.get('platform');
    if (
      params.get('social_oauth') === '1' &&
      (socialPlatform === 'facebook' ||
        socialPlatform === 'instagram' ||
        socialPlatform === 'linkedin')
    ) {
      next = socialPlatform;
    }

    const twilioResult = params.get('twilio_connect');
    if (twilioResult) {
      next = 'twilio';
      if (twilioResult === 'success') {
        toast.success('Twilio account connected — now choose a phone number.');
      } else if (twilioResult === 'denied') {
        toast.error('Twilio authorization was cancelled.');
      } else if (twilioResult === 'expired') {
        toast.error('Connection link expired. Please try connecting again.');
      } else {
        toast.error('Could not complete the Twilio connection.');
      }
      params.delete('twilio_connect');
      const nextUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    }

    if (next) {
      setConnectAutoStart(false);
      setPlatformConnect(next);
    }
  }, [location.search]);

  // Set up real-time subscription for live campaign metrics updates
  useEffect(() => {
    if (!businessId || !supabase) return;

    console.log('📡 Setting up real-time subscription for email campaigns...');

    const subscription = supabase
      .channel(`email-campaigns-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'email_campaigns',
          filter: `business_id=eq.${businessId}`
        },
        (payload) => {
          console.log('📊 Campaign metrics updated:', payload.new);
          
          // Update the campaign in local state with new metrics. Extended-campaign
          // follow-up steps live under their root's _steps, so route those there.
          const updated: any = payload.new;
          setCampaigns(prevCampaigns =>
            prevCampaigns.map(campaign => {
              if (campaign.id === updated.id) {
                return { ...campaign, ...updated };
              }
              if (updated.sequence_root_id && campaign.id === updated.sequence_root_id) {
                const steps = ((campaign as any)._steps as any[] | undefined) || [];
                const exists = steps.some(s => s.id === updated.id);
                const nextSteps = exists
                  ? steps.map(s => (s.id === updated.id ? { ...s, ...updated } : s))
                  : [...steps, updated].sort((a, b) => (a.step_number || 0) - (b.step_number || 0));
                return { ...campaign, _steps: nextSteps } as any;
              }
              return campaign;
            })
          );
        }
      )
      .subscribe((status) => {
        console.log('📡 Campaign subscription status:', status);
        setRealtimeConnected(status === 'SUBSCRIBED');
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('🔌 Cleaning up campaign subscription...');
      subscription.unsubscribe();
    };
  }, [businessId, supabase]);

  const loadEmailSettings = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      
      // Check if settings exist
      const { data: existingSettings } = await supabase
        .from('email_marketing_settings')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (existingSettings) {
        setSettings(existingSettings);
      } else {
        // Create default settings
        const { data: newSettings, error } = await supabase
          .from('email_marketing_settings')
          .insert({
            business_id: businessId,
            verified_senders: [],
            monthly_email_limit: 5000,
            emails_sent_this_month: 0,
            is_enabled: true
          })
          .select()
          .single();

        if (error) throw error;
        setSettings(newSettings);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    if (!businessId) return;
    
    try {
      // Fetch campaigns in batches to avoid 1000 row limit
      const BATCH_SIZE = 1000;
      let allCampaigns: any[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
      const { data: campaignData, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('business_id', businessId)
          .order('created_at', { ascending: false })
          .range(offset, offset + BATCH_SIZE - 1);

      if (error) throw error;
      
        if (campaignData && campaignData.length > 0) {
          allCampaigns = [...allCampaigns, ...campaignData];
          offset += BATCH_SIZE;
          hasMore = campaignData.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }

      // Hide extended-campaign follow-up steps from the list — the sequence
      // shows as one item (its root campaign) — but attach the steps to their
      // root so the row can show per-step performance.
      const childSteps = allCampaigns.filter((c: any) => c.sequence_root_id);
      allCampaigns = allCampaigns.filter((c: any) => !c.sequence_root_id);
      if (childSteps.length > 0) {
        const stepsByRoot = new Map<string, any[]>();
        for (const step of childSteps) {
          const list = stepsByRoot.get(step.sequence_root_id) || [];
          list.push(step);
          stepsByRoot.set(step.sequence_root_id, list);
        }
        for (const root of allCampaigns) {
          const steps = stepsByRoot.get(root.id);
          if (steps) {
            root._steps = steps.sort((a: any, b: any) => (a.step_number || 0) - (b.step_number || 0));
          }
        }
      }
      
      // Load variants for A/B test campaigns
      const abTestCampaignIds = allCampaigns
        .filter((c: any) => c.is_ab_test)
        .map((c: any) => c.id);

      if (abTestCampaignIds.length > 0) {
        const { data: allVariants } = await supabase
          .from('email_campaign_variants')
          .select('*')
          .in('campaign_id', abTestCampaignIds);

        if (allVariants && allVariants.length > 0) {
          const variantsByCampaign: Record<string, any[]> = {};
          for (const v of allVariants) {
            if (!variantsByCampaign[v.campaign_id]) variantsByCampaign[v.campaign_id] = [];
            variantsByCampaign[v.campaign_id].push(v);
          }
          allCampaigns = allCampaigns.map((c: any) => ({
            ...c,
            _variants: variantsByCampaign[c.id] || []
          }));
        }
      }

      setCampaigns(allCampaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaigns([]);
    }
  };

  const loadTwilioSettings = async () => {
    if (!businessId || !supabase) return;
    
    try {
      setLoadingTwilioSettings(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('twilio_enabled, VITE_TWILIO_PHONE_NUMBER, twilio_status')
        .eq('id', businessId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      
      if (data) {
        setTwilioSettings({
          twilio_enabled: data.twilio_enabled || false,
          VITE_TWILIO_PHONE_NUMBER: data.VITE_TWILIO_PHONE_NUMBER || null,
          twilio_status: data.twilio_status || null
        });
      } else {
        setTwilioSettings(null);
      }
    } catch (error) {
      console.error('Error loading Twilio settings:', error);
      setTwilioSettings(null);
    } finally {
      setLoadingTwilioSettings(false);
    }
  };

  const loadEmailsSentThisMonth = async () => {
    if (!businessId || !supabase) return;

    try {
      // Get the start of the current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfMonthISO = startOfMonth.toISOString();

      // First get all campaign IDs for this business - fetch in batches
      const CAMPAIGN_BATCH_SIZE = 1000;
      let allCampaigns: any[] = [];
      let campaignOffset = 0;
      let hasMoreCampaigns = true;

      while (hasMoreCampaigns) {
        const { data: campaigns, error: campaignsError } = await supabase
          .from('email_campaigns')
          .select('id')
          .eq('business_id', businessId)
          .range(campaignOffset, campaignOffset + CAMPAIGN_BATCH_SIZE - 1);

        if (campaignsError) throw campaignsError;

        if (campaigns && campaigns.length > 0) {
          allCampaigns = [...allCampaigns, ...campaigns];
          campaignOffset += CAMPAIGN_BATCH_SIZE;
          hasMoreCampaigns = campaigns.length === CAMPAIGN_BATCH_SIZE;
        } else {
          hasMoreCampaigns = false;
        }
      }

      const campaigns = allCampaigns;

      if (!campaigns || campaigns.length === 0) {
        setEmailsSentThisMonth(0);
        return;
      }

      const campaignIds = campaigns.map(c => c.id);

      // Count recipients that were actually delivered this month
      // Only count emails that have been delivered (not just queued or sent)
      // Include statuses: 'delivered', 'opened', 'clicked' OR have delivered_at set
      // Fetch in batches to avoid 1000 row limit
      const BATCH_SIZE = 1000;
      let totalCount = 0;
      let offset = 0;
      let hasMore = true;
      let countError: any = null;

      while (hasMore) {
        const { data, error } = await supabase
          .from('email_campaign_recipients')
          .select('id')
          .in('campaign_id', campaignIds)
          .in('status', ['delivered', 'opened', 'clicked'])
          .gte('delivered_at', startOfMonthISO)
          .range(offset, offset + BATCH_SIZE - 1);

        if (error) {
          countError = error;
          break;
        }
        
        if (data && data.length > 0) {
          totalCount += data.length;
          offset += BATCH_SIZE;
          hasMore = data.length === BATCH_SIZE;
        } else {
          hasMore = false;
        }
      }
      
      if (countError) throw countError;
      
      const count = totalCount;
      setEmailsSentThisMonth(count || 0);
    } catch (error) {
      console.error('Error loading emails sent this month:', error);
      setEmailsSentThisMonth(0);
    }
  };

  const loadMetrics = async () => {
    if (!businessId) return;
    
    try {
      setLoadingMetrics(true);
      
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
      
      // Get campaigns from last 30 days
      const { data: recentCampaigns, error: recentError } = await supabase
        .from('email_campaigns')
        .select('sent_count, opened_count, clicked_count')
        .eq('business_id', businessId)
        .gte('sent_at', thirtyDaysAgo.toISOString())
        .not('sent_at', 'is', null);

      if (recentError) throw recentError;

      // Get campaigns from previous 30 days (30-60 days ago)
      const { data: previousCampaigns, error: previousError } = await supabase
        .from('email_campaigns')
        .select('sent_count, opened_count, clicked_count')
        .eq('business_id', businessId)
        .gte('sent_at', sixtyDaysAgo.toISOString())
        .lt('sent_at', thirtyDaysAgo.toISOString())
        .not('sent_at', 'is', null);

      if (previousError) throw previousError;

      // Calculate metrics for last 30 days
      const recentTotalSent = (recentCampaigns || []).reduce((sum, c) => sum + (c.sent_count || 0), 0);
      const recentTotalOpened = (recentCampaigns || []).reduce((sum, c) => sum + (c.opened_count || 0), 0);
      const recentTotalClicked = (recentCampaigns || []).reduce((sum, c) => sum + (c.clicked_count || 0), 0);
      
      const recentOpenRate = recentTotalSent > 0 ? (recentTotalOpened / recentTotalSent) * 100 : 0;
      const recentClickRate = recentTotalSent > 0 ? (recentTotalClicked / recentTotalSent) * 100 : 0;

      // Calculate metrics for previous 30 days
      const previousTotalSent = (previousCampaigns || []).reduce((sum, c) => sum + (c.sent_count || 0), 0);
      const previousTotalOpened = (previousCampaigns || []).reduce((sum, c) => sum + (c.opened_count || 0), 0);
      const previousTotalClicked = (previousCampaigns || []).reduce((sum, c) => sum + (c.clicked_count || 0), 0);
      
      const previousOpenRate = previousTotalSent > 0 ? (previousTotalOpened / previousTotalSent) * 100 : 0;
      const previousClickRate = previousTotalSent > 0 ? (previousTotalClicked / previousTotalSent) * 100 : 0;

      // Calculate changes
      const openRateChangeValue = previousOpenRate > 0 
        ? ((recentOpenRate - previousOpenRate) / previousOpenRate) * 100 
        : (recentOpenRate > 0 ? 100 : 0);
      
      const clickRateChangeValue = previousClickRate > 0 
        ? ((recentClickRate - previousClickRate) / previousClickRate) * 100 
        : (recentClickRate > 0 ? 100 : 0);

      setAvgOpenRate(recentOpenRate);
      setAvgClickRate(recentClickRate);
      setOpenRateChange(openRateChangeValue);
      setClickRateChange(clickRateChangeValue);
    } catch (error) {
      console.error('Error loading metrics:', error);
      setAvgOpenRate(null);
      setAvgClickRate(null);
      setOpenRateChange(null);
      setClickRateChange(null);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim() || !businessId) return;
    
    // Check if there's already a verified email
    const hasVerifiedEmail = settings?.verified_senders && Array.isArray(settings.verified_senders) && 
      settings.verified_senders.some((s: any) => s.verification_status === 'verified');
    
    if (hasVerifiedEmail) {
      alert('You can only have one verified email address at a time. Please remove the current verified email before adding a new one.');
      return;
    }
    
    setVerifyingEmail(true);
    
    try {
      console.log('🔗 Starting SendGrid email verification for:', newEmail);
      
      // Call the new verify-sendgrid-email function
      const { data: verificationResult, error: verificationError } = await supabase.functions.invoke('verify-sendgrid-email', {
        body: {
          email: newEmail.trim(),
          businessId: businessId
        }
      });

      const parsed =
        verificationResult && typeof verificationResult === 'object'
          ? (verificationResult as { success?: boolean; error?: string; warning?: string; message?: string })
          : null;

      // Invoke may set `error` on non-2xx even when the function body explains success; prefer explicit success flag.
      if (!parsed?.success) {
        const msg =
          parsed?.error ||
          (verificationError as Error | null)?.message ||
          'Email verification request failed';
        throw new Error(msg);
      }

      console.log('✅ Verification email sent successfully');
      
      const warn = parsed.warning
        ? `\n\nNote: ${parsed.warning}\n${parsed.message || ''}`
        : '';
      // Show success message with instructions
      alert(`Verification email sent to ${newEmail}! Check your inbox and click the verification link to finish.${warn}`);
      
      setNewEmail('');
      setShowEmailSetup(false);
      
      // Reload settings to check for verification status
      await loadEmailSettings();
      
    } catch (error) {
      console.error('Error verifying email:', error);
      alert(`Failed to send verification email: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleCheckVerificationStatus = async (senderId: string, email: string) => {
    if (!businessId || !senderId) return;
    
    try {
      const { data: statusResult, error: statusError } = await supabase.functions.invoke('check-sendgrid-verification', {
        body: {
          businessId: businessId,
          senderId: senderId,
          email: email
        }
      });

      if (statusError) {
        throw new Error(statusError.message || 'Failed to check verification status');
      }

      if (statusResult?.success) {
        if (statusResult.verified) {
          alert(`Email ${email} is now verified and ready to use!`);
        } else {
          alert(`Email verification is still pending. Check the inbox for ${email} and click the verification link, then check status again.`);
        }
        await loadEmailSettings();
      } else {
        throw new Error(statusResult?.error || 'Failed to check verification status');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      alert(`Failed to check verification status: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    }
  };

  const handleRemoveEmail = async (emailToRemove: string, senderId: string) => {
    if (!businessId || !settings) return;
    
    if (!confirm(`Are you sure you want to remove ${emailToRemove}? You'll need to verify a new email address to send campaigns.`)) {
      return;
    }
    
    try {
      // Remove the email from verified_senders array
      const updatedSenders = (settings.verified_senders || []).filter(
        (sender: any) => sender.email !== emailToRemove
      );
      
      const { error } = await supabase
        .from('email_marketing_settings')
        .update({ verified_senders: updatedSenders })
        .eq('business_id', businessId);

      if (error) throw error;
      
      // Reload settings
      await loadEmailSettings();
    } catch (error) {
      console.error('Error removing email:', error);
      alert(`Failed to remove email: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    }
  };

  const handleCreateCampaign = (date?: string) => {
    setEditingCampaign(null);
    setInitialCampaignDate(typeof date === 'string' ? date : null);
    setShowCampaignBuilder(true);
  };

  const handleEditCampaign = (campaign: EmailCampaign) => {
    setEditingCampaign(campaign);
    setShowCampaignBuilder(true);
  };

  const handleSaveCampaign = async (campaignData: any) => {
    if (!businessId) return;

    try {
      const result = await saveCampaignRecord(businessId, campaignData, editingCampaign?.id);
      const saved = result.campaign as EmailCampaign;

      if (result.isUpdate) {
        setCampaigns((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
      } else {
        setCampaigns((prev) => [saved, ...prev]);
      }

      if (result.sendImmediately) {
        setShowCampaignBuilder(false);
        await handleLaunchCampaign({ ...saved, recipient_count: result.recipientCount });
        return;
      }

      if (result.scheduledAt) {
        setShowCampaignBuilder(false);
        await handleScheduleCampaign({ ...saved, recipient_count: result.recipientCount });
        return;
      }

      alert(
        `${result.isUpdate ? 'Campaign updated' : 'Campaign created'} successfully! (${result.recipientCount} recipients)`
      );
    } catch (error) {
      console.error('Error saving campaign:', error);
      throw error;
    }
  };

  const sendCampaignDirectly = async (campaign: EmailCampaign) => {
    if (!businessId) return;
    try {
      // Update campaign status to 'sending'
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ status: 'sending', send_immediately: true })
        .eq('id', campaign.id);
      if (updateError) throw updateError;

      await loadCampaigns();

      await queueCampaignSend(campaign.id, businessId, campaign.channel_type || 'email');
      await loadCampaigns();
      setCampaignToLaunch(null);
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert(`Failed to send campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
      await loadCampaigns();
    }
  };

  const handleLaunchCampaign = async (campaign: EmailCampaign) => {
    if (!businessId) return;

    // First, ensure recipients are built
    try {
      // Check if recipients exist, if not build them
      const { data: existingRecipients } = await supabase
        .from('email_campaign_recipients')
        .select('id')
        .eq('campaign_id', campaign.id)
        .limit(1);

      let recipientCount = campaign.recipient_count || 0;

      if (!existingRecipients || existingRecipients.length === 0) {
        // Build recipient list
        const recipients = await buildCampaignRecipientList(
          businessId,
          (campaign as any).recipient_type || 'all_customers',
          (campaign as any).recipient_filters,
          campaign.channel_type || 'email'
        );

        if (recipients.length === 0) {
          alert('No eligible recipients found for this campaign.');
          return;
        }

        // Load variant names for split test campaigns
        let variantNames: string[] | undefined;
        if (campaign.is_ab_test) {
          const { data: dbVariants } = await supabase
            .from('email_campaign_variants')
            .select('variant_name')
            .eq('campaign_id', campaign.id);
          if (dbVariants && dbVariants.length >= 2) {
            variantNames = dbVariants.map((v: any) => v.variant_name);
          }
        }

        // Create recipient records with variant assignment
        await createCampaignRecipients(campaign.id, recipients, variantNames);
        recipientCount = recipients.length;

        // Update campaign recipient count
        await supabase
          .from('email_campaigns')
          .update({ recipient_count: recipientCount })
          .eq('id', campaign.id);
      }

      // SMS campaigns are never charged — they send through the business's own Twilio.
      if ((campaign.channel_type || 'email') === 'sms') {
        setCampaignToLaunch({ ...campaign, recipient_count: recipientCount });
        await sendCampaignDirectly({ ...campaign, recipient_count: recipientCount });
        return;
      }

      // Email: confirm + charge at send (no free dev bypass).
      const amount = recipientCount * 0.01;
      setBillingAmount(amount);
      setCampaignToLaunch({ ...campaign, recipient_count: recipientCount });
      setIsScheduledLaunch(false);
      setShowBillingModal(true);
    } catch (error) {
      console.error('Error preparing campaign launch:', error);
      alert(`Failed to prepare campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleScheduleCampaign = async (campaign: EmailCampaign) => {
    if (!businessId) return;

    try {
      const { data: existingRecipients } = await supabase
        .from('email_campaign_recipients')
        .select('id')
        .eq('campaign_id', campaign.id)
        .limit(1);

      let recipientCount = campaign.recipient_count || 0;

      if (!existingRecipients || existingRecipients.length === 0) {
        const recipients = await buildCampaignRecipientList(
          businessId,
          (campaign as any).recipient_type || 'all_customers',
          (campaign as any).recipient_filters,
          campaign.channel_type || 'email'
        );

        if (recipients.length === 0) {
          alert('No eligible recipients found for this campaign.');
          return;
        }

        let variantNames: string[] | undefined;
        if (campaign.is_ab_test) {
          const { data: dbVariants } = await supabase
            .from('email_campaign_variants')
            .select('variant_name')
            .eq('campaign_id', campaign.id);
          if (dbVariants && dbVariants.length >= 2) {
            variantNames = dbVariants.map((v: any) => v.variant_name);
          }
        }

        await createCampaignRecipients(campaign.id, recipients, variantNames);
        recipientCount = recipients.length;

        await supabase
          .from('email_campaigns')
          .update({ recipient_count: recipientCount })
          .eq('id', campaign.id);
      }

      // SMS campaigns are never charged — schedule directly.
      if ((campaign.channel_type || 'email') === 'sms') {
        const scheduledAt = (campaign as any).scheduled_at;
        alert(`Campaign scheduled${scheduledAt ? ` for ${new Date(scheduledAt).toLocaleString()}` : ''}. (${recipientCount} recipients)`);
        await loadCampaigns();
        return;
      }

      // Email: authorize the amount now; it's captured automatically when the campaign sends.
      const amount = recipientCount * 0.01;
      setBillingAmount(amount);
      setCampaignToLaunch({ ...campaign, recipient_count: recipientCount });
      setIsScheduledLaunch(true);
      setShowBillingModal(true);
    } catch (error) {
      console.error('Error preparing scheduled campaign:', error);
      alert(`Failed to schedule campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleApproveAndSend = async () => {
    if (!campaignToLaunch || !businessId) return;

    setProcessingBilling(true);
    try {
      const recipientCount = campaignToLaunch.recipient_count || 0;
      const wasScheduled = isScheduledLaunch;

      if (wasScheduled) {
        // Scheduled email: AUTHORIZE now; the amount is captured automatically when it sends.
        const scheduledAt = (campaignToLaunch as any).scheduled_at || null;
        const { successUrl, cancelUrl } = buildEmailCampaignCheckoutUrls(campaignToLaunch.id);
        console.log(`🔒 Authorizing campaign: ${recipientCount} recipients = $${billingAmount.toFixed(2)}`);
        const { data: authResult, error: authError } = await supabase.functions.invoke('authorize-email-campaign', {
          body: { businessId, campaignId: campaignToLaunch.id, recipientCount, sendAt: scheduledAt, successUrl, cancelUrl }
        });
        if (redirectToCampaignCheckout(authResult)) {
          return;
        }
        if (authError || !authResult?.success) {
          const errorMessage =
            (await invokeErrorMessage(authError, authResult)) ||
            'Authorization failed. Update your payment method in Subscription settings and try again.';
          console.error('Authorization error:', authError, authResult);
          await supabase
            .from('email_campaigns')
            .update({ status: 'failed', billing_status: 'failed' })
            .eq('id', campaignToLaunch.id);
          alert(`Could not authorize payment: ${errorMessage}. Please check your payment method and try again.`);
          await loadCampaigns();
          setProcessingBilling(false);
          return;
        }
        setShowBillingModal(false);
        const launchingCampaign = campaignToLaunch;
        setCampaignToLaunch(null);
        setIsScheduledLaunch(false);
        const displayTime = scheduledAt ? new Date(scheduledAt).toLocaleString() : 'the scheduled time';
        const note = authResult.mode === 'charge_at_send'
          ? `It will be charged and sent at ${displayTime}.`
          : authResult.mode === 'already_paid'
            ? `It's already paid and will send at ${displayTime}.`
            : `Payment is authorized and will be charged when it sends at ${displayTime}.`;
        alert(`Campaign scheduled! ${note}`);
        await loadCampaigns();
        setProcessingBilling(false);
        return;
      }

      // Send now: charge immediately, then send.
      console.log(`💰 Charging for campaign: ${recipientCount} recipients = $${billingAmount.toFixed(2)}`);
      const { successUrl, cancelUrl } = buildEmailCampaignCheckoutUrls(campaignToLaunch.id);
      const { data: chargeResult, error: chargeError } = await supabase.functions.invoke('charge-email-campaign', {
        body: {
          businessId: businessId,
          campaignId: campaignToLaunch.id,
          recipientCount: recipientCount,
          successUrl,
          cancelUrl,
          intent: 'send_now',
        }
      });

      if (redirectToCampaignCheckout(chargeResult)) {
        return;
      }

      if (chargeError || !chargeResult?.success) {
        const errorMessage =
          (await invokeErrorMessage(chargeError, chargeResult)) ||
          'Payment failed. Update your payment method in Subscription settings and try again.';
        console.error('Billing error:', chargeError, chargeResult);
        await supabase
          .from('email_campaigns')
          .update({ billing_status: 'failed' })
          .eq('id', campaignToLaunch.id);
        alert(`Payment failed: ${errorMessage}. Please check your payment method and try again.`);
        await loadCampaigns();
        setProcessingBilling(false);
        return;
      }

      // Only proceed if payment status is 'succeeded'
      // The edge function now polls for 'processing' status until it succeeds or fails
      if (chargeResult.status !== 'succeeded') {
        const errorMessage = chargeResult?.error || `Payment status: ${chargeResult?.status || 'unknown'}`;
        console.error('Payment not succeeded:', chargeResult);
        await supabase
          .from('email_campaigns')
          .update({ billing_status: 'failed' })
          .eq('id', campaignToLaunch.id);
        alert(`Payment could not be confirmed: ${errorMessage}. Please try again.`);
        await loadCampaigns();
        setProcessingBilling(false);
        return;
      }

      const paymentIntentId = String(chargeResult.paymentIntentId || '').trim();
      if (!paymentIntentId) {
        await supabase
          .from('email_campaigns')
          .update({ billing_status: 'failed' })
          .eq('id', campaignToLaunch.id);
        alert('Payment succeeded but no payment id was returned. Please try again.');
        await loadCampaigns();
        setProcessingBilling(false);
        return;
      }

      console.log('✅ Payment successful:', paymentIntentId);

      // Close billing modal
      setShowBillingModal(false);
      const launchingCampaign = campaignToLaunch;
      setCampaignToLaunch(null);
      setIsScheduledLaunch(false);

      // Update campaign status to 'sending' (billing already marked captured by charge fn)
      const { error: updateError } = await supabase
        .from('email_campaigns')
        .update({ 
          status: 'sending',
          send_immediately: true
        })
        .eq('id', launchingCampaign.id);

      if (updateError) throw updateError;

      // Reload campaigns to show updated status
      await loadCampaigns();

      // Call edge function to send campaign (now queues batches via QStash)
      try {
        const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-email-campaign', {
          body: {
            campaignId: launchingCampaign.id,
            businessId: businessId,
            skipBilling: true
          }
        });

        if (sendError) {
          console.error('Error invoking send campaign function:', sendError);
          await supabase
            .from('email_campaigns')
            .update({ status: 'failed' })
            .eq('id', launchingCampaign.id);
          throw new Error(
            (await invokeErrorMessage(sendError, sendResult)) || 'Failed to queue campaign send',
          );
        }

        if (!sendResult?.success) {
          const errorMessage = sendResult?.error || 'Failed to queue campaign';
          console.error('Campaign queue failed:', sendResult);
          await supabase
            .from('email_campaigns')
            .update({ status: 'failed' })
            .eq('id', launchingCampaign.id);
          throw new Error(errorMessage);
        }

        console.log('✅ Campaign queued successfully:', sendResult);
        const channelType = launchingCampaign.channel_type || 'email';
        const messageType = channelType === 'sms' ? 'SMS messages' : 'Emails';
        alert(`Campaign launched successfully! ${sendResult.totalBatches || 1} batch(es) queued. ${messageType} are being sent in the background.`);
        await loadCampaigns();
      } catch (sendErr) {
        console.error('Error in send campaign step:', sendErr);
        await supabase
          .from('email_campaigns')
          .update({ status: 'failed' })
          .eq('id', launchingCampaign.id)
          .catch(console.error);
        throw sendErr;
      }
    } catch (error) {
      console.error('Error launching campaign:', error);
      alert(`Failed to launch campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Always clear loading state, even if there was an error
      setProcessingBilling(false);
      setShowBillingModal(false);
      setCampaignToLaunch(null);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      // Release any authorization hold / refund any captured-but-unsent payment
      // BEFORE deleting the row. If this fails we keep the campaign so the
      // payment stays traceable — never delete with money unresolved.
      const { data: billingResult, error: billingError } = await supabase.functions.invoke(
        'cancel-email-campaign',
        { body: { businessId, campaignId } }
      );
      if (billingError || !billingResult?.success) {
        const message =
          (billingResult as any)?.error ||
          billingError?.message ||
          'Could not release the campaign payment.';
        alert(`Campaign NOT deleted: ${message}`);
        return;
      }

      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', campaignId)
        .eq('business_id', businessId);

      if (error) throw error;

      // Remove from local state
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      if (billingResult.action === 'refunded') {
        const amount = ((billingResult.refundedCents || 0) / 100).toFixed(2);
        alert(`Campaign deleted. Your $${amount} payment has been refunded (it can take a few business days to appear on your card).`);
      } else if (billingResult.action === 'released') {
        alert('Campaign deleted. The pending card authorization has been released.');
      } else {
        alert('Campaign deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    }
  };

  const handleDuplicateCampaign = async (campaign: EmailCampaign) => {
    if (!businessId) return;
    
    try {
      const duplicatedData: any = {
        business_id: businessId,
        name: `${campaign.name} (Copy)`,
        subject: campaign.subject,
        from_email: campaign.from_email,
        from_name: campaign.from_name,
        html_content: campaign.html_content,
        channel_type: campaign.channel_type || 'email',
        sms_content: (campaign as any).sms_content || null,
        from_phone_number: (campaign as any).from_phone_number || null,
        status: 'draft',
        campaign_type: campaign.campaign_type,
        is_ab_test: campaign.is_ab_test,
        recipient_count: 0,
        sent_count: 0,
        delivered_count: 0,
        opened_count: 0,
        clicked_count: 0
      };

      const { data: newCampaign, error } = await supabase
        .from('email_campaigns')
        .insert(duplicatedData)
        .select()
        .single();

      if (error) throw error;

      // Copy variants if this was a split test
      if (campaign.is_ab_test && (campaign as any)._variants?.length > 0) {
        const variantRows = (campaign as any)._variants.map((v: any) => ({
          campaign_id: newCampaign.id,
          variant_name: v.variant_name,
          subject: v.subject,
          html_content: v.html_content,
          plain_text_content: v.plain_text_content,
          sms_content: v.sms_content,
          from_name: v.from_name,
          sent_count: 0,
          delivered_count: 0,
          opened_count: 0,
          clicked_count: 0,
          open_rate: 0,
          click_rate: 0
        }));

        await supabase
          .from('email_campaign_variants')
          .insert(variantRows);
        
        newCampaign._variants = variantRows;
      }

      setCampaigns(prev => [newCampaign, ...prev]);
      alert('Campaign duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      alert('Failed to duplicate campaign. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'sending': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle className="h-4 w-4" />;
      case 'sending': return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'scheduled': return <Clock className="h-4 w-4" />;
      case 'draft': return <FileText className="h-4 w-4" />;
      case 'paused': return <Pause className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (campaign.subject || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const calculateOpenRate = (opened: number, delivered: number) => {
    return delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : '0.0';
  };

  const calculateClickRate = (clicked: number, delivered: number) => {
    return delivered > 0 ? ((clicked / delivered) * 100).toFixed(1) : '0.0';
  };

  // Channel statuses (Platforms modal on the bottom nav).
  const senders = settings?.verified_senders;
  const verifiedSender = Array.isArray(senders)
    ? senders.find((s: { verification_status?: string }) => s.verification_status === 'verified')
    : undefined;
  const pendingSender = Array.isArray(senders)
    ? senders.find((s: { verification_status?: string }) => s.verification_status === 'pending')
    : undefined;

  let sendgridStatus: ChannelStatus = 'disconnected';
  let sendgridDetail: string | undefined;
  if (verifiedSender) {
    sendgridStatus = 'connected';
    sendgridDetail = (verifiedSender as { email?: string }).email;
  } else if (pendingSender) {
    sendgridStatus = 'pending';
    sendgridDetail = (pendingSender as { email?: string }).email;
  }

  const twilioConnected = Boolean(
    twilioSettings?.twilio_enabled && twilioSettings?.VITE_TWILIO_PHONE_NUMBER
  );

  const platformChannelItems = buildMarketingChannelItems({
    sendgridStatus,
    sendgridDetail,
    twilioStatus: twilioConnected ? 'connected' : 'disconnected',
    twilioDetail: twilioSettings?.VITE_TWILIO_PHONE_NUMBER || undefined,
    gbpConnected: socialChannels.gbp,
    gbpLocations,
    facebookConnected: socialChannels.facebook,
    instagramConnected: socialChannels.instagram,
    linkedinConnected: socialChannels.linkedin,
    gbpProfileName: channelProfiles.gbp,
    facebookProfile: channelProfiles.facebook,
    instagramProfile: channelProfiles.instagram,
    linkedinProfile: channelProfiles.linkedin,
    onSendgrid: () => navigate('/app-store?tab=integrations'),
    onTwilio: () => navigate('/app-store?tab=integrations'),
    onSocial: () => navigate('/app-store?tab=integrations'),
  });

  /** Disconnected Platforms row: connect here. Connected gear still opens Integrations. */
  const connectPlatformFromMarketing = (id: string) => {
    const base = id.split(':')[0];
    const channel = platformChannelItems.find((c) => c.id === id);
    if (channel?.status === 'connected') {
      setShowPlatformsModal(false);
      navigate('/app-store?tab=integrations');
      return;
    }
    setShowPlatformsModal(false);
    if (base === 'sendgrid') {
      setShowEmailSetup(true);
      return;
    }
    if (
      base === 'gbp' ||
      base === 'facebook' ||
      base === 'instagram' ||
      base === 'linkedin' ||
      base === 'twilio'
    ) {
      setConnectAutoStart(channel?.status === 'disconnected' && base !== 'twilio');
      setPlatformConnect(base);
      return;
    }
    navigate('/app-store?tab=integrations');
  };

  /** A Platforms-modal row was tapped: open the platform view or integrations. */
  const openPlatformChannel = (id: string) => {
    setShowPlatformsModal(false);
    if (id.startsWith('gbp') && socialChannels.gbp) {
      // "gbp:<connectionId>" — open the workspace on that location. The
      // workspace reads the stored pick on mount and listens if already open.
      const connectionId = id.split(':')[1];
      if (connectionId && businessId) {
        try {
          sessionStorage.setItem(`gbp_ws_connection_${businessId}`, connectionId);
        } catch { /* ignore */ }
        window.dispatchEvent(
          new CustomEvent('gbp:select-connection', { detail: { connectionId } }),
        );
      }
      selectView('google-business');
      return;
    }
    if (id === 'facebook' && socialChannels.facebook) {
      selectView('facebook');
      return;
    }
    if (id === 'instagram' && socialChannels.instagram) {
      selectView('instagram');
      return;
    }
    if (id === 'linkedin' && socialChannels.linkedin) {
      selectView('linkedin');
      return;
    }
    navigate('/app-store?tab=integrations');
  };

  // Plan home — the calendar IS the marketing home now; it fills the screen.
  const OverviewTab = () => (
    <div className="flex min-h-full flex-col p-3 sm:p-4">
      <div className="flex w-full flex-1 flex-col min-h-0 overflow-hidden rounded-[14px] border border-[#e8eaed] bg-white">
        <MarketingMediaCalendar
          campaigns={campaigns}
          gbpPosts={gbpCalendarPosts}
          socialPosts={SOCIAL_ENABLED ? socialCalendarPosts : []}
          businessId={businessId}
          fillHeight
          showCreateButton={false}
          onCampaignClick={(c) => {
            const full = campaigns.find((x) => x.id === c.id);
            if (full) setDetailsCampaign(full);
          }}
          onCreateCampaign={(dateStr) => handleCreateCampaign(dateStr)}
          onGbpChanged={() => {
            void refreshGbpCalendarPosts();
            void refreshSocialChannelStatus();
          }}
          onSocialChanged={() => {
            void refreshSocialCalendarPosts();
            void refreshSocialChannelStatus();
          }}
        />
      </div>
    </div>
  );

  // Campaigns Tab
  const CampaignsTab = () => (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Campaigns</h2>
          <p className="text-[15px] sm:text-base text-gray-600" style={{ letterSpacing: '-0.01em' }}>Create and manage your SMS & email campaigns</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {(!settings?.verified_senders || !Array.isArray(settings.verified_senders) || settings.verified_senders.filter((s: any) => s.verification_status === 'verified').length === 0) && (
            <div className="px-3 py-1 bg-orange-100 text-orange-800 text-[11px] font-semibold rounded-[6px] self-start" style={{ letterSpacing: '-0.01em' }}>
              Test Mode - No SendGrid Integration
            </div>
          )}
          <button
            onClick={handleCreateCampaign}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] transition-all duration-150 font-semibold flex items-center justify-center sm:justify-start touch-manipulation shadow-sm"
            style={{ 
              minHeight: '44px',
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em',
              fontSize: '15px'
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            <span>Create Campaign</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-[15px] bg-white border border-gray-200/60 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              style={{ 
                minHeight: '44px',
                letterSpacing: '-0.01em',
                WebkitTapHighlightColor: 'transparent'
              }}
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-auto px-4 py-2.5 text-[15px] bg-white border border-gray-200/60 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          style={{ 
            minHeight: '44px',
            letterSpacing: '-0.01em',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="sending">Sending</option>
          <option value="sent">Sent</option>
          <option value="paused">Paused</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Campaigns List */}
      {filteredCampaigns.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-gray-200/60 p-6 sm:p-12 text-center shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="h-16 w-16 rounded-[12px] bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>No Campaigns Found</h3>
          <p className="text-[15px] text-gray-600 mb-6" style={{ letterSpacing: '-0.01em' }}>
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Create your first email campaign to get started'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <div className="flex flex-col items-center gap-2">
              {(!settings?.verified_senders || !Array.isArray(settings.verified_senders) || settings.verified_senders.filter((s: any) => s.verification_status === 'verified').length === 0) && (
                <div className="px-3 py-1 bg-orange-100 text-orange-800 text-[11px] font-semibold rounded-[6px]" style={{ letterSpacing: '-0.01em' }}>
                  Test Mode - No SendGrid Integration
                </div>
              )}
              <button
                onClick={handleCreateCampaign}
                className="px-6 py-3 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] transition-all duration-150 font-semibold touch-manipulation shadow-sm"
                style={{ 
                  minHeight: '44px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em',
                  fontSize: '15px'
                }}
              >
                <Plus className="h-5 w-5 mr-2 inline" />
                Create Your First Campaign
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredCampaigns.map((campaign) => (
            <div key={campaign.id} onClick={() => setDetailsCampaign(campaign)} className="bg-white rounded-[12px] border border-gray-200/60 p-4 sm:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 touch-manipulation cursor-pointer hover:border-gray-300" style={{ WebkitTapHighlightColor: 'transparent' }}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-[16px] sm:text-lg font-semibold text-gray-900 break-words" style={{ letterSpacing: '-0.01em' }}>{campaign.name}</h3>
                    {/* Channel Type Badge */}
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-[6px] flex items-center space-x-1 flex-shrink-0 ${
                      campaign.channel_type === 'sms' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`} style={{ letterSpacing: '-0.01em' }}>
                      {campaign.channel_type === 'sms' ? (
                        <>
                          <MessageCircle className="h-3 w-3" />
                          <span>SMS</span>
                        </>
                      ) : (
                        <>
                          <Mail className="h-3 w-3" />
                          <span>Email</span>
                        </>
                      )}
                    </span>
                    {campaign.is_ab_test && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-[11px] font-semibold rounded-[6px] flex-shrink-0" style={{ letterSpacing: '-0.01em' }}>
                        <TestTube className="h-3 w-3 mr-1 inline" />
                        A/B Test
                      </span>
                    )}
                    {(campaign as any).is_sequence_root && (
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[11px] font-semibold rounded-[6px] flex-shrink-0" style={{ letterSpacing: '-0.01em' }}>
                        Extended
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-[6px] flex items-center space-x-1 flex-shrink-0 ${getStatusColor(campaign.status)}`} style={{ letterSpacing: '-0.01em' }}>
                      {getStatusIcon(campaign.status)}
                      <span className="capitalize">{campaign.status}</span>
                    </span>
                  </div>
                  {/* Show subject for email, SMS message preview for SMS */}
                  {campaign.channel_type === 'sms' ? (
                    <p className="text-[13px] text-gray-600 mb-2 break-words" style={{ letterSpacing: '-0.01em' }}>
                      {campaign.sms_content ? (
                        <span className="line-clamp-2">{campaign.sms_content}</span>
                      ) : (
                        <span className="text-gray-400 italic">No message set</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-[13px] text-gray-600 mb-2 break-words" style={{ letterSpacing: '-0.01em' }}>{campaign.subject || <span className="text-gray-400 italic">No subject set</span>}</p>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                    <span className="break-words">
                      From: {campaign.channel_type === 'sms' 
                        ? (campaign.from_phone_number || 'No phone number') 
                        : (campaign.from_email || 'No email')}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span>Recipients: {campaign.recipient_count.toLocaleString()}</span>
                    {campaign.scheduled_at && (
                      <>
                        <span className="hidden sm:inline">•</span>
                        <span>Scheduled: {new Date(campaign.scheduled_at).toLocaleDateString()}</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {/* Launch Campaign button for draft/scheduled campaigns */}
                  {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                    <button
                      onClick={() => handleLaunchCampaign(campaign)}
                      disabled={campaign.recipient_count === 0}
                      className="px-3 sm:px-4 py-2 bg-green-600 active:bg-green-700 text-white rounded-[10px] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-150 flex items-center space-x-1 sm:space-x-2 text-[13px] sm:text-[15px] font-semibold touch-manipulation shadow-sm"
                      style={{ 
                        minHeight: '36px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                      title={campaign.recipient_count === 0 ? 'No recipients configured' : 'Launch campaign'}
                    >
                      <Send className="h-4 w-4" />
                      <span className="hidden sm:inline">Launch Campaign</span>
                      <span className="sm:hidden">Launch</span>
                    </button>
                  )}
                  {/* Edit button - only show for draft, scheduled, paused, or failed campaigns */}
                  {(campaign.status === 'draft' || campaign.status === 'scheduled' || campaign.status === 'paused' || campaign.status === 'failed') && (
                  <button
                    onClick={() => handleEditCampaign(campaign)}
                    className="p-2 text-gray-400 active:text-gray-600 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                    style={{ 
                      minHeight: '36px',
                      minWidth: '36px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    title="Edit campaign"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  )}
                  {/* Duplicate button - only show for draft, scheduled, paused, or failed campaigns */}
                  {(campaign.status === 'draft' || campaign.status === 'scheduled' || campaign.status === 'paused' || campaign.status === 'failed') && (
                  <button
                    onClick={() => handleDuplicateCampaign(campaign)}
                    className="p-2 text-gray-400 active:text-gray-600 active:bg-gray-100 rounded-[10px] transition-all duration-150 touch-manipulation"
                    style={{ 
                      minHeight: '36px',
                      minWidth: '36px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    title="Duplicate campaign"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    className="p-2 text-gray-400 active:text-red-600 active:bg-red-50 rounded-[10px] transition-all duration-150 touch-manipulation"
                    style={{ 
                      minHeight: '36px',
                      minWidth: '36px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                    title="Delete campaign"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Campaign Stats */}
              {campaign.status === 'sent' && (
                <div className="pt-4 border-t border-gray-200/60">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-500" style={{ letterSpacing: '-0.01em' }}>Live Metrics</span>
                      {realtimeConnected && (
                        <span className="flex items-center gap-1 text-[11px] text-green-600 font-semibold" style={{ letterSpacing: '-0.01em' }}>
                          <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
                          Live
                        </span>
                      )}
                    </div>
                  </div>
                  {campaign.channel_type === 'sms' ? (
                    // SMS campaigns: only show Sent metric
                    <div className="grid grid-cols-1 gap-4">
                      <div className="text-center">
                        <div className="text-[20px] sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{campaign.sent_count.toLocaleString()}</div>
                        <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Sent</div>
                      </div>
                    </div>
                  ) : (
                    // Email campaigns: show all metrics - responsive grid
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4">
                      <div className="text-center">
                        <div className="text-[20px] sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{campaign.sent_count.toLocaleString()}</div>
                        <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Sent</div>
                      </div>
                  <div className="text-center">
                    <div className="text-[20px] sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{campaign.delivered_count.toLocaleString()}</div>
                    <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Delivered</div>
                  </div>
                      <div className="text-center">
                        <div className="text-[20px] sm:text-2xl font-bold text-green-600" style={{ letterSpacing: '-0.02em' }}>{campaign.opened_count.toLocaleString()}</div>
                        <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Opened</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[20px] sm:text-2xl font-bold text-green-600" style={{ letterSpacing: '-0.02em' }}>
                      {calculateOpenRate(campaign.opened_count, campaign.delivered_count)}%
                    </div>
                    <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Open Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[20px] sm:text-2xl font-bold text-blue-600" style={{ letterSpacing: '-0.02em' }}>
                      {calculateClickRate(campaign.clicked_count, campaign.delivered_count)}%
                    </div>
                    <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Click Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[20px] sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{campaign.clicked_count}</div>
                    <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Clicks</div>
                  </div>
                      <div className="text-center">
                        <div className="text-[20px] sm:text-2xl font-bold text-red-600" style={{ letterSpacing: '-0.02em' }}>{(campaign.bounced_count || 0).toLocaleString()}</div>
                        <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Bounced</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[20px] sm:text-2xl font-bold text-orange-600" style={{ letterSpacing: '-0.02em' }}>{(campaign.unsubscribed_count || 0).toLocaleString()}</div>
                        <div className="text-[11px] sm:text-[13px] text-gray-500" style={{ letterSpacing: '-0.01em' }}>Unsubscribed</div>
                      </div>
                    </div>
                  )}

                  {/* Per-variant analytics */}
                  {campaign.is_ab_test && (campaign as any)._variants && (campaign as any)._variants.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-2 mb-3">
                        <TestTube className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-semibold text-gray-900">Split Test Results</span>
                      </div>
                      <div className="space-y-2">
                        {((campaign as any)._variants as any[]).map((variant: any) => (
                          <div key={variant.variant_name} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                            <span className="text-sm font-semibold text-purple-700 uppercase">Variant {variant.variant_name}</span>
                            <div className="flex items-center space-x-6 text-sm">
                              <span className="text-gray-600">Sent: <span className="font-semibold text-gray-900">{variant.sent_count || 0}</span></span>
                              <span className="text-gray-600">Delivered: <span className="font-semibold text-gray-900">{variant.delivered_count || 0}</span></span>
                              <span className="text-gray-600">Opens: <span className="font-semibold text-green-600">{variant.opened_count || 0}</span></span>
                              <span className="text-gray-600">Open Rate: <span className="font-semibold text-green-600">{variant.open_rate || 0}%</span></span>
                              <span className="text-gray-600">Clicks: <span className="font-semibold text-blue-600">{variant.clicked_count || 0}</span></span>
                              <span className="text-gray-600">Click Rate: <span className="font-semibold text-blue-600">{variant.click_rate || 0}%</span></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Extended campaign: per-step performance */}
              {(campaign as any)._steps && ((campaign as any)._steps as any[]).length > 0 && (
                <div className="pt-4 border-t border-gray-200/60 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[11px] font-semibold rounded-[6px]">Extended</span>
                    <span className="text-[11px] font-semibold text-gray-500" style={{ letterSpacing: '-0.01em' }}>
                      Campaign journey · {((campaign as any)._steps as any[]).length + 1} emails
                    </span>
                  </div>
                  <div className="space-y-2">
                    {/* Email 1 = the root campaign itself */}
                    <div className="rounded-lg bg-gray-50 px-3.5 py-2.5">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-[12px] font-bold text-indigo-700">Email 1</span>
                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-900">{campaign.subject || campaign.name}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-[5px] capitalize ${getStatusColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </div>
                      {(campaign.status === 'sent' || campaign.status === 'sending') && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-gray-600">
                          <span>Sent: <span className="font-semibold text-gray-900">{campaign.sent_count || 0}</span></span>
                          <span>Delivered: <span className="font-semibold text-gray-900">{campaign.delivered_count || 0}</span></span>
                          <span>Opened: <span className="font-semibold text-green-600">{campaign.opened_count || 0} ({calculateOpenRate(campaign.opened_count, campaign.delivered_count)}%)</span></span>
                          <span>Clicks: <span className="font-semibold text-blue-600">{campaign.clicked_count || 0}</span></span>
                        </div>
                      )}
                    </div>

                    {((campaign as any)._steps as any[]).map((step: any) => (
                      <div key={step.id} className="rounded-lg bg-gray-50 px-3.5 py-2.5">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-[12px] font-bold text-indigo-700">Email {step.step_number}</span>
                          <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-900">{step.subject || 'Untitled'}</span>
                          {step.is_ab_test && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-[5px]">A/B</span>
                          )}
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-[5px] capitalize ${step.status === 'waiting' ? 'bg-gray-200 text-gray-600' : getStatusColor(step.status)}`}>
                            {step.status}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          Wait {formatStepDelayHours(step.step_delay_hours)} → {STEP_CONDITION_LABELS[(step.step_condition as keyof typeof STEP_CONDITION_LABELS)] || 'Everyone'}
                          {step.status === 'scheduled' && step.scheduled_at
                            ? ` · sends ${new Date(step.scheduled_at).toLocaleString()}`
                            : ''}
                          {step.status === 'waiting' ? ' · waiting on previous email' : ''}
                        </p>
                        {(step.status === 'sent' || step.status === 'sending') && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-gray-600">
                            <span>Matched: <span className="font-semibold text-gray-900">{step.recipient_count || 0}</span></span>
                            <span>Sent: <span className="font-semibold text-gray-900">{step.sent_count || 0}</span></span>
                            <span>Delivered: <span className="font-semibold text-gray-900">{step.delivered_count || 0}</span></span>
                            <span>Opened: <span className="font-semibold text-green-600">{step.opened_count || 0} ({calculateOpenRate(step.opened_count || 0, step.delivered_count || 0)}%)</span></span>
                            <span>Clicks: <span className="font-semibold text-blue-600">{step.clicked_count || 0}</span></span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );






  // Lists Tab Component
  const ListsTab = () => {
    const [emailList, setEmailList] = useState<SecondaryEmailEntry[]>([]);
    const [phoneList, setPhoneList] = useState<SecondaryPhoneEntry[]>([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [customerEmailCount, setCustomerEmailCount] = useState<number>(0);
    const [customerPhoneCount, setCustomerPhoneCount] = useState<number>(0);
    const [showEmailListModal, setShowEmailListModal] = useState(false);
    const [showPhoneListModal, setShowPhoneListModal] = useState(false);
    const [showCustomerEmailListModal, setShowCustomerEmailListModal] = useState(false);
    const [showCustomerPhoneListModal, setShowCustomerPhoneListModal] = useState(false);

    const loadLists = async () => {
      if (!businessId || !supabase) return;
      setLoadingLists(true);
      try {
        // Get customer counts - fetch actual data to deduplicate
        // For emails: fetch all emails, normalize, and count unique
        const BATCH_SIZE = 1000;
        let allCustomerEmails: string[] = [];
        let emailOffset = 0;
        let hasMoreEmails = true;

        while (hasMoreEmails) {
          const { data: emailBatch, error: emailError } = await supabase
            .from('customers')
            .select('email')
            .eq('business_id', businessId)
            .eq('status', 'active')
            .not('email', 'is', null)
            .neq('email', '')
            .range(emailOffset, emailOffset + BATCH_SIZE - 1);

          if (emailError) {
            console.error('Error fetching customer emails:', emailError);
            break;
          }

          if (emailBatch && emailBatch.length > 0) {
            allCustomerEmails = [...allCustomerEmails, ...emailBatch.map(c => c.email).filter(Boolean)];
            emailOffset += BATCH_SIZE;
            hasMoreEmails = emailBatch.length === BATCH_SIZE;
          } else {
            hasMoreEmails = false;
          }
        }

        // Normalize and deduplicate emails
        const uniqueEmails = new Set<string>();
        allCustomerEmails.forEach(email => {
          const normalized = normalizeEmail(email);
          if (normalized) {
            uniqueEmails.add(normalized);
          }
        });

        // For phones: fetch all phones, normalize, and count unique
        let allCustomerPhones: string[] = [];
        let phoneOffset = 0;
        let hasMorePhones = true;

        while (hasMorePhones) {
          const { data: phoneBatch, error: phoneError } = await supabase
            .from('customers')
            .select('phone')
            .eq('business_id', businessId)
            .eq('status', 'active')
            .not('phone', 'is', null)
            .neq('phone', '')
            .range(phoneOffset, phoneOffset + BATCH_SIZE - 1);

          if (phoneError) {
            console.error('Error fetching customer phones:', phoneError);
            break;
          }

          if (phoneBatch && phoneBatch.length > 0) {
            allCustomerPhones = [...allCustomerPhones, ...phoneBatch.map(c => c.phone).filter(Boolean)];
            phoneOffset += BATCH_SIZE;
            hasMorePhones = phoneBatch.length === BATCH_SIZE;
          } else {
            hasMorePhones = false;
          }
        }

        // Normalize and deduplicate phones
        const uniquePhones = new Set<string>();
        allCustomerPhones.forEach(phone => {
          const normalized = normalizePhoneNumber(phone);
          if (normalized) {
            uniquePhones.add(normalized);
          }
        });

        setCustomerEmailCount(uniqueEmails.size);
        setCustomerPhoneCount(uniquePhones.size);
      } catch (error) {
        console.error('Error loading lists:', error);
        alert('Failed to load lists');
      } finally {
        setLoadingLists(false);
      }
    };

    useEffect(() => {
      if (activeTab === 'lists' && businessId) {
        loadLists();
      }
    }, [activeTab, businessId]);

    const handleDeleteEmail = async (id: string) => {
      if (!confirm('Are you sure you want to delete this email?')) return;
      if (!supabase) return;
      
      try {
        await deleteSecondaryEmail(supabase, id);
        await loadLists();
      } catch (error) {
        console.error('Error deleting email:', error);
        alert('Failed to delete email');
      }
    };

    const handleDeletePhone = async (id: string) => {
      if (!confirm('Are you sure you want to delete this phone number?')) return;
      if (!supabase) return;
      
      try {
        await deleteSecondaryPhone(supabase, id);
        await loadLists();
      } catch (error) {
        console.error('Error deleting phone:', error);
        alert('Failed to delete phone number');
      }
    };

    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Contact Lists</h2>
          <p className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Customer email and phone lists used by campaigns</p>
        </div>

        {/* Email List Section */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 sm:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" style={{ letterSpacing: '-0.01em' }}>
                <Mail className="h-5 w-5" />
                Email List
              </h3>
            </div>
          </div>

          {loadingLists ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Customer Email List */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-[10px] active:bg-gray-100 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <div>
                  <p className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Customer Email List</p>
                  <p className="text-[13px] text-gray-600 mt-1" style={{ letterSpacing: '-0.01em' }}>
                    {customerEmailCount.toLocaleString()} email{customerEmailCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCustomerEmailListModal(true);
                  }}
                  className="px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] transition-all duration-150 font-semibold touch-manipulation shadow-sm"
                  style={{ 
                    minHeight: '36px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '13px'
                  }}
                >
                  View List
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Phone List Section */}
        <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 sm:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" style={{ letterSpacing: '-0.01em' }}>
                <MessageCircle className="h-5 w-5" />
                Phone List
              </h3>
            </div>
          </div>

          {loadingLists ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Customer Phone List */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-[10px] active:bg-gray-100 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <div>
                  <p className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Customer Phone List</p>
                  <p className="text-[13px] text-gray-600 mt-1" style={{ letterSpacing: '-0.01em' }}>
                    {customerPhoneCount.toLocaleString()} phone number{customerPhoneCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCustomerPhoneListModal(true);
                  }}
                  className="px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] transition-all duration-150 font-semibold touch-manipulation shadow-sm"
                  style={{ 
                    minHeight: '36px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '13px'
                  }}
                >
                  View List
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modals for viewing lists - portaled to document.body to escape stacking context */}
        {/* Customer Phone List Modal */}
        {showCustomerPhoneListModal && typeof document !== 'undefined' && createPortal(
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity duration-200"
            style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setShowCustomerPhoneListModal(false)}
          >
            <div 
              className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
                typeof window !== 'undefined' && window.innerWidth < 768
                  ? 'fixed inset-0 w-full'
                  : 'fixed inset-y-0 right-0 w-full md:w-[700px]'
              }`}
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                zIndex: 100050,
                top: 'env(safe-area-inset-top, 0px)',
                bottom: 'env(safe-area-inset-bottom, 0px)',
                ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Phone List</h3>
                <button
                  onClick={() => setShowCustomerPhoneListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {loadingLists ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <CustomerPhoneListTable businessId={businessId} supabase={supabase} />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Secondary Phone List Modal */}
        {showPhoneListModal && typeof document !== 'undefined' && createPortal(
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity duration-200"
            style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setShowPhoneListModal(false)}
          >
            <div 
              className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
                typeof window !== 'undefined' && window.innerWidth < 768
                  ? 'fixed inset-0 w-full'
                  : 'fixed inset-y-0 right-0 w-full md:w-[700px]'
              }`}
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                zIndex: 100050,
                top: 'env(safe-area-inset-top, 0px)',
                bottom: 'env(safe-area-inset-bottom, 0px)',
                ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Secondary Phone List</h3>
                <button
                  onClick={() => setShowPhoneListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {phoneList.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No phone numbers in secondary list</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {phoneList.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{entry.phone}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {entry.first_name || entry.last_name
                                  ? `${entry.first_name || ''} ${entry.last_name || ''}`.trim()
                                  : '-'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => {
                                    handleDeletePhone(entry.id);
                                    if (phoneList.length === 1) {
                                      setShowPhoneListModal(false);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Customer Email List Modal */}
        {showCustomerEmailListModal && typeof document !== 'undefined' && createPortal(
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity duration-200"
            style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setShowCustomerEmailListModal(false)}
          >
            <div 
              className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
                typeof window !== 'undefined' && window.innerWidth < 768
                  ? 'fixed inset-0 w-full'
                  : 'fixed inset-y-0 right-0 w-full md:w-[700px]'
              }`}
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                zIndex: 100050,
                top: 'env(safe-area-inset-top, 0px)',
                bottom: 'env(safe-area-inset-bottom, 0px)',
                ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Email List</h3>
                <button
                  onClick={() => setShowCustomerEmailListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {loadingLists ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <CustomerEmailListTable businessId={businessId} supabase={supabase} />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Secondary Email List Modal */}
        {showEmailListModal && typeof document !== 'undefined' && createPortal(
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity duration-200"
            style={{ zIndex: 100010, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setShowEmailListModal(false)}
          >
            <div 
              className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
                typeof window !== 'undefined' && window.innerWidth < 768
                  ? 'fixed inset-0 w-full'
                  : 'fixed inset-y-0 right-0 w-full md:w-[700px]'
              }`}
              style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                zIndex: 100050,
                top: 'env(safe-area-inset-top, 0px)',
                bottom: 'env(safe-area-inset-bottom, 0px)',
                ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Secondary Email List</h3>
                <button
                  onClick={() => setShowEmailListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {emailList.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-600">No emails in secondary list</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {emailList.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{entry.email}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {entry.first_name || entry.last_name
                                  ? `${entry.first_name || ''} ${entry.last_name || ''}`.trim()
                                  : '-'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => {
                                    handleDeleteEmail(entry.id);
                                    if (emailList.length === 1) {
                                      setShowEmailListModal(false);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  // Helper components for customer lists
  const CustomerPhoneListTable = ({ businessId, supabase }: { businessId: string | undefined; supabase: any }) => {
    const [customers, setCustomers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadCustomers = async () => {
        if (!businessId || !supabase) return;
        setLoading(true);
        try {
          const BATCH_SIZE = 1000;
          let allCustomers: any[] = [];
          let offset = 0;
          let hasMore = true;

          while (hasMore) {
            const { data, error } = await supabase
              .from('customers')
              .select('id, phone, first_name, last_name')
              .eq('business_id', businessId)
              .eq('status', 'active')
              .not('phone', 'is', null)
              .neq('phone', '')
              .range(offset, offset + BATCH_SIZE - 1);

            if (error) throw error;

            if (data && data.length > 0) {
              allCustomers = [...allCustomers, ...data];
              offset += BATCH_SIZE;
              hasMore = data.length === BATCH_SIZE;
            } else {
              hasMore = false;
            }
          }

          setCustomers(allCustomers);
        } catch (error) {
          console.error('Error loading customer phones:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCustomers();
    }, [businessId, supabase]);

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{customer.phone}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {customer.first_name || customer.last_name
                      ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const CustomerEmailListTable = ({ businessId, supabase }: { businessId: string | undefined; supabase: any }) => {
    const [customers, setCustomers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadCustomers = async () => {
        if (!businessId || !supabase) return;
        setLoading(true);
        try {
          const BATCH_SIZE = 1000;
          let allCustomers: any[] = [];
          let offset = 0;
          let hasMore = true;

          while (hasMore) {
            const { data, error } = await supabase
              .from('customers')
              .select('id, email, first_name, last_name')
              .eq('business_id', businessId)
              .eq('status', 'active')
              .not('email', 'is', null)
              .neq('email', '')
              .range(offset, offset + BATCH_SIZE - 1);

            if (error) throw error;

            if (data && data.length > 0) {
              allCustomers = [...allCustomers, ...data];
              offset += BATCH_SIZE;
              hasMore = data.length === BATCH_SIZE;
            } else {
              hasMore = false;
            }
          }

          setCustomers(allCustomers);
        } catch (error) {
          console.error('Error loading customer emails:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCustomers();
    }, [businessId, supabase]);

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{customer.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {customer.first_name || customer.last_name
                      ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div 
      className="h-full min-h-0 flex flex-col bg-gray-50"
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}
    >
      {/* Header — title + ⋯ overflow menu (Overview…Brand guidelines) */}
      <div className="border-b border-gray-200/60 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between px-2.5 sm:px-4 h-12">
          <div className="flex items-center gap-1 min-w-0">
            {activeView || activeTab !== 'overview' ? (
              <button
                type="button"
                onClick={() => selectTab('overview')}
                aria-label="Back to calendar"
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : null}
            <h1
              className="text-[17px] font-bold text-gray-900 truncate pl-1"
              style={{ letterSpacing: '-0.02em' }}
            >
              {activeView
                ? {
                    media: 'Library',
                    automate: 'Automate',
                    history: 'History',
                    'google-business': 'Google Business',
                    facebook: 'Facebook',
                    instagram: 'Instagram',
                  }[activeView]
                : {
                    overview: 'Marketing',
                    campaigns: 'Campaigns',
                    lists: 'Lists',
                    templates: 'Email Templates',
                    discounts: 'Discount Codes',
                    referral: 'Referral Program',
                    rewards: 'Rewards Program',
                    brand: 'Brand Guidelines',
                  }[activeTab]}
            </h1>
          </div>

          <div className="relative" ref={overflowMenuRef}>
            <button
              type="button"
              onClick={() => setShowOverflowMenu((v) => !v)}
              aria-label="Marketing menu"
              className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
            {showOverflowMenu ? (
              <div className="absolute right-0 top-full mt-1.5 w-52 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden py-1 z-30">
                {(
                  [
                    { id: 'overview', label: 'Overview' },
                    { id: 'campaigns', label: 'Campaigns' },
                    { id: 'lists', label: 'Lists' },
                    { id: 'templates', label: 'Email Templates' },
                    { id: 'discounts', label: 'Discount Codes' },
                    { id: 'referral', label: 'Referral Program' },
                    { id: 'rewards', label: 'Rewards Program' },
                    { id: 'brand', label: 'Brand Guidelines' },
                  ] as { id: MarketingTab; label: string }[]
                ).map((item) => {
                  const isActive = !activeView && activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false);
                        selectTab(item.id);
                      }}
                      className={`flex w-full items-center px-4 py-2.5 text-left text-[14px] touch-manipulation ${
                        isActive
                          ? 'font-semibold text-gray-900 bg-gray-50'
                          : 'font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                    >
                      {item.label}
                    </button>
                  );
                })}
                {/* Automate + History moved here from the bottom nav's More menu
                    (the bottom-right slot is left empty for the app FAB) */}
                <div className="my-1 border-t border-gray-100" aria-hidden />
                {(
                  [
                    { id: 'automate', label: 'Automate' },
                    { id: 'history', label: 'History' },
                  ] as { id: 'automate' | 'history'; label: string }[]
                ).map((item) => {
                  const isActive = activeView === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setShowOverflowMenu(false);
                        selectView(item.id);
                      }}
                      className={`flex w-full items-center px-4 py-2.5 text-left text-[14px] touch-manipulation ${
                        isActive
                          ? 'font-semibold text-gray-900 bg-gray-50'
                          : 'font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content — single scroller; min-h-0 required so flex child doesn't expand past the shell (iOS double-scroll) */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain ios-momentum-scroll">
        {activeView ? (
          <div className="p-3 sm:p-4 pb-8">
            {GBP_ENABLED ? (
              <MediaManager key={`mm-${mediaRefreshKey}`} embedded hideChrome />
            ) : null}
          </div>
        ) : (
          <>
            {/* Rendered as function calls, not <Components/> — these are defined
                inside the render body, so as components they'd get a new type
                every parent re-render and React would remount the subtree
                (resetting e.g. the calendar's month navigation). */}
            {activeTab === 'overview' && OverviewTab()}
            {activeTab === 'campaigns' && CampaignsTab()}
            {activeTab === 'lists' && <ListsTab />}
            {activeTab === 'templates' && <EmailTemplates />}
            {activeTab === 'discounts' && <DiscountCodes />}
            {activeTab === 'referral' && <ReferralProgram />}
            {activeTab === 'rewards' && <RewardsProgram />}
            {activeTab === 'brand' && <BrandGuidelines />}
          </>
        )}
      </div>

      {/* Bottom tab bar — Plan · Library · Create · Platforms · Email templates */}
      <MarketingBottomNav
        active={
          activeView
            ? activeView === 'media'
              ? 'library'
              : activeView === 'automate' || activeView === 'history'
                ? null
                : 'platforms'
            : activeTab === 'overview'
              ? 'plan'
              : activeTab === 'templates'
                ? 'templates'
                : null
        }
        onPlan={() => selectTab('overview')}
        onTemplates={() => selectTab('templates')}
        onLibrary={() => selectView('media')}
        createChannels={socialChannels}
        onCreateAction={(action) => {
          if (action === 'campaign') handleCreateCampaign();
          else if (action === 'gbp') setPlusGbpOpen(true);
          else if (action === 'facebook' || action === 'instagram' || action === 'linkedin')
            setPlusSocialPlatform(action);
          else if (action === 'upload') setShowUploadSheet(true);
        }}
        onPlatforms={() => setShowPlatformsModal(true)}
      />

      <MarketingPlatformsModal
        open={showPlatformsModal}
        onClose={() => setShowPlatformsModal(false)}
        channels={platformChannelItems}
        onViewChannel={openPlatformChannel}
        onManageChannel={connectPlatformFromMarketing}
      />

      <MarketingPlatformConnectModal
        target={platformConnect}
        autoStart={connectAutoStart}
        onClose={() => {
          setPlatformConnect(null);
          setConnectAutoStart(false);
          const params = new URLSearchParams(window.location.search);
          let changed = false;
          for (const key of ['gbp_oauth', 'gbp_returning', 'social_oauth', 'platform']) {
            if (params.has(key)) {
              params.delete(key);
              changed = true;
            }
          }
          if (changed) {
            const nextUrl = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
            window.history.replaceState({}, '', nextUrl);
          }
          void refreshSocialChannelStatus();
          void loadTwilioSettings();
        }}
        onChanged={() => {
          void refreshSocialChannelStatus();
          void loadTwilioSettings();
        }}
      />

      {/* [+] menu → new Google Business / social post from anywhere in Marketing */}
      <CreateGbpPostModal
        open={plusGbpOpen}
        onClose={() => setPlusGbpOpen(false)}
        onSuccess={() => {
          void refreshGbpCalendarPosts();
          void refreshSocialChannelStatus();
        }}
      />
      {plusSocialPlatform ? (
        <CreateSocialPostModal
          open
          platform={plusSocialPlatform}
          onClose={() => setPlusSocialPlatform(null)}
          onSuccess={() => {
            void refreshSocialCalendarPosts();
            void refreshSocialChannelStatus();
          }}
        />
      ) : null}

      {/* [+] menu → Upload media sheet. Kept mounted (CSS-hide) — iOS delivers
          picked photos to the hidden file input asynchronously. */}
      <div className={showUploadSheet ? '' : 'hidden'}>
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
          onClick={() => setShowUploadSheet(false)}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h3
                className="text-[17px] font-bold text-gray-900"
                style={{ letterSpacing: '-0.02em' }}
              >
                Upload media
              </h3>
              <button
                type="button"
                onClick={() => setShowUploadSheet(false)}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {businessId ? (
              <div className="px-2 pb-3">
                <AddMediaOptions
                  businessId={businessId}
                  variant="rows"
                  onAdded={() => {
                    // Remount the media views so the grid picks up new images.
                    setMediaRefreshKey((k) => k + 1);
                  }}
                  onDone={() => {
                    setShowUploadSheet(false);
                    selectView('media');
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Email Setup Modal */}
      {showEmailSetup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" style={{ WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="bg-white rounded-[12px] border border-gray-200/60 max-w-md w-full p-4 sm:p-6 shadow-xl" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Add Email Address</h3>
              <button
                onClick={() => {
                  setShowEmailSetup(false);
                  setNewEmail('');
                }}
                className="w-11 h-11 rounded-[10px] text-gray-400 active:bg-gray-100 transition-all duration-150 touch-manipulation flex items-center justify-center"
                style={{ 
                  minHeight: '44px',
                  minWidth: '44px',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {settings?.verified_senders && Array.isArray(settings.verified_senders) && 
             settings.verified_senders.some((s: any) => s.verification_status === 'verified') && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200/60 rounded-[10px]">
                <p className="text-[15px] text-yellow-800" style={{ letterSpacing: '-0.01em' }}>
                  You already have a verified email address. Please remove it first before adding a new one.
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-2" style={{ letterSpacing: '-0.01em' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="hello@yourbusiness.com"
                  className="w-full px-4 py-2.5 text-[15px] bg-white border border-gray-200/60 rounded-[10px] focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                  style={{ 
                    minHeight: '44px',
                    letterSpacing: '-0.01em',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200/60 p-4 rounded-[10px]">
                <p className="text-[15px] text-blue-800" style={{ letterSpacing: '-0.01em' }}>
                  We'll send a verification email to this address. Click the link in that email and
                  you're ready to send — no SendGrid account needed.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEmailSetup(false)}
                  className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 active:bg-gray-200 rounded-[10px] transition-all duration-150 font-semibold touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '15px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddEmail}
                  disabled={
                    !newEmail.trim() || 
                    verifyingEmail || 
                    (settings?.verified_senders && Array.isArray(settings.verified_senders) && 
                     settings.verified_senders.some((s: any) => s.verification_status === 'verified'))
                  }
                  className="flex-1 px-4 py-2.5 bg-blue-600 active:bg-blue-700 text-white rounded-[10px] disabled:bg-blue-400 disabled:cursor-not-allowed transition-all duration-150 font-semibold touch-manipulation shadow-sm"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '15px'
                  }}
                >
                  {verifyingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 inline animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Add & Verify'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Details Modal */}
      {detailsCampaign && (
        <CampaignDetailsModal
          campaign={detailsCampaign}
          onClose={() => setDetailsCampaign(null)}
          onEdit={(c) => { setDetailsCampaign(null); handleEditCampaign(c); }}
          onLaunch={(c) => { setDetailsCampaign(null); handleLaunchCampaign(c); }}
          onDuplicate={(c) => { setDetailsCampaign(null); handleDuplicateCampaign(c); }}
          onDelete={(id) => { setDetailsCampaign(null); handleDeleteCampaign(id); }}
          onRetryBilling={(c) => {
            setDetailsCampaign(null);
            setRetryBillingCampaign({
              id: c.id,
              name: c.name,
              recipient_count: c.recipient_count,
              billing_status: c.billing_status,
              billing_payment_intent_id: c.billing_payment_intent_id,
              scheduled_at: c.scheduled_at,
              channel_type: c.channel_type,
            });
          }}
        />
      )}

      {businessId && retryBillingCampaign ? (
        <CampaignBillingRetryModal
          isOpen
          businessId={businessId}
          campaign={retryBillingCampaign}
          onClose={() => setRetryBillingCampaign(null)}
          onSuccess={() => {
            void loadCampaigns();
          }}
        />
      ) : null}

      {/* Campaign Builder Modal - Right Side Mounted - Using Portal */}
      {showCampaignBuilder && typeof document !== 'undefined' && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{
            zIndex: 100010,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
          onClick={() => setShowCampaignBuilder(false)}
        >
          <div 
            className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-transform duration-200 ${
              typeof window !== 'undefined' && window.innerWidth < 768
                ? 'fixed inset-0 w-full'
                : 'fixed inset-y-0 right-0 w-full md:w-[800px] lg:w-[900px]'
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
              zIndex: 100050,
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              ...(typeof window !== 'undefined' && window.innerWidth >= 768 && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                {editingCampaign?.id ? 'Edit Campaign' : 'Create New Campaign'}
              </h2>
              <button
                onClick={() => setShowCampaignBuilder(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          
            {/* Modal Content */}
            <div 
              className="flex-1 overflow-y-auto bg-white"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="p-4 sm:p-6">
                <CampaignBuilderForm 
                  key={editingCampaign?.id || initialCampaignDate || 'new-campaign'}
                  onClose={() => setShowCampaignBuilder(false)}
                  onSave={handleSaveCampaign}
                  editingCampaign={editingCampaign}
                  selectedBusiness={selectedBusiness}
                  businessId={businessId}
                  supabase={supabase}
                  initialDate={initialCampaignDate}
                  onStartMultiMedia={(channels, schedule) => {
                    setShowCampaignBuilder(false);
                    setMultiMediaSetup({ channels, schedule });
                  }}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Multi Media Campaign wizard — chained channel setup + final create */}
      {multiMediaSetup && businessId ? (
        <MultiMediaCampaignWizard
          open
          onClose={() => setMultiMediaSetup(null)}
          businessId={businessId}
          channels={multiMediaSetup.channels}
          schedule={multiMediaSetup.schedule}
          onSaveCampaign={handleSaveCampaign}
          onFinished={() => {
            void loadCampaigns();
          }}
          renderCampaignStep={({ channel, draft, onCollect, onCancel }) => (
            <CampaignBuilderForm
              key={`multi-media-${channel}`}
              onClose={onCancel}
              onSave={onCollect}
              editingCampaign={draft ? ({ ...draft } as unknown as EmailCampaign) : null}
              selectedBusiness={selectedBusiness}
              businessId={businessId}
              supabase={supabase}
              multiMediaStep={{
                channel,
                sendOption: multiMediaSetup.schedule.sendOption,
                scheduledDate: multiMediaSetup.schedule.scheduledDate,
                scheduledTime: multiMediaSetup.schedule.scheduledTime,
              }}
            />
          )}
        />
      ) : null}

      {/* Billing Confirmation Modal */}
      {showBillingModal && campaignToLaunch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4" style={{ WebkitBackdropFilter: 'blur(20px)' }}>
          <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-xl max-w-lg w-full overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
            {/* Black Header with NexusAi Branding */}
            <div className="bg-gray-900 border-b border-gray-800 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white" style={{ letterSpacing: '-0.01em' }}>NexusAi</h2>
              <button
                onClick={() => {
                  setShowBillingModal(false);
                  setCampaignToLaunch(null);
                  setProcessingBilling(false);
                }}
                className="w-11 h-11 rounded-[10px] text-gray-400 active:bg-gray-800 transition-all duration-150 touch-manipulation flex items-center justify-center"
                style={{ 
                  minHeight: '44px',
                  minWidth: '44px',
                  WebkitTapHighlightColor: 'transparent'
                }}
                disabled={processingBilling}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ letterSpacing: '-0.01em' }}>
                  {isScheduledLaunch ? 'Schedule Campaign Payment' : 'Campaign Launch Payment'}
                </h3>
                <p className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                  {isScheduledLaunch 
                    ? 'Review and confirm your payment to schedule this campaign'
                    : 'Review and confirm your payment to launch this campaign'
                  }
                </p>
              </div>

              {/* Campaign Details */}
              <div className="bg-gray-50 border border-gray-200/60 rounded-[10px] p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="h-10 w-10 rounded-[10px] bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Send className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[16px] font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>{campaignToLaunch.name}</div>
                    <div className="text-[13px] text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>{campaignToLaunch.subject || 'No subject'}</div>
                  </div>
                </div>
              </div>

              {/* Billing Breakdown */}
              <div className="border border-gray-200/60 rounded-[10px] overflow-hidden mb-6">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200/60">
                  <h4 className="text-[11px] font-semibold text-gray-900 uppercase tracking-wide" style={{ letterSpacing: '0.05em' }}>Billing Summary</h4>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Recipients</span>
                    <span className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>{campaignToLaunch.recipient_count?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] text-gray-600" style={{ letterSpacing: '-0.01em' }}>Cost per recipient</span>
                    <span className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>$0.01</span>
                  </div>
                  <div className="border-t border-gray-200/60 pt-3 mt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[16px] font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Total Amount</span>
                      <span className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>${billingAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Notice */}
              <div className="bg-blue-50 border border-blue-200/60 rounded-[10px] p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 text-xs font-bold">i</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-[15px] text-blue-900" style={{ letterSpacing: '-0.01em' }}>
                      We’ll try the card on file first. If that charge fails, you’ll be sent to a secure checkout to pay with a different card for this campaign only.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBillingModal(false);
                    setCampaignToLaunch(null);
                    setProcessingBilling(false);
                    setIsScheduledLaunch(false);
                  }}
                  disabled={processingBilling}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-200/60 active:bg-gray-50 rounded-[10px] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed font-semibold touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '15px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApproveAndSend}
                  disabled={processingBilling}
                  className="px-6 py-2.5 bg-gray-900 active:bg-gray-800 text-white rounded-[10px] disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-150 flex items-center space-x-2 font-semibold touch-manipulation shadow-sm"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em',
                    fontSize: '15px'
                  }}
                >
                  {processingBilling ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Processing Payment...</span>
                    </>
                  ) : (
                    <>
                      <span>{isScheduledLaunch ? 'Approve & Schedule' : 'Approve & Launch Campaign'}</span>
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
